import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { MAZE, type SideId } from '@somnhold/content';
import { createMaze, hexToWorld, type MazeLayout } from '@somnhold/sim';
import { DWOOD, cloth, emis, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { keep } from './structures.ts';
import { guard } from './guard.ts';
import { BOAR_HIP_Y, BOAR_LEGS, boarBody, boarLeg } from './boar.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The cellars' trophy (docs/EXPEDICIONES.md): the gate bell back on the keep. Built in the keep's own units
 * (its walls are 0.8 wide, the walkway at 1.9) so it is placed with the keep's matrix.
 *
 * - First time up: an iron gallows rises from the keep's front-right corner and the bell hangs from it, out
 *   over the plaza, its band of the clan's runes glowing dragon red; a rope drops to the ground.
 * - Five times (`banners`): the old garrison's banners hang from the beam (charcoal, a red bell on each) and a
 *   stone tablet stands at the keep's foot.
 * - Once with nobody falling (`sash`): a red sash knotted on the bell's crown, its tails in the wind, and the
 *   runes burn brighter.
 */

export interface BellLook {
  banners?: boolean;
  sash?: boolean;
}

export interface BellTrophy extends Model {
  /** Swings the bell hard for a few seconds (a ring). */
  ring(): void;
}

/** The night of the bell's small bell, with where its body hangs (model frame: on the crown, or off the bracket). */
export interface LidBellTrophy extends BellTrophy {
  at: THREE.Vector3;
}

const RED = '#ff4f3a';
const RED_LIGHT = '#ff8a6e';
const IRON = metal('#2c3036', { roughness: 0.5, metalness: 0.7, flatShading: true });
const RIVET = metal('#8f969f', { roughness: 0.3, flatShading: true });
const BELL = std('#4f6662', { roughness: 0.5, metalness: 0.35, flatShading: true, emissive: '#16211f', emissiveIntensity: 0.6 });
const BELL_LIP = std('#8fb0a8', { roughness: 0.4, metalness: 0.4, flatShading: true, emissive: '#1f2c2a', emissiveIntensity: 0.6 });
const CHARCOAL = cloth('#22252b');
const ROPE = cloth('#6b6256');

/** The bell's outline, from the crown down to the lip (x: radius, y: height), about 0.5 tall. */
const PROFILE: [number, number][] = [
  [0.0, 0.5], [0.07, 0.5], [0.11, 0.48], [0.13, 0.42], [0.14, 0.3], [0.17, 0.17], [0.22, 0.07], [0.25, 0.015], [0.255, 0.0], [0.23, 0.0], [0.18, 0.05], [0.0, 0.07],
];

export const gateBellTrophy = (o?: BaseOpts & BellLook): BellTrophy => {
  const banners0 = !!o?.banners;
  const sash0 = !!o?.sash;
  const c = begin(o, 0.8, 41);
  const { g } = c;
  // The gallows: a post on the walkway's corner, a beam out over the plaza, a brace under it.
  const base = V(0.3, 1.94, 0.3);
  const top = V(0.3, 3.15, 0.3);
  const tip = V(1.3, 3.15, 1.3);
  limb(g, base, top, 0.05, 0.042, IRON, 6);
  limb(g, top, tip, 0.042, 0.036, IRON, 6);
  limb(g, V(0.3, 2.4, 0.3), V(0.85, 3.12, 0.85), 0.026, 0.026, IRON, 5);
  for (const p of [base, top, tip, V(0.8, 3.15, 0.8)]) M(new THREE.OctahedronGeometry(0.03, 0), RIVET, g).position.copy(p);
  // A finial: a small iron spike at the tip, catching the light.
  limb(g, tip, V(1.34, 3.32, 1.34), 0.018, 0.002, RIVET, 4);

  // The headstock (a dark oak yoke) the bell swings under.
  const pivot = G(g, 1.3, 3.08, 1.3);
  pivot.rotation.y = -Math.PI / 4;
  const yoke = M(new THREE.BoxGeometry(0.7, 0.12, 0.14), DWOOD, pivot);
  yoke.position.y = 0;
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.045, 0.15, 0.16), IRON, pivot).position.set(k * 0.3, 0, 0);
  // The bell under it, mouth down; the swing turns the pivot.
  const swing = G(pivot, 0, -0.04, 0);
  const bell = G(swing, 0, -1.08, 0);
  bell.scale.setScalar(2.0);
  M(new THREE.LatheGeometry(PROFILE.map(([x, y]) => new THREE.Vector2(x, y)).reverse(), 20), BELL, bell);
  // Its lip and shoulder bands, and the band of runes between them.
  const band = (y: number, r: number, h: number, mat: THREE.Material): void => {
    const m = M(new THREE.CylinderGeometry(r, r, h, 20, 1, true), mat, bell);
    m.position.y = y;
  };
  band(0.025, 0.252, 0.03, BELL_LIP);
  band(0.4, 0.142, 0.02, BELL_LIP);
  const runes = emis(RED_LIGHT, RED, sash0 ? 2.2 : 1.5);
  const runeBand = M(new THREE.CylinderGeometry(0.163, 0.172, 0.022, 20, 1, true), runes, bell);
  runeBand.position.y = 0.12;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const r = M(new THREE.BoxGeometry(i % 3 === 0 ? 0.045 : 0.03, i % 2 === 0 ? 0.075 : 0.05, 0.012), runes, bell);
    r.position.set(Math.sin(a) * 0.17, 0.175, Math.cos(a) * 0.17);
    r.rotation.set(-0.38, a, i % 2 === 0 ? 0.2 : -0.15);
  }
  // Crown loops into the yoke, and the clapper inside.
  for (const k of [-1, 1]) {
    const loop = M(new THREE.TorusGeometry(0.045, 0.014, 5, 10, Math.PI), IRON, bell);
    loop.position.set(k * 0.035, 0.5, 0);
    loop.rotation.y = Math.PI / 2;
  }
  limb(bell, V(0, 0.46, 0), V(0, 0.08, 0), 0.008, 0.008, IRON, 4);
  M(new THREE.SphereGeometry(0.035, 8, 6), IRON, bell).position.y = 0.07;
  const glow = glowSprite(bell, RED, sash0 ? 1.4 : 1.05, V(0, 0.2, 0), sash0 ? 0.6 : 0.45, true);
  // A red glow on the ground under it, so it reads from the bastion's camera.
  glowSprite(g, RED, 1.8, V(1.3, 0.06, 1.3), 0.3);
  // The bell-rope, from the yoke down past the keep's foot.
  const rope = M(new THREE.CylinderGeometry(0.008, 0.008, 1, 4), ROPE, g);
  rope.position.set(1.55, 3.08 - 1.54, 1.55);
  rope.scale.y = 3.08;

  const banners: THREE.Group[] = [];
  if (banners0) {
    // The old garrison's banners from the beam: charcoal, a red bell on each, a forked tail.
    for (const t of [0.08, 0.28]) {
      const b = G(g, 0.3 + (tip.x - 0.3) * t, 3.1, 0.3 + (tip.z - 0.3) * t);
      b.rotation.y = -Math.PI / 4;
      M(new THREE.BoxGeometry(0.2, 0.025, 0.025), DWOOD, b);
      const cloth1 = G(b, 0, -0.02, 0);
      const flag = M(new THREE.BoxGeometry(0.3, 0.95, 0.014), CHARCOAL, cloth1);
      flag.position.y = -0.48;
      for (const k of [-1, 1]) {
        const tail = M(new THREE.ConeGeometry(0.075, 0.2, 3), CHARCOAL, cloth1);
        tail.rotation.z = Math.PI;
        tail.scale.z = 0.12;
        tail.position.set(k * 0.075, -1.05, 0);
      }
      const em = emis(RED_LIGHT, RED, 0.6);
      // The red bell on both faces, and a red hem at the top.
      for (const z of [0.01, -0.01]) {
        const emb = M(new THREE.CylinderGeometry(0.05, 0.095, 0.14, 8), em, cloth1);
        emb.position.set(0, -0.4, z);
        emb.scale.z = 0.15;
        M(new THREE.BoxGeometry(0.2, 0.022, 0.016), em, cloth1).position.set(0, -0.58, z);
      }
      M(new THREE.BoxGeometry(0.3, 0.03, 0.03), em, cloth1).position.set(0, -0.04, 0);
      banners.push(cloth1);
    }
    // A stone tablet at the keep's foot, under the bell, with a red-lit line of names.
    const tab = G(g, 0.95, 0, 0.95);
    tab.rotation.y = Math.PI / 4;
    M(new THREE.BoxGeometry(0.5, 0.07, 0.24), stone('#3a3c42'), tab).position.y = 0.035;
    const slab = M(new THREE.BoxGeometry(0.42, 0.52, 0.08), stone('#2b2d33'), tab);
    slab.position.set(0, 0.33, 0);
    slab.rotation.x = -0.12;
    M(new THREE.CylinderGeometry(0.035, 0.06, 0.08, 8), emis(RED_LIGHT, RED, 1.0), tab).position.set(0, 0.5, 0.045);
    for (let i = 0; i < 3; i++) M(new THREE.BoxGeometry(0.28 - i * 0.05, 0.02, 0.008), emis(RED_LIGHT, RED, 1.0), tab).position.set(0, 0.38 - i * 0.08, 0.05 - i * 0.008);
  }

  const tails: THREE.Mesh[] = [];
  if (sash0) {
    // A red sash knotted on the crown, its two tails loose in the wind.
    const sash = emis(RED, RED, 0.35, { side: THREE.DoubleSide, roughness: 0.9 });
    const knot = M(new THREE.TorusGeometry(0.125, 0.03, 6, 16), sash, bell);
    knot.position.y = 0.43;
    knot.rotation.x = Math.PI / 2;
    for (const k of [-1, 1]) {
      const tl = M(new THREE.PlaneGeometry(0.09, 0.62), sash, bell);
      tl.geometry.translate(0, -0.31, 0);
      tl.position.set(k * 0.05, 0.44, 0.13);
      tl.rotation.set(0.35, 0, k * 0.22);
      tails.push(tl);
    }
  }

  let ringing = 0;
  const tick = (n: number): void => {
    // A slow sway in the wind; a ring swings it hard and dies away.
    const strong = ringing > 0 ? (ringing / 36) * 0.55 : 0;
    swing.rotation.x = Math.sin(n * 0.21) * 0.035 + Math.sin(n * 0.9) * strong;
    if (ringing > 0) ringing--;
    for (let i = 0; i < banners.length; i++) (banners[i] as THREE.Group).rotation.x = Math.sin(n * 0.33 + i * 1.7) * 0.06;
    for (let i = 0; i < tails.length; i++) (tails[i] as THREE.Mesh).rotation.x = 0.35 + Math.sin(n * 0.5 + i * 2.1) * 0.18;
    const k = 0.5 + 0.5 * Math.sin(n * 0.25);
    (glow.material as THREE.SpriteMaterial).opacity = (sash0 ? 0.4 : 0.22) + k * 0.2 + strong * 0.6;
  };
  const m = done(c, 3.3, tick) as BellTrophy;
  m.ring = () => {
    ringing = 36;
  };
  return m;
};

/** For the gallery: the keep with the trophy on it. */
export const keepWithBell = (o?: BaseOpts & BellLook & { era?: number }): Model => {
  const k = keep(o);
  const t = gateBellTrophy({ ...o, base: false });
  k.root.add(t.root);
  return { ...k, tick: (n) => {
    k.tick(n);
    t.tick(n);
  }, height: Math.max(k.height, 3.3) };
};

/*
 * The Furrows' trophy (docs/LABERINTOS.md): the table of the Furrows, on the plaza at the keep's front-left foot,
 * in the keep's units like the bell. A low slab of grey stone on four stout legs with a labyrinth carved in its
 * top, the lines lit dragon red and the way to the middle brighter; beside it, the andariegos' waystone.
 *
 * - First week won: the table and the waystone.
 * - Three weeks (`posts`): two posts with red-glassed lanterns at the table's corners.
 * - A week won with nobody falling (`sash`): a red cord knotted round the waystone, its tails in the wind.
 */

export interface TableLook {
  posts?: boolean;
  sash?: boolean;
  /** The week won (its labyrinth's seed and size): the one carved on the table. */
  carve?: { seed: number; radius: number };
}

/** The labyrinth the gallery shows when no week is given. */
const SAMPLE = { seed: 7, radius: MAZE.radius };

/**
 * The carving: every corridor of the week's labyrinth, and the way from the rope to the middle (its open
 * corridors, gates left out). Segments in the slab's top, centred, the labyrinth's rim at `half`.
 */
const carving = (L: MazeLayout, half: number): { lines: number[][]; way: number[][]; step: number } => {
  const pts = L.nodes.map((n) => hexToWorld(n.q, n.r));
  let ext = 1;
  for (const p of pts) ext = Math.max(ext, Math.abs(p.x), Math.abs(p.y));
  const k = half / ext;
  const at = (i: number): [number, number] => [(pts[i] as { x: number }).x * k, (pts[i] as { y: number }).y * k];
  const seg = (a: number, b: number): number[] => [...at(a), ...at(b)];
  const adj: number[][] = L.nodes.map(() => []);
  for (const e of L.edges) {
    if (e.kind === 'gate') continue;
    (adj[e.a] as number[]).push(e.b);
    (adj[e.b] as number[]).push(e.a);
  }
  const prev = new Array<number>(L.nodes.length).fill(-2);
  prev[L.entry] = -1;
  const queue = [L.entry];
  for (let i = 0; i < queue.length; i++) {
    const a = queue[i] as number;
    for (const b of adj[a] as number[]) {
      if (prev[b] !== -2) continue;
      prev[b] = a;
      queue.push(b);
    }
  }
  const way: number[][] = [];
  for (let c = L.hall; prev[c] !== undefined && (prev[c] as number) >= 0; c = prev[c] as number) way.push(seg(prev[c] as number, c));
  // Between two neighbouring crossings: the labyrinth's grain, which sets how wide a groove is.
  const len = (e: { a: number; b: number }): number => Math.hypot(at(e.a)[0] - at(e.b)[0], at(e.a)[1] - at(e.b)[1]);
  const step = Math.min(...L.edges.filter((e) => e.b !== L.hall).map(len));
  return { lines: L.edges.map((e) => seg(e.a, e.b)), way, step };
};

/** Flat bars along segments (x0, z0, x1, z1), merged into one mesh. */
const bars = (segs: number[][], w: number, y: number, mat: THREE.Material, parent: THREE.Object3D): void => {
  const geos = segs.map(([x0, z0, x1, z1]) => {
    const dx = (x1 as number) - (x0 as number);
    const dz = (z1 as number) - (z0 as number);
    const g = new THREE.BoxGeometry(Math.hypot(dx, dz) + w, 0.012, w);
    g.rotateY(-Math.atan2(dz, dx));
    g.translate(((x0 as number) + (x1 as number)) / 2, y, ((z0 as number) + (z1 as number)) / 2);
    return g;
  });
  if (!geos.length) return;
  M(mergeGeometries(geos, false), mat, parent);
  for (const g of geos) g.dispose();
};

export const furrowsTableTrophy = (o?: BaseOpts & TableLook): Model => {
  const c = begin(o, 0.8, 53);
  const { g } = c;
  const posts0 = !!o?.posts;
  const sash0 = !!o?.sash;
  const at = G(g, -1.35, 0, 1.15);
  at.rotation.y = Math.PI / 5;
  const slabM = stone('#5a5d65');
  const legM = stone('#3a3c42');
  // The slab and its legs: big enough to read from the bastion's camera.
  M(new THREE.BoxGeometry(1.25, 0.12, 0.9), slabM, at).position.y = 0.5;
  M(new THREE.BoxGeometry(1.31, 0.04, 0.96), legM, at).position.y = 0.43;
  for (const x of [-0.5, 0.5]) for (const z of [-0.32, 0.32]) M(new THREE.BoxGeometry(0.16, 0.42, 0.16), legM, at).position.set(x, 0.21, z);
  // The carving: the week's labyrinth in dim red, the way from the rope to the middle bright, the middle a red stone.
  const cut = o?.carve ?? SAMPLE;
  const { lines, way: path, step } = carving(createMaze(cut.seed, cut.radius).layout, 0.4);
  const line = emis('#4a1a15', RED, 0.3);
  const way = emis(RED_LIGHT, RED, 1.6);
  bars(lines, step * 0.4, 0.565, line, at);
  bars(path, step * 0.6, 0.568, way, at);
  M(new THREE.CylinderGeometry(step * 1.6, step * 1.6, 0.03, 6), way, at).position.set(0, 0.57, 0);
  glowSprite(at, RED, 1.6, V(0, 0.62, 0), 0.32);
  // The waystone beside it: a tall rough stone with a band of runes.
  const stoneG = G(at, 0.95, 0, -0.2);
  const pillar = M(new THREE.CylinderGeometry(0.13, 0.2, 1.25, 6), stone('#4a4c53'), stoneG);
  pillar.position.y = 0.62;
  pillar.rotation.y = 0.4;
  M(new THREE.CylinderGeometry(0.17, 0.17, 0.07, 6), emis(RED_LIGHT, RED, sash0 ? 2.2 : 1.5), stoneG).position.y = 0.95;
  const crownGlow = glowSprite(stoneG, RED, 1.0, V(0, 1.0, 0), 0.4, true);
  if (posts0) {
    // Lantern posts at two corners, red glass.
    for (const [x, z] of [[-0.75, 0.55], [0.75, 0.55]] as [number, number][]) {
      limb(at, V(x, 0, z), V(x, 1.35, z), 0.035, 0.03, DWOOD, 5);
      limb(at, V(x, 1.3, z), V(x + 0.14, 1.3, z), 0.015, 0.015, IRON, 4);
      const lamp = G(at, x + 0.14, 1.15, z);
      M(new THREE.BoxGeometry(0.12, 0.16, 0.12), IRON, lamp);
      M(new THREE.BoxGeometry(0.09, 0.12, 0.13), emis(RED_LIGHT, RED, 2.2), lamp);
      glowSprite(lamp, RED, 0.9, V(0, 0, 0), 0.5);
    }
  }
  const tails: THREE.Mesh[] = [];
  if (sash0) {
    const cord = emis(RED, RED, 0.35, { side: THREE.DoubleSide, roughness: 0.9 });
    const knot = M(new THREE.TorusGeometry(0.17, 0.03, 6, 16), cord, stoneG);
    knot.position.y = 0.78;
    knot.rotation.x = Math.PI / 2;
    for (const k of [-1, 1]) {
      const tl = M(new THREE.PlaneGeometry(0.08, 0.5), cord, stoneG);
      tl.geometry.translate(0, -0.25, 0);
      tl.position.set(k * 0.05, 0.78, 0.18);
      tl.rotation.set(0.3, 0, k * 0.2);
      tails.push(tl);
    }
  }
  const tick = (n: number): void => {
    for (let i = 0; i < tails.length; i++) (tails[i] as THREE.Mesh).rotation.x = 0.3 + Math.sin(n * 0.5 + i * 2.1) * 0.18;
    (crownGlow.material as THREE.SpriteMaterial).opacity = (sash0 ? 0.45 : 0.3) + (0.5 + 0.5 * Math.sin(n * 0.25)) * 0.2;
  };
  return done(c, 1.4, tick);
};

/** Where the table stands in the keep's frame (for a word over it or a tap on it). */
export const TABLE_SPOT = V(-1.35, 0.6, 1.15);

/*
 * The tomes' shelf (docs/PODERES.md §6): a low stone table by the keep's front, in the keep's frame, read from
 * the bastion's high camera. One book lies on it for each tome the group has filled, its cover in its skill's
 * colour (fire, frost, lightning, poison, light or steel) with a lit rune; past a dozen they stack. While
 * someone is still gathering pages, a lectern beside it holds an open book with a red glow over it.
 */
export interface ShelfLook {
  /** One per tome filled, by its skill's look ('phys' | 'fire' | 'frost' | 'bolt' | 'poison' | 'holy'). */
  books: string[];
  /** Someone has a tome half gathered. */
  reading: boolean;
}

const BOOK: Record<string, [string, string]> = {
  phys: ['#6b7079', '#e3e6ec'],
  fire: ['#a8401c', '#ffb070'],
  frost: ['#3f7aa0', '#d8f2ff'],
  bolt: ['#5a4ea0', '#e9e3ff'],
  poison: ['#2f7a52', '#9df0c4'],
  holy: ['#c9ced8', '#ffffff'],
};

export const SHELF_SPOT = V(1.25, 0.5, 1.0);

export const tomeShelfTrophy = (o?: BaseOpts & ShelfLook): Model => {
  const c = begin(o, 0.8, 54);
  const { g } = c;
  const at = G(g, 1.25, 0, 1.0);
  at.rotation.y = -Math.PI / 5;
  const slab = stone('#8a8e98');
  const legs = stone('#4a4c53');
  // The table: a pale slab on two stone blocks, a dark rim.
  M(new THREE.BoxGeometry(1.3, 0.1, 0.72), slab, at).position.y = 0.42;
  M(new THREE.BoxGeometry(1.36, 0.04, 0.78), legs, at).position.y = 0.36;
  for (const x of [-0.48, 0.48]) M(new THREE.BoxGeometry(0.22, 0.34, 0.6), legs, at).position.set(x, 0.17, 0);
  // The books: two rows of six lying flat, the 13th on goes on top of the first.
  const books = (o?.books ?? []).slice(0, 24);
  books.forEach((look, i) => {
    const [cover, glow] = BOOK[look] ?? BOOK.phys!;
    const k = i % 12;
    const b = G(at, -0.52 + (k % 6) * 0.208, 0.5 + Math.floor(i / 12) * 0.075, k < 6 ? -0.16 : 0.17);
    b.rotation.y = ((i * 37) % 9 - 4) * 0.04;
    M(new THREE.BoxGeometry(0.17, 0.06, 0.25), std(cover, { roughness: 0.8 }), b);
    // The pages' edge and the lit rune on the cover.
    M(new THREE.BoxGeometry(0.15, 0.045, 0.012), std('#e9ecf1', { roughness: 0.9 }), b).position.set(0.005, 0, 0.126);
    const rune = M(new THREE.BoxGeometry(0.07, 0.008, 0.07), emis(glow, glow, 2.4), b);
    rune.position.y = 0.032;
    rune.rotation.y = Math.PI / 4;
    glowSprite(b, glow, 0.32, V(0, 0.06, 0), 0.4);
  });
  if (books.length) glowSprite(at, RED, 1.5, V(0, 0.55, 0), 0.22);
  // The lectern with the open book, while a tome is being gathered.
  let pages: THREE.Mesh | null = null;
  if (o?.reading) {
    const lect = G(at, -0.95, 0, 0.3);
    limb(lect, V(0, 0, 0), V(0, 0.7, 0), 0.05, 0.04, DWOOD, 6);
    const top = G(lect, 0, 0.74, 0);
    top.rotation.x = -0.45;
    M(new THREE.BoxGeometry(0.44, 0.04, 0.32), DWOOD, top);
    for (const k of [-1, 1]) {
      const leaf = M(new THREE.BoxGeometry(0.2, 0.02, 0.28), std('#eef0f4', { roughness: 0.9 }), top);
      leaf.position.set(k * 0.105, 0.035, 0);
      leaf.rotation.z = k * -0.12;
    }
    pages = M(new THREE.BoxGeometry(0.02, 0.03, 0.28), emis(RED_LIGHT, RED, 1.8), top);
    pages.position.y = 0.05;
    glowSprite(top, RED, 1.0, V(0, 0.2, 0), 0.45, true);
  }
  const tick = (n: number): void => {
    if (pages) pages.rotation.z = Math.sin(n * 0.4) * 0.6;
  };
  return done(c, 1.2, tick);
};

/*
 * The glyph stone (docs/GLIFOS.md §4): a pale stone tablet in front of the keep, between the barrels and the tomes'
 * shelf, in the keep's frame, leaning back so its face reads from the bastion's high camera. One mark is carved on
 * it, lit in the dragon red, for each glyph a hero of the group completed (four to a row, sixteen at most); while
 * someone is still gathering fragments, a fresh scratch at its foot glows faintly.
 */
export interface GlyphStoneLook {
  /** One per glyph completed, by its id (the mark's shape). */
  marks: string[];
  /** Someone has a glyph half gathered. */
  carving: boolean;
  /**
   * The saga's glyphs given whole (docs/UNICAS.md §1), their trophies beside the stone: Hilde's clan mark from the
   * Turtle 4 (La Hondura, the Storm glyph) and what is left of Maelis's remedies from the Turtle 6 (the Frost glyph).
   */
  clan?: boolean;
  remedies?: boolean;
}

export const GLYPH_STONE_SPOT = V(0.3, 0.6, 1.9);

/** Each glyph's mark: short strokes on the stone's face, in a unit square (x right, y up). */
const MARK: Record<string, [number, number, number, number][]> = {
  prolong: [[-0.3, 0.4, 0.3, 0.4], [-0.3, -0.4, 0.3, -0.4], [-0.25, 0.35, 0.25, -0.35], [0.25, 0.35, -0.25, -0.35]],
  celerity: [[-0.35, 0.35, 0, 0], [0, 0, -0.35, -0.35], [0, 0.35, 0.35, 0], [0.35, 0, 0, -0.35]],
  amplitude: [[0, 0.45, 0, -0.45], [-0.45, 0, 0.45, 0], [-0.2, 0.2, 0.2, 0.2], [-0.2, -0.2, 0.2, -0.2]],
  focus: [[-0.4, 0.4, -0.1, 0.1], [0.4, 0.4, 0.1, 0.1], [-0.4, -0.4, -0.1, -0.1], [0.4, -0.4, 0.1, -0.1]],
  // The conversions: a flame, a snowflake, a bolt, a drop.
  ember: [[0, 0.45, -0.25, -0.1], [0, 0.45, 0.25, -0.1], [-0.25, -0.1, 0, -0.45], [0.25, -0.1, 0, -0.45]],
  rime: [[0, 0.45, 0, -0.45], [-0.4, 0.22, 0.4, -0.22], [-0.4, -0.22, 0.4, 0.22]],
  storm: [[0.15, 0.45, -0.15, 0.02], [-0.15, 0.02, 0.15, 0.02], [0.15, 0.02, -0.15, -0.45]],
  venom: [[0, 0.45, -0.3, -0.1], [0, 0.45, 0.3, -0.1], [-0.3, -0.1, 0, -0.4], [0.3, -0.1, 0, -0.4], [-0.1, -0.15, 0.1, -0.15]],
  // The forms: two arcs, three rays, a winding line, a spiral, two heads.
  echo: [[-0.4, 0, -0.2, 0.3], [-0.2, 0.3, 0.2, 0.3], [0.2, 0.3, 0.4, 0], [-0.25, -0.15, 0.25, -0.15]],
  fan: [[0, -0.45, -0.35, 0.4], [0, -0.45, 0, 0.45], [0, -0.45, 0.35, 0.4]],
  trail: [[-0.45, -0.2, -0.15, 0.1], [-0.15, 0.1, 0.15, -0.1], [0.15, -0.1, 0.45, 0.2]],
  vortex: [[0, 0, 0.25, 0], [0.25, 0, 0.25, 0.3], [0.25, 0.3, -0.3, 0.3], [-0.3, 0.3, -0.3, -0.35], [-0.3, -0.35, 0.4, -0.35]],
  bond: [[-0.45, 0, -0.15, 0.25], [-0.15, 0.25, 0.15, -0.25], [0.15, -0.25, 0.45, 0], [-0.45, 0, 0.45, 0]],
  pack: [[-0.35, 0.3, -0.15, 0.3], [0.15, 0.3, 0.35, 0.3], [-0.45, -0.3, -0.05, -0.3], [0.05, -0.3, 0.45, -0.3]],
};

export const glyphStoneTrophy = (o?: BaseOpts & GlyphStoneLook): Model => {
  const c = begin(o, 0.8, 91);
  const { g } = c;
  const at = G(g, 0.3, 0, 1.9);
  // Its face toward the high camera (the keep's frame looks the other way).
  at.rotation.y = Math.PI + Math.PI / 10;
  const rock = stone('#c4c8d0');
  const dark = stone('#4a4c53');
  // A low dark footing and a pale tablet on it, leaning back so its carved face looks up at the high camera.
  M(new THREE.BoxGeometry(1.0, 0.14, 0.62), dark, at).position.y = 0.07;
  const lean = G(at, 0, 0.14, -0.1);
  lean.rotation.x = -1.05;
  M(new THREE.BoxGeometry(0.86, 0.12, 0.74), rock, lean).position.set(0, 0.06, 0.37);
  const lit = emis('#ff8a6e', '#ff4f3a', 2.6);
  const marks = (o?.marks ?? []).slice(0, 16);
  // Four to a row on the face, the first row at the top.
  marks.forEach((id, i) => {
    // Turned half a round, +x is the viewer's left.
    const face = G(lean, 0.3 - (i % 4) * 0.2, 0.125, 0.64 - Math.floor(i / 4) * 0.19);
    face.rotation.x = -Math.PI / 2;
    for (const [x0, y0, x1, y1] of MARK[id] ?? MARK.celerity!) {
      const len = Math.hypot(x1 - x0, y1 - y0) * 0.16;
      const cut = M(new THREE.BoxGeometry(len, 0.024, 0.012), lit, face);
      cut.position.set(((x0 + x1) / 2) * 0.16, ((y0 + y1) / 2) * 0.16, 0);
      cut.rotation.z = Math.atan2(y1 - y0, x1 - x0);
    }
    glowSprite(face, RED, 0.3, V(0, 0, 0.03), 0.35);
  });
  if (marks.length) glowSprite(at, RED, 1.2, V(0, 0.5, 0.2), 0.25);
  // Hilde's clan mark (the Turtle 4), at the stone's left: an iron shield of the clan standing on a block of the deep
  // rock, two hammers crossed on it under the Storm's bolt cut in dragon red, a chain of dark iron at its foot.
  if (o?.clan) {
    const iron = metal('#5d636e', { roughness: 0.45 });
    const ironDk = metal('#2c3038', { roughness: 0.5 });
    const deep = stone('#3a3d45');
    const side = G(at, -0.95, 0, 0.12);
    side.rotation.y = 0.35;
    // A size bigger than the stone's marks, to read from the high camera.
    side.scale.setScalar(1.6);
    M(new THREE.BoxGeometry(0.42, 0.16, 0.34), deep, side).position.y = 0.08;
    // The shield leans back on its block like the tablet, its face up to the high camera.
    const lean = G(side, 0, 0.16, -0.05);
    lean.rotation.x = -1.05;
    const sh = M(new THREE.CylinderGeometry(0.27, 0.27, 0.05, 6), iron, lean);
    sh.position.set(0, 0.025, 0.24);
    const rim = M(new THREE.TorusGeometry(0.27, 0.022, 4, 6), ironDk, lean);
    rim.rotation.x = Math.PI / 2;
    rim.position.set(0, 0.05, 0.24);
    // On its face (its +y): two hammers crossed and the Storm's bolt cut in dragon red over them.
    const face = G(lean, 0, 0.055, 0.24);
    face.rotation.x = -Math.PI / 2;
    for (const k of [-1, 1]) {
      const ham = G(face, 0, 0, 0);
      ham.rotation.z = k * 0.75;
      M(new THREE.BoxGeometry(0.03, 0.36, 0.02), ironDk, ham);
      M(new THREE.BoxGeometry(0.12, 0.06, 0.04), ironDk, ham).position.y = 0.17;
    }
    const bolt = G(face, 0, 0, 0.02);
    for (const [x0, y0, x1, y1] of MARK.storm!) {
      const len = Math.hypot(x1 - x0, y1 - y0) * 0.32;
      const cut = M(new THREE.BoxGeometry(len, 0.034, 0.014), lit, bolt);
      cut.position.set(((x0 + x1) / 2) * 0.32, ((y0 + y1) / 2) * 0.32, 0);
      cut.rotation.z = Math.atan2(y1 - y0, x1 - x0);
    }
    glowSprite(bolt, RED, 0.5, V(0, 0, 0.04), 0.45);
    // A chain of three links lying at its foot.
    for (let i = 0; i < 3; i++) {
      const l = M(new THREE.TorusGeometry(0.05, 0.014, 4, 8), ironDk, side);
      l.position.set(-0.14 + i * 0.08, 0.17, 0.2);
      l.rotation.set(Math.PI / 2, 0, i % 2 ? Math.PI / 2 : 0);
    }
  }
  // Maelis's remedies (the Turtle 6), at the stone's right: a small board with three stoppered flasks (the frost-pale
  // one still glows), a fourth lying on its side, and a sprig of the herb she cut.
  if (o?.remedies) {
    const board = std('#5a4632', { roughness: 0.9 });
    const cork = std('#8a6a48', { roughness: 0.95 });
    const frost = emis('#cfefff', '#7fd0ff', 1.4);
    const teal = emis('#7fe0c0', '#2f9c84', 0.8);
    const dusk = std('#6d6680', { roughness: 0.3, metalness: 0.1 });
    const side = G(at, 0.98, 0, 0.16);
    side.rotation.y = -0.4;
    side.scale.setScalar(1.6);
    M(new THREE.BoxGeometry(0.5, 0.05, 0.3), board, side).position.y = 0.025;
    const flask = (x: number, z: number, h: number, mat: THREE.Material): void => {
      const f = G(side, x, 0.05, z);
      M(new THREE.SphereGeometry(0.075, 8, 6), mat, f).position.y = 0.075;
      M(new THREE.CylinderGeometry(0.026, 0.034, h, 6), mat, f).position.y = 0.15 + h / 2 - 0.02;
      M(new THREE.CylinderGeometry(0.03, 0.026, 0.04, 6), cork, f).position.y = 0.15 + h;
    };
    flask(-0.14, 0.02, 0.1, frost);
    flask(0.02, -0.05, 0.14, teal);
    flask(0.15, 0.04, 0.08, dusk);
    glowSprite(side, '#9fdcff', 0.55, V(-0.14, 0.2, 0.02), 0.5);
    // One lying down, emptied.
    const down = G(side, 0.05, 0.08, 0.11);
    down.rotation.z = Math.PI / 2;
    M(new THREE.CylinderGeometry(0.028, 0.06, 0.16, 6), dusk, down);
    // A sprig of herb.
    for (let i = 0; i < 3; i++) limb(side, V(-0.2, 0.05, -0.1), V(-0.27 + i * 0.05, 0.16, -0.14 + i * 0.03), 0.012, 0.006, std('#5f8a4a', { roughness: 0.9 }), 3);
  }
  // A mark half carved at the tablet's foot while a glyph is being gathered.
  let scratch: THREE.Mesh | null = null;
  if (o?.carving) {
    const face = G(lean, -0.3, 0.125, 0.08);
    face.rotation.x = -Math.PI / 2;
    scratch = M(new THREE.BoxGeometry(0.02, 0.12, 0.012), emis(RED_LIGHT, RED, 1.4), face);
    glowSprite(face, RED, 0.35, V(0, 0, 0.03), 0.35, true);
  }
  const tick = (n: number): void => {
    // Materials are shared: the scratch grows and shrinks instead of dimming.
    if (scratch) scratch.scale.y = 0.75 + Math.sin(n * 0.5) * 0.25;
  };
  return done(c, 1.4, tick);
};

/*
 * The Unsleeping felled for good (docs/ANTIHEROES.md): by the keep's left flank, in the keep's frame, read from
 * the bastion's high camera. For Ser Corvin, a block of the old gate's stone with his black glass sword driven
 * into it point first, its burning edge gone pale and still, his crowned helm at its foot and the slab of the
 * gate he carried leaning on it; a cold white light stands over it (he rests). Vela's broken bow hangs on a post of
 * the wall walk in front of it, Ansel's burnt book lies open on a lectern behind it; each other Unsleeping felled
 * will add his own piece beside them.
 */
export interface FallenLook {
  /** The Unsleeping felled for good, by id ('corvin', 'lucan', 'bruni', 'ragna', 'edda', 'kaela'). */
  fallen: string[];
  /** How far round the middle the pieces stand (FALLEN_PATIO_R: close together, for their plinth in the patio). */
  ring?: number;
  /**
   * Ragna after El Cuerno 4: 'home' (she fell, and the clan brought her home: her piece with a ring of cairn stones
   * and the clan's red wool on the haft) or 'call' (she knelt at the clan's call and went to her mother's grave: only
   * her axe, left upright in a small cairn by the clan's way, red wool tied on it, a warm ember light; she kept the horn).
   */
  ragna?: 'home' | 'call';
}

/**
 * Where each felled Unsleeping's piece stands, in the keep's frame: a ring round the keep, an eighth of a turn apart,
 * outside the table, the shelf, the barrels and the clue board (Corvin's first, on the keep's left as before).
 */
const FALLEN_AT: Record<string, number> = { corvin: 180, vela: 135, ansel: 90, lucan: 45, bruni: 0, ragna: 300, edda: 270, kaela: 225 };
const FALLEN_R = 2.7;
/** The ring of the fallen drawn small, close round its plinth's middle (the patio of deeds). */
export const FALLEN_PATIO_R = 0.95;
const fallenXZ = (id: string, r = FALLEN_R): [number, number] => {
  const a = (((FALLEN_AT[id] ?? 165) as number) * Math.PI) / 180;
  return [Math.cos(a) * r, -Math.sin(a) * r];
};
/** Over one felled Unsleeping's piece (the flare when it is new), in the keep's frame. */
export const fallenSpot = (id: string, r = FALLEN_R): THREE.Vector3 => {
  const [x, z] = fallenXZ(id, r);
  return V(x, 0.9, z);
};

export const fallenTrophy = (o?: BaseOpts & FallenLook): Model => {
  const c = begin(o, 0.9, 77);
  const { g } = c;
  const slot = (id: string): [number, number, number] => {
    const [x, z] = fallenXZ(id, o?.ring);
    return [x, 0, z];
  };
  // Eight cold lights side by side washed the keep out: each piece's glow is kept small.
  const glow = (...a: Parameters<typeof glowSprite>): ReturnType<typeof glowSprite> => {
    const [parent, color, size, at, alpha, ...rest] = a;
    return glowSprite(parent, color, size * 0.6, at, (alpha ?? 0.8) * 0.75, ...rest);
  };
  const at = G(g, ...slot('corvin'));
  at.rotation.y = Math.PI / 7;
  const block = stone('#9a9ea8');
  const blockD = stone('#55585f');
  const glass = metal('#1b1d24', { roughness: 0.18, metalness: 0.55 });
  const iron = metal('#3a3d44', { roughness: 0.55, metalness: 0.6 });
  const char = std('#1c1410', { roughness: 0.9 });
  const pale = emis('#f2f6ff', '#cfdcff', 2.2);
  const ember = emis('#ffd9b0', '#ff4f3a', 2.4);
  if ((o?.fallen ?? []).includes('corvin')) {
    // The block of the gate's stone, a step under it.
    M(new THREE.BoxGeometry(0.9, 0.12, 0.9), blockD, at).position.y = 0.06;
    M(new THREE.BoxGeometry(0.7, 0.42, 0.7), block, at).position.y = 0.33;
    M(new THREE.BoxGeometry(0.74, 0.05, 0.74), blockD, at).position.y = 0.56;
    // The sword, point down in the stone: grip, guard, a long blade of black glass with a pale edge.
    const sw = G(at, 0, 0.58, 0);
    sw.rotation.z = 0.06;
    M(new THREE.BoxGeometry(0.11, 1.15, 0.03), glass, sw).position.y = 0.55;
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.012, 1.1, 0.034), pale, sw).position.set(k * 0.058, 0.55, 0);
    M(new THREE.BoxGeometry(0.5, 0.06, 0.08), iron, sw).position.y = 1.15;
    M(new THREE.CylinderGeometry(0.03, 0.03, 0.3, 6), char, sw).position.y = 1.33;
    M(new THREE.SphereGeometry(0.055, 6, 5), iron, sw).position.y = 1.5;
    // Cracks in the stone where it went in, a last ember in them.
    for (const [x, z, r] of [[0.1, 0.05, 0.4], [-0.08, -0.09, -0.6], [0.02, 0.12, 1.4]] as const) {
      const cr = M(new THREE.BoxGeometry(0.22, 0.012, 0.018), ember, at);
      cr.position.set(x, 0.585, z);
      cr.rotation.y = r;
    }
    // The helm at the block's foot, its crown of iron bars.
    const hm = G(at, 0.32, 0.12, 0.36);
    hm.rotation.set(0.3, -0.7, 0.4);
    M(new THREE.CylinderGeometry(0.13, 0.14, 0.26, 9), glass, hm).position.y = 0.13;
    M(new THREE.SphereGeometry(0.132, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2), glass, hm).position.y = 0.26;
    M(new THREE.BoxGeometry(0.2, 0.03, 0.03), char, hm).position.set(0, 0.17, 0.13);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const bar = M(new THREE.BoxGeometry(0.02, 0.16, 0.02), iron, hm);
      bar.position.set(Math.sin(a) * 0.13, 0.36, Math.cos(a) * 0.13);
    }
    // The slab of the gate leaning on the block: charred planks bound in iron.
    const sl = G(at, -0.42, 0, -0.1);
    sl.rotation.set(0, 0.5, 0.32);
    for (let i = 0; i < 4; i++) M(new THREE.BoxGeometry(0.15, 0.95, 0.05), char, sl).position.set(-0.24 + i * 0.16, 0.48, 0);
    for (const y of [0.22, 0.72]) M(new THREE.BoxGeometry(0.66, 0.06, 0.03), iron, sl).position.set(0, y, 0.035);
    // The cold light over it: he rests.
    glow(sw, '#dfe8ff', 1.6, V(0, 1.0, 0), 0.5);
    glow(at, '#ff4f3a', 1.4, V(0, 0.6, 0), 0.3);
  }
  if ((o?.fallen ?? []).includes('lucan')) {
    // Prior Lucan: a chapel's stone step with his crozier standing in it, its broken sun on top, and his censer
    // hanging cold from the crook on its chain; the shards of his halo at its foot. A cold light over it.
    const lu = G(g, ...slot('lucan'));
    lu.rotation.y = -Math.PI / 9;
    lu.scale.setScalar(1);
    const vio = emis('#efe2ff', '#9a5cff', 1.6);
    const wood = std('#1e1712', { roughness: 0.9 });
    M(new THREE.BoxGeometry(0.95, 0.12, 0.7), blockD, lu).position.y = 0.06;
    M(new THREE.BoxGeometry(0.75, 0.24, 0.5), block, lu).position.y = 0.24;
    M(new THREE.BoxGeometry(0.79, 0.04, 0.54), blockD, lu).position.y = 0.38;
    // The crozier, a little askew, iron bands up the staff.
    const st = G(lu, -0.12, 0.4, 0);
    st.rotation.z = -0.05;
    M(new THREE.CylinderGeometry(0.035, 0.04, 1.75, 6), wood, st).position.y = 0.87;
    for (const y of [0.3, 0.9, 1.45]) M(new THREE.CylinderGeometry(0.045, 0.045, 0.05, 6), iron, st).position.y = y;
    // The broken sun at its head: an iron ring open at the top, its rays snapped short, the light in it gone pale.
    const sun = G(st, 0, 1.92, 0);
    M(new THREE.TorusGeometry(0.22, 0.03, 4, 16, Math.PI * 1.6), iron, sun).rotation.z = Math.PI * 0.7;
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + 0.3;
      const len = i % 3 === 0 ? 0.07 : 0.16;
      const ray = M(new THREE.BoxGeometry(0.024, len, 0.024), iron, sun);
      ray.position.set(Math.cos(a) * (0.25 + len / 2), Math.sin(a) * (0.25 + len / 2), 0);
      ray.rotation.z = a - Math.PI / 2;
    }
    M(new THREE.OctahedronGeometry(0.07, 0), pale, sun).scale.set(0.8, 1.2, 0.8);
    // A crook of iron off the staff, the censer hanging from it on its chain.
    const arm = M(new THREE.BoxGeometry(0.42, 0.035, 0.035), iron, st);
    arm.position.set(0.21, 1.55, 0);
    const hook = G(st, 0.4, 1.55, 0);
    for (let i = 0; i < 8; i++) {
      const l = M(new THREE.TorusGeometry(0.026, 0.007, 3, 6), iron, hook);
      l.position.set(0, -0.04 - i * 0.058, 0);
      l.rotation.y = i % 2 ? Math.PI / 2 : 0;
    }
    const cn = G(hook, 0, -0.58, 0);
    M(new THREE.ConeGeometry(0.085, 0.12, 8), glass, cn).position.y = 0.1;
    const bowl = M(new THREE.SphereGeometry(0.11, 8, 6), glass, cn);
    bowl.scale.set(1, 0.9, 1);
    M(new THREE.TorusGeometry(0.112, 0.014, 3, 10), iron, cn).rotation.x = Math.PI / 2;
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const slit = M(new THREE.BoxGeometry(0.02, 0.07, 0.012), vio, cn);
      slit.position.set(Math.sin(a) * 0.107, -0.01, Math.cos(a) * 0.107);
      slit.rotation.y = a;
    }
    // The shards of his halo on the step: a broken ring of black glass.
    for (const [x, z, r, a] of [[0.18, 0.12, 0.2, 0.3], [-0.05, 0.18, 0.17, 2.1], [0.26, -0.1, 0.14, 4.2]] as const) {
      const arc = M(new THREE.TorusGeometry(r, 0.022, 4, 10, Math.PI * 0.55), glass, lu);
      arc.position.set(x, 0.42, z);
      arc.rotation.set(-Math.PI / 2, 0, a);
    }
    glow(sun, '#dfe8ff', 1.3, V(0, 0, 0.02), 0.5);
    glow(cn, '#b07aff', 0.9, V(0, 0, 0.02), 0.45);
  }
  if ((o?.fallen ?? []).includes('edda')) {
    // Ser Edda: her tall kite shield split down the middle, the two halves standing apart on a chapel's stone, the
    // light gone out of the split; her mace laid across the stone's foot, a wing of her helm broken beside it.
    const ed = G(g, ...slot('edda'));
    ed.rotation.y = Math.PI / 8;
    ed.scale.setScalar(1);
    const light = emis('#ffffff', '#b8cdff', 1.8);
    const steel = metal('#4a4f5a', { roughness: 0.45, metalness: 0.7 });
    M(new THREE.BoxGeometry(1.0, 0.12, 0.6), blockD, ed).position.y = 0.06;
    M(new THREE.BoxGeometry(0.8, 0.2, 0.42), block, ed).position.y = 0.22;
    M(new THREE.BoxGeometry(0.84, 0.04, 0.46), blockD, ed).position.y = 0.34;
    // The two halves of the kite, each leaning out from the split.
    for (const k of [-1, 1]) {
      const half = new THREE.Shape();
      half.moveTo(0, 0.5);
      half.quadraticCurveTo(k * 0.18, 0.56, k * 0.34, 0.48);
      half.lineTo(k * 0.31, -0.12);
      half.quadraticCurveTo(k * 0.17, -0.56, 0, -0.82);
      half.lineTo(k * 0.03, -0.3);
      half.lineTo(-k * 0.01, 0.05);
      half.lineTo(0, 0.5);
      const geo = new THREE.ExtrudeGeometry(half, { depth: 0.05, bevelEnabled: true, bevelThickness: 0.012, bevelSize: 0.015, bevelSegments: 1, curveSegments: 5 });
      geo.translate(0, 0, -0.025);
      const hg = G(ed, k * 0.13, 1.18, 0);
      hg.rotation.set(0, k * 0.25, -k * 0.16);
      M(geo, glass, hg);
      // Half of the star on each, and the pale edge where it split.
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI + (k > 0 ? 0 : Math.PI);
        const ray = M(new THREE.BoxGeometry(0.035, i % 2 ? 0.14 : 0.24, 0.015), steel, hg);
        ray.position.set(Math.sin(a) * 0.14, 0.08 + Math.cos(a) * 0.14, 0.05);
        ray.rotation.z = -a;
      }
      const edge = M(new THREE.BoxGeometry(0.012, 0.9, 0.06), light, hg);
      edge.position.set(k * 0.005, -0.12, 0);
    }
    // The mace across the stone's foot.
    const mc = G(ed, 0.05, 0.42, 0.34);
    mc.rotation.set(0, 0.3, Math.PI / 2);
    M(new THREE.CylinderGeometry(0.025, 0.025, 0.75, 6), steel, mc);
    const hd = G(mc, 0, 0.42, 0);
    M(new THREE.SphereGeometry(0.07, 8, 6), glass, hd);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const fl = M(new THREE.BoxGeometry(0.024, 0.16, 0.08), glass, hd);
      fl.position.set(Math.sin(a) * 0.07, 0, Math.cos(a) * 0.07);
      fl.rotation.y = a;
    }
    // A broken wing of her helm, glass feathers on the stone.
    for (const [x, z, r, l] of [[-0.3, 0.12, 0.5, 0.3], [-0.22, 0.18, 0.8, 0.22], [-0.36, 0.2, 0.2, 0.18]] as const) {
      const f = M(new THREE.OctahedronGeometry(1, 0), glass, ed);
      f.scale.set(0.03, l / 2, 0.012);
      f.position.set(x, 0.37, z);
      f.rotation.set(Math.PI / 2, 0, r);
    }
    // The cold light over it.
    glow(ed, '#dfe8ff', 1.8, V(0, 1.25, 0.05), 0.5);
  }
  if ((o?.fallen ?? []).includes('kaela')) {
    // Kaela of the Moss: a mound of earth grown over with moss and roots, her staff of glass-wood driven into it,
    // its crook of thorns broken and its seed gone pale; her hood with its crown of antlers lies at its foot, and a
    // sapling, alive, has come up beside it. A cold light over it, a green one under: the forest came, at last.
    const kp = G(g, ...slot('kaela'));
    kp.rotation.y = -Math.PI / 5;
    const earth = std('#2a241e', { roughness: 1 });
    const moss = std('#3f6a2e', { roughness: 1, bumpScale: 0.06 });
    const mossD = std('#2c4a22', { roughness: 1 });
    const wood = std('#1e1915', { roughness: 0.85 });
    const sap = emis('#dcffc0', '#6cc832', 2.0);
    const leaf = std('#5d9a3e', { roughness: 0.9 });
    const mound = M(new THREE.SphereGeometry(0.58, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), mossD, kp);
    mound.scale.set(1, 0.38, 1);
    const cap = M(new THREE.SphereGeometry(0.5, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), moss, kp);
    cap.scale.set(1, 0.42, 1);
    // Bare earth only in a ring at its foot.
    const foot = M(new THREE.CylinderGeometry(0.62, 0.66, 0.03, 12), earth, kp);
    foot.position.y = 0.015;
    for (const [x, z, sc] of [[0.3, 0.2, 0.18], [-0.28, 0.25, 0.14], [0.1, -0.35, 0.16], [-0.35, -0.15, 0.12]] as const) {
      const t = M(new THREE.SphereGeometry(sc, 7, 5), mossD, kp);
      t.position.set(x, 0.12, z);
      t.scale.y = 0.6;
    }
    // Roots crawling out of the mound across the ground.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.3;
      limb(kp, V(Math.cos(a) * 0.5, 0.03, Math.sin(a) * 0.5), V(Math.cos(a + 0.2) * 0.85, 0.01, Math.sin(a + 0.2) * 0.85), 0.03, 0.012, wood, 5);
    }
    // The staff, leaning a little, in two lengths: wood below, black glass above with a pale crack.
    const st = G(kp, 0.02, 0.2, 0);
    st.rotation.set(0.08, 0, -0.12);
    limb(st, V(0, 0, 0), V(0, 0.8, 0), 0.04, 0.036, wood, 6);
    limb(st, V(0, 0.8, 0), V(0.01, 1.85, 0), 0.04, 0.03, glass, 6);
    M(new THREE.BoxGeometry(0.014, 0.8, 0.014), pale, st).position.set(0.035, 1.3, 0.025);
    // The crook: two thorned branches left of five, one snapped off, round a seed gone pale.
    const hd = G(st, 0, 1.94, 0);
    hd.scale.setScalar(1.4);
    const seed = M(new THREE.OctahedronGeometry(0.09, 0), pale, hd);
    seed.scale.set(1, 1.4, 1);
    for (const a of [0.4, 2.6]) {
      const p1 = V(Math.cos(a) * 0.15, 0.02, Math.sin(a) * 0.15);
      limb(hd, V(0, -0.12, 0), p1, 0.02, 0.015, glass, 4);
      limb(hd, p1, V(Math.cos(a + 0.6) * 0.1, 0.24, Math.sin(a + 0.6) * 0.1), 0.015, 0.006, glass, 4);
    }
    limb(hd, V(0, -0.12, 0), V(-0.08, -0.06, -0.08), 0.02, 0.018, glass, 4);
    // Moss creeping up the staff.
    limb(st, V(0, 0.05, 0.03), V(0.0, 0.55, 0.04), 0.045, 0.02, moss, 5);
    // Her hood on the mound, its antlers of black glass standing up out of the moss.
    const hood = G(kp, 0.3, 0.2, 0.3);
    hood.rotation.set(-0.2, 0.8, 0.1);
    const hc = M(new THREE.SphereGeometry(0.11, 9, 6, 0, Math.PI * 2, 0, Math.PI * 0.6), cloth('#3a4a36'), hood);
    hc.scale.set(1, 1.1, 1.05);
    for (const k of [-1, 1]) {
      limb(hood, V(k * 0.07, 0.14, -0.02), V(k * 0.18, 0.3, -0.05), 0.018, 0.012, glass, 4);
      limb(hood, V(k * 0.18, 0.3, -0.05), V(k * 0.24, 0.46, 0), 0.012, 0.006, glass, 4);
      limb(hood, V(k * 0.18, 0.3, -0.05), V(k * 0.29, 0.32, 0.04), 0.01, 0.005, glass, 4);
    }
    // The sapling: a thin live trunk and a few leaves, the only green thing that grew for her.
    const sp = G(kp, -0.3, 0.15, 0.1);
    limb(sp, V(0, 0, 0), V(0.03, 0.55, 0.01), 0.025, 0.012, wood, 5);
    for (let i = 0; i < 6; i++) {
      const lf = M(new THREE.SphereGeometry(0.07, 5, 4), leaf, sp);
      lf.position.set(Math.cos(i * 1.1) * 0.09, 0.42 + (i % 3) * 0.06, Math.sin(i * 1.1) * 0.09);
      lf.scale.set(1, 0.4, 0.7);
    }
    // Seed pods of light in the moss.
    for (const [x, z] of [[0.25, -0.05], [-0.12, 0.3], [0.05, 0.33], [-0.3, -0.25]] as const) M(new THREE.SphereGeometry(0.03, 5, 4), sap, kp).position.set(x, 0.2, z);
    glow(st, '#dfe8ff', 1.8, V(0, 1.9, 0), 0.55);
    glow(kp, '#8adf4a', 2.0, V(0, 0.3, 0), 0.4);
  }
  if ((o?.fallen ?? []).includes('vela')) {
    // Vela: a post of the wall walk's stone with her black glass bow hung on it, snapped at the grip, its two limbs
    // crossed and its thread of light gone out; her empty quiver and her hood at its foot; a cold light over it.
    const vt = G(g, ...slot('vela'));
    vt.rotation.y = -Math.PI / 9;
    const vglass = metal('#1c2029', { roughness: 0.18, metalness: 0.55 });
    const cold = emis('#e4eeff', '#5f8cff', 2.4);
    const hood = cloth('#141724', { side: THREE.DoubleSide });
    const wood = std('#151417', { roughness: 0.85 });
    M(new THREE.BoxGeometry(0.62, 0.1, 0.62), blockD, vt).position.y = 0.05;
    M(new THREE.BoxGeometry(0.26, 1.5, 0.26), block, vt).position.y = 0.85;
    M(new THREE.BoxGeometry(0.34, 0.08, 0.34), blockD, vt).position.y = 1.62;
    // An iron peg near the top, the two halves of the bow hung crossed from it, in front of the post.
    M(new THREE.CylinderGeometry(0.025, 0.025, 0.18, 6), iron, vt).position.set(0, 1.38, 0.17);
    for (const k of [-1, 1]) {
      const half = G(vt, k * 0.16, 1.4, 0.2 + (k > 0 ? 0.03 : 0));
      half.rotation.z = -k * 0.4;
      const pts: THREE.Vector3[] = [V(0, 0, 0), V(0.05 * k, -0.3, 0.02), V(0.06 * k, -0.6, 0.0), V(0.02 * k, -0.82, -0.03), V(-0.04 * k, -0.9, -0.01)];
      for (let i = 0; i < pts.length - 1; i++) limb(half, pts[i] as THREE.Vector3, pts[i + 1] as THREE.Vector3, 0.026, 0.022, vglass, 6);
      // The crack of light along the limb, a last glow at its tip.
      for (let i = 0; i < 3; i++) limb(half, (pts[i] as THREE.Vector3).clone().add(V(0, 0, 0.022)), (pts[i + 1] as THREE.Vector3).clone().add(V(0, 0, 0.022)), 0.006, 0.006, cold, 3);
      M(new THREE.SphereGeometry(0.03, 6, 4), pale, half).position.copy(pts[4] as THREE.Vector3);
      // The snapped grip, splintered.
      const gr = M(new THREE.ConeGeometry(0.03, 0.1, 4), wood, half);
      gr.position.set(0, 0.05, 0);
    }
    // The quiver leaning on the post, empty but for broken shafts.
    const q = G(vt, 0.28, 0, 0.12);
    q.rotation.set(0.1, 0, -0.32);
    M(new THREE.CylinderGeometry(0.075, 0.06, 0.6, 8), wood, q).position.y = 0.3;
    for (const [x, h] of [[0.02, 0.16], [-0.025, 0.1]] as const) M(new THREE.CylinderGeometry(0.008, 0.008, h, 4), wood, q).position.set(x, 0.6 + h / 2, 0);
    // The hood fallen at the foot, folded on the step.
    const hd = M(new THREE.ConeGeometry(0.2, 0.3, 10, 1, true), hood, vt);
    hd.position.set(-0.25, 0.18, 0.2);
    hd.rotation.set(1.2, 0.3, 0.2);
    // The cold light over it: she rests, and keeps watching.
    glow(vt, '#dfe8ff', 1.5, V(0, 1.55, 0.15), 0.5);
    glow(vt, '#5f8cff', 1.3, V(0, 1.1, 0.2), 0.35);
  }
  if ((o?.fallen ?? []).includes('ansel')) {
    // Ansel: a lectern of the library's stone, his book open on it, burnt black, one page still glowing at its edge;
    // his charred staff broken across its foot, the cage of its crown cold; a cold light over it.
    const at2 = G(g, ...slot('ansel'));
    at2.rotation.y = Math.PI / 5;
    // A little over life size, so the book reads from the bastion's high camera.
    at2.scale.setScalar(1);
    const char2 = std('#140d08', { roughness: 0.92 });
    const pg = std('#2a221d', { roughness: 0.9, side: THREE.DoubleSide });
    const fire = emis('#ffd0a0', '#ff7a1f', 2.4);
    M(new THREE.BoxGeometry(0.66, 0.1, 0.66), blockD, at2).position.y = 0.05;
    M(new THREE.CylinderGeometry(0.12, 0.18, 1.05, 6), block, at2).position.y = 0.6;
    const top = G(at2, 0, 1.15, 0);
    top.rotation.x = -0.45;
    M(new THREE.BoxGeometry(0.6, 0.06, 0.44), blockD, top);
    // The book, open: two charred covers, burnt pages, a glowing edge, ash curling up.
    for (const k of [-1, 1]) {
      const cov = M(new THREE.BoxGeometry(0.26, 0.02, 0.34), char2, top);
      cov.position.set(k * 0.13, 0.04, 0);
      cov.rotation.z = -k * 0.12;
      const leaf = M(new THREE.BoxGeometry(0.24, 0.03, 0.31), pg, top);
      leaf.position.set(k * 0.12, 0.065, 0);
      leaf.rotation.z = -k * 0.12;
    }
    const edge = M(new THREE.BoxGeometry(0.22, 0.012, 0.012), fire, top);
    edge.position.set(0.12, 0.085, 0.155);
    const curl = M(new THREE.BoxGeometry(0.2, 0.01, 0.16), pg, top);
    curl.position.set(-0.08, 0.14, -0.04);
    curl.rotation.set(0.5, 0, 0.7);
    // The staff, snapped in two, lying across the step; the cage of its crown dark.
    for (const [x, z, ry, len] of [[0.05, 0.28, 0.4, 0.9], [-0.28, -0.05, -1.1, 0.7]] as const) {
      const st = M(new THREE.CylinderGeometry(0.025, 0.03, len, 6), char2, at2);
      st.position.set(x, 0.13, z);
      st.rotation.set(0, ry, Math.PI / 2);
    }
    const cage = G(at2, -0.55, 0.18, -0.3);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const rib = M(new THREE.BoxGeometry(0.014, 0.2, 0.014), iron, cage);
      rib.position.set(Math.sin(a) * 0.07, 0, Math.cos(a) * 0.07);
      rib.rotation.set(Math.cos(a) * 0.35 + 1.3, 0, -Math.sin(a) * 0.35);
    }
    // The cold light over it, and the last ember of the library under it.
    glow(at2, '#dfe8ff', 1.5, V(0, 1.5, 0), 0.5);
    glow(at2, '#ff7a1f', 1.2, V(0, 1.2, 0.05), 0.35);
  }
  if ((o?.fallen ?? []).includes('bruni')) {
    // Bruni: his anvil on a stump of forge stone, his hammer split down the haft laid across it, its head cracked
    // and cold; the stubby forge-cannon tipped over at its foot, a last blue arc in its bore; a cold light over it.
    const bp = G(g, ...slot('bruni'));
    bp.rotation.y = -Math.PI / 9;
    bp.scale.setScalar(1);
    const soot = std('#141517', { roughness: 0.9 });
    const arc = emis('#c8f6ff', '#22c4ff', 2.4);
    M(new THREE.CylinderGeometry(0.42, 0.48, 0.14, 8), blockD, bp).position.y = 0.07;
    M(new THREE.CylinderGeometry(0.3, 0.36, 0.34, 8), block, bp).position.y = 0.31;
    // The anvil: waist, body, the horn.
    M(new THREE.BoxGeometry(0.26, 0.16, 0.2), iron, bp).position.y = 0.56;
    M(new THREE.BoxGeometry(0.62, 0.16, 0.28), iron, bp).position.y = 0.72;
    const horn = M(new THREE.ConeGeometry(0.11, 0.34, 8), iron, bp);
    horn.rotation.z = -Math.PI / 2;
    horn.position.set(0.47, 0.73, 0);
    horn.scale.set(1, 1, 0.8);
    // The hammer across it, the haft split, the head of black glass with its cracks gone pale.
    const hm = G(bp, -0.05, 0.84, 0.02);
    hm.rotation.set(0, 0.5, Math.PI / 2 - 0.08);
    M(new THREE.BoxGeometry(0.34, 0.2, 0.2), glass, hm).position.y = -0.32;
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.05, 0.23, 0.23), iron, hm).position.set(k * 0.18, -0.32, 0);
    M(new THREE.BoxGeometry(0.3, 0.014, 0.21), pale, hm).position.set(0, -0.3, 0);
    M(new THREE.CylinderGeometry(0.028, 0.03, 0.42, 6), char, hm).position.set(0.02, 0.0, 0);
    const half = M(new THREE.CylinderGeometry(0.02, 0.026, 0.36, 5), char, hm);
    half.position.set(-0.05, 0.4, 0.06);
    half.rotation.z = 0.35;
    // The cannon tipped at the anvil's foot, its bore still holding a blue arc.
    const cn = G(bp, 0.18, 0.13, 0.46);
    cn.rotation.set(0.15, -0.9, 0);
    const barrel = M(new THREE.CylinderGeometry(0.1, 0.12, 0.62, 10), glass, cn);
    barrel.rotation.x = Math.PI / 2;
    for (const z of [-0.2, 0.05, 0.25]) M(new THREE.TorusGeometry(0.115, 0.02, 4, 12), iron, cn).position.z = z;
    const bore = M(new THREE.CircleGeometry(0.07, 10), arc, cn);
    bore.position.z = 0.32;
    M(new THREE.SphereGeometry(0.12, 8, 6), soot, cn).position.z = -0.3;
    glow(cn, '#4fe0ff', 0.7, V(0, 0, 0.4), 0.6);
    // The cold light over it: he rests.
    glow(bp, '#e4f6ff', 1.6, V(0, 1.15, 0), 0.5);
    glow(bp, '#4fe0ff', 1.4, V(0, 0.75, 0), 0.32);
  }
  if (o?.ragna === 'call') {
    // Her axe left at the graveyard's way: upright, head up, in a small cairn; red wool tied on its haft; warm.
    const rp = G(g, ...slot('ragna'));
    rp.rotation.y = Math.PI / 6;
    // A touch bigger than the felled pieces: alone, with no stump under it, it must still read from the high camera.
    rp.scale.setScalar(1.35);
    const cairnM = stone('#8a8e94');
    const cairnD = stone('#62666c');
    const woolM = std('#b0382a', { roughness: 0.95 });
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      const r = M(new THREE.DodecahedronGeometry(0.15, 0), i % 2 ? cairnM : cairnD, rp);
      r.position.set(Math.sin(a) * 0.3, 0.08, Math.cos(a) * 0.3);
      r.rotation.set(i, i * 2, 0);
      r.scale.set(1, 0.65, 0.9);
    }
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + 0.4;
      const r = M(new THREE.DodecahedronGeometry(0.12, 0), i % 2 ? cairnD : cairnM, rp);
      r.position.set(Math.sin(a) * 0.15, 0.22, Math.cos(a) * 0.15);
      r.rotation.set(i * 3, i, 0);
      r.scale.set(1, 0.7, 0.9);
    }
    const ax = G(rp, 0, 0.18, 0);
    ax.rotation.z = 0.05;
    M(new THREE.CylinderGeometry(0.026, 0.03, 1.25, 6), char, ax).position.y = 0.62;
    for (const y of [0.2, 1.18]) M(new THREE.CylinderGeometry(0.034, 0.034, 0.06, 6), iron, ax).position.y = y;
    const blade = new THREE.Shape();
    blade.moveTo(0, 0.12);
    blade.quadraticCurveTo(0.2, 0.2, 0.32, 0.3);
    blade.quadraticCurveTo(0.24, 0.0, 0.32, -0.24);
    blade.quadraticCurveTo(0.16, -0.12, 0, -0.08);
    blade.lineTo(0, 0.12);
    const bg = new THREE.ExtrudeGeometry(blade, { depth: 0.03, bevelEnabled: false });
    bg.translate(0.02, 0, -0.015);
    M(bg, glass, ax).position.y = 1.05;
    M(new THREE.BoxGeometry(0.012, 0.3, 0.034), ember, ax).position.set(0.33, 1.08, 0);
    // The clan's red wool, wound round the haft and its ends hanging.
    for (const y of [0.78, 0.86]) M(new THREE.CylinderGeometry(0.045, 0.045, 0.05, 8), woolM, ax).position.y = y;
    for (let k = 0; k < 3; k++) {
      const w = M(new THREE.BoxGeometry(0.03, 0.34 - k * 0.07, 0.012), woolM, ax);
      w.position.set(0.04 + k * 0.025, 0.62 - k * 0.03, 0.035);
      w.rotation.z = 0.12 + k * 0.08;
    }
    // A warm light: she went home.
    glow(rp, '#ffb27a', 1.5, V(0, 1.15, 0), 0.5);
    glow(rp, '#ff8a6e', 1.0, V(0, 0.3, 0), 0.32);
  }
  if ((o?.fallen ?? []).includes('ragna') && o?.ragna !== 'call') {
    // Ragna: her great axe driven into a stump, her war horn split down its length hanging from the haft by its
    // strap, the bell gone cold but for a last crimson spark; a cold light over it.
    const rp = G(g, ...slot('ragna'));
    rp.rotation.y = Math.PI / 6;
    rp.scale.setScalar(1);
    const wood = std('#2a211c', { roughness: 0.9 });
    const hornM = std('#a8a29c', { roughness: 0.7 });
    const blood = emis('#ffb0c4', '#ff1f50', 2.2);
    M(new THREE.CylinderGeometry(0.34, 0.4, 0.42, 9), wood, rp).position.y = 0.21;
    M(new THREE.CylinderGeometry(0.3, 0.33, 0.03, 9), std('#5a4a3e', { roughness: 0.9 }), rp).position.y = 0.43;
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const root = M(new THREE.ConeGeometry(0.07, 0.3, 4), wood, rp);
      root.position.set(Math.sin(a) * 0.38, 0.06, Math.cos(a) * 0.38);
      root.rotation.set(Math.cos(a) * 1.2, 0, -Math.sin(a) * 1.2);
    }
    // The axe, blade down in the wood: the haft standing, the glass blade half sunk, its edge gone pale.
    const ax = G(rp, 0.02, 0.44, 0);
    ax.rotation.z = -0.12;
    M(new THREE.CylinderGeometry(0.026, 0.028, 1.15, 6), char, ax).position.y = 0.62;
    for (const y of [0.12, 1.12]) M(new THREE.CylinderGeometry(0.034, 0.034, 0.06, 6), iron, ax).position.y = y;
    const blade = new THREE.Shape();
    blade.moveTo(0, 0.12);
    blade.quadraticCurveTo(0.2, 0.2, 0.32, 0.3);
    blade.quadraticCurveTo(0.24, 0.0, 0.32, -0.24);
    blade.quadraticCurveTo(0.16, -0.12, 0, -0.08);
    blade.lineTo(0, 0.12);
    const bg = new THREE.ExtrudeGeometry(blade, { depth: 0.03, bevelEnabled: false });
    bg.translate(0.02, 0, -0.015);
    const bl = M(bg, glass, ax);
    bl.position.y = 0.06;
    bl.rotation.z = Math.PI;
    M(new THREE.BoxGeometry(0.012, 0.3, 0.034), pale, ax).position.set(-0.3, 0.08, 0);
    // The horn hanging from the haft by its strap, split, its two halves apart.
    const hn = G(ax, 0.05, 0.95, 0.06);
    M(new THREE.BoxGeometry(0.02, 0.2, 0.012), char, hn).position.set(0, -0.08, 0);
    for (const k of [-1, 1]) {
      const half = M(new THREE.ConeGeometry(0.07, 0.42, 8, 1, true, k < 0 ? 0 : Math.PI, Math.PI), hornM, hn);
      half.position.set(k * 0.02, -0.35, 0.02);
      half.rotation.set(Math.PI, 0, k * 0.12);
    }
    const band = M(new THREE.TorusGeometry(0.065, 0.012, 3, 10), iron, hn);
    band.rotation.x = Math.PI / 2;
    band.position.y = -0.18;
    M(new THREE.SphereGeometry(0.02, 5, 4), blood, hn).position.set(0, -0.15, 0.06);
    glow(hn, '#ff3a6a', 0.5, V(0, -0.2, 0.06), 0.5);
    // The cold light over it: she rests.
    glow(rp, '#e8eeff', 1.6, V(0, 1.3, 0), 0.5);
    glow(rp, '#ff3a6a', 1.2, V(0, 0.6, 0), 0.28);
    if (o?.ragna === 'home') {
      // Brought home (El Cuerno 4): a ring of cairn stones round the stump and the clan's red wool on the haft.
      const cairnM = stone('#8a8e94');
      const cairnD = stone('#62666c');
      const woolM = std('#b0382a', { roughness: 0.95 });
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const r = M(new THREE.DodecahedronGeometry(0.11, 0), i % 2 ? cairnM : cairnD, rp);
        r.position.set(Math.sin(a) * 0.62, 0.06, Math.cos(a) * 0.62);
        r.rotation.set(i, i * 2, 0);
        r.scale.set(1, 0.65, 0.9);
      }
      for (const y of [0.78, 0.86]) M(new THREE.CylinderGeometry(0.044, 0.044, 0.05, 8), woolM, ax).position.y = y;
      for (let k = 0; k < 3; k++) {
        const w = M(new THREE.BoxGeometry(0.03, 0.3 - k * 0.06, 0.012), woolM, ax);
        w.position.set(-0.04 - k * 0.025, 0.64 - k * 0.03, 0.035);
        w.rotation.z = -0.12 - k * 0.08;
      }
    }
  }
  return done(c, 2.1);
};

/**
 * Brann and Hilde's bets (docs/SAGAS.md): every bet the group wins, the one who doubted pays a barrel, and it
 * goes by the keep's front, in the keep's frame, where the bastion's high camera sees it. One barrel stands
 * upright on a trestle with its tap and a tin mug, a little beer at its foot; more lie on their sides in a
 * pyramid (two, then three over one, up to four, three and two). Each head is painted with the payer's colour
 * (Brann's faded blue, Hilde's forge orange); a chalk board keeps the score. Merged by material: a handful of
 * draw calls however high the pile.
 */
export interface BarrelLook {
  /** How many bets were won (1 to 9). */
  barrels: number;
  /** Who paid each one, in order ('brann' | 'hilde'); missing ones alternate. */
  from?: string[];
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the pile stands in the keep's frame (for a flare over it): in front of the keep, toward the camera. */
export const BARREL_SPOT = V(-0.75, 0.6, 1.85);

const ONE = new THREE.Vector3(1, 1, 1);
const PAYER: Record<string, string> = { brann: '#3a5a8c', hilde: '#d0642a' };
/** The rows of the pile, bottom first, by how many barrels lie in it. */
const ROWS: number[][] = [[], [1], [2], [2, 1], [3, 1], [3, 2], [3, 2, 1], [4, 3], [4, 3, 1], [4, 3, 2]];

export const barrelPileTrophy = (o?: BaseOpts & BarrelLook): Model => {
  const c = begin(o, 0.9, 58);
  const { g, rng } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, BARREL_SPOT.x, 0, BARREL_SPOT.z);
  at.rotation.y = o?.centered ? Math.PI / 2 : Math.PI / 2 - 0.35;
  const n = Math.max(0, Math.min(9, o?.barrels ?? 0));
  // One cask: a bulging lathe of oak lying along x, three iron hoops, two heads.
  const R = 0.38;
  const L = 0.5;
  const prof: THREE.Vector2[] = [];
  for (let i = 0; i <= 10; i++) {
    const y = -L + (i / 10) * 2 * L;
    prof.push(new THREE.Vector2(R * 0.84 + R * 0.16 * Math.cos((y / L) * (Math.PI / 2)), y));
  }
  const bulge = (x: number): number => R * 0.84 + R * 0.16 * Math.cos((x / L) * (Math.PI / 2));
  const body = new THREE.LatheGeometry(prof, 16).rotateZ(Math.PI / 2);
  const hoops = mergeGeometries([-0.4, -0.16, 0.16, 0.4].map((x) => new THREE.TorusGeometry(bulge(x) + 0.008, 0.018, 4, 16).rotateY(Math.PI / 2).translate(x, 0, 0)), false);
  const heads = mergeGeometries([-1, 1].map((s) => new THREE.CylinderGeometry(R * 0.82, R * 0.82, 0.02, 16).rotateZ(Math.PI / 2).translate(s * (L - 0.006), 0, 0)), false);
  const paint = mergeGeometries([-1, 1].map((s) => new THREE.CircleGeometry(R * 0.52, 14).rotateY((s * Math.PI) / 2).translate(s * (L + 0.006), 0, 0)), false);
  const parts: Record<string, THREE.BufferGeometry[]> = { body: [], hoops: [], heads: [], brann: [], hilde: [], wood: [], tin: [] };
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const newest = new THREE.Matrix4();
  const put = (mm: THREE.Matrix4, who: string): void => {
    parts.body!.push(body.clone().applyMatrix4(mm));
    parts.hoops!.push(hoops.clone().applyMatrix4(mm));
    parts.heads!.push(heads.clone().applyMatrix4(mm));
    (parts[who === 'hilde' ? 'hilde' : 'brann'] as THREE.BufferGeometry[]).push(paint.clone().applyMatrix4(mm));
  };
  const box = (w: number, h: number, d: number, x: number, y: number, z: number, list: THREE.BufferGeometry[], ry = 0): void => {
    list.push(new THREE.BoxGeometry(w, h, d).rotateY(ry).translate(x, y, z));
  };
  let width = 0;
  let topY = 0;
  if (n === 1) {
    // The first one: upright on a trestle, its tap out front, a tin mug on the trestle's edge.
    const stand = 0.32;
    for (const x of [-0.3, 0.3]) for (const z of [-0.3, 0.3]) box(0.07, stand, 0.07, x, stand / 2, z, parts.wood!);
    box(0.78, 0.06, 0.78, 0, stand + 0.03, 0, parts.wood!);
    const mm = m.compose(new THREE.Vector3(0, stand + 0.06 + L, 0), q.setFromEuler(e.set(0, 0, Math.PI / 2)), ONE);
    put(mm, o?.from?.[0] ?? 'brann');
    newest.copy(mm);
    // The tap on the front stave, low down, and the mug under it.
    const tapY = stand + 0.06 + 0.18;
    parts.tin!.push(new THREE.CylinderGeometry(0.035, 0.035, 0.16, 8).rotateX(Math.PI / 2).translate(0, tapY, R + 0.06));
    parts.tin!.push(new THREE.BoxGeometry(0.05, 0.08, 0.05).translate(0, tapY + 0.04, R + 0.12));
    parts.tin!.push(new THREE.CylinderGeometry(0.06, 0.055, 0.13, 10).translate(0.12, stand + 0.06 + 0.065, R + 0.02));
    width = 0.8;
    topY = stand + 0.06 + 2 * L;
  } else if (n > 1) {
    const rows = ROWS[n] as number[];
    let i = 0;
    rows.forEach((count, row) => {
      for (let k = 0; k < count; k++, i++) {
        const z = (k - (count - 1) / 2) * (2 * R + 0.03);
        const y = R + 0.05 + row * (2 * R * 0.87);
        e.set((rng() - 0.5) * 0.5, (rng() - 0.5) * 0.1, 0);
        const mm = m.compose(new THREE.Vector3((rng() - 0.5) * 0.06, y, z), q.setFromEuler(e), ONE);
        put(mm, o?.from?.[i] ?? (i % 2 ? 'hilde' : 'brann'));
        if (i === n - 1) newest.copy(mm);
      }
    });
    width = (rows[0] as number) * (2 * R + 0.03) + 0.2;
    topY = R + 0.05 + (rows.length - 1) * (2 * R * 0.87) + R;
    // Chocks under the bottom row.
    for (const x of [-0.3, 0.3]) box(0.16, 0.08, width, x, 0.04, 0, parts.wood!);
  }
  const mats: Record<string, THREE.Material> = {
    body: std('#6a4a30', { roughness: 0.8, flatShading: true }),
    hoops: metal('#3c3a38', { roughness: 0.6 }),
    heads: std('#86623f', { roughness: 0.85 }),
    brann: emis(PAYER.brann as string, PAYER.brann as string, 0.25, { roughness: 0.7 }),
    hilde: emis(PAYER.hilde as string, PAYER.hilde as string, 0.25, { roughness: 0.7 }),
    wood: DWOOD,
    tin: metal('#9aa0a8', { roughness: 0.4 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    M(mergeGeometries(list, false), mats[k] as THREE.Material, at);
    for (const x of list) x.dispose();
  }
  for (const x of [body, hoops, heads, paint]) x.dispose();
  if (n) {
    // A spill of beer at its foot: dark amber, glossy.
    const puddle = M(new THREE.CircleGeometry(0.42, 18), std('#5a3412', { roughness: 0.15, metalness: 0.2, transparent: true, opacity: 0.85 }), at);
    puddle.rotation.x = -Math.PI / 2;
    puddle.position.set(0.55, 0.012, n === 1 ? 0.45 : width / 2 - 0.2);
    puddle.scale.set(1, 0.7, 1);
    // The tally board on its stake beside the pile.
    const post = G(at, -0.55, 0, width / 2 + 0.15);
    limb(post, V(0, 0, 0), V(0, 1.15, 0), 0.04, 0.032, DWOOD, 5);
    const board = G(post, 0, 1.0, 0.03);
    board.rotation.y = -0.35;
    M(new THREE.BoxGeometry(0.62, 0.4, 0.04), std('#23262c', { roughness: 0.95 }), board);
    M(new THREE.BoxGeometry(0.66, 0.04, 0.05), DWOOD, board).position.y = 0.22;
    // Chalk tallies, one per barrel, the fifth across the four.
    const chalk = mergeGeometries(
      Array.from({ length: n }, (_, i) => {
        const grp = Math.floor(i / 5);
        const k = i % 5;
        const gm = new THREE.BoxGeometry(0.016, 0.22, 0.006);
        if (k < 4) return gm.translate(-0.2 + grp * 0.26 + k * 0.04, 0, 0.024);
        return gm.scale(1, 1.2, 1).rotateZ(1.1).translate(-0.2 + grp * 0.26 + 0.06, 0, 0.026);
      }),
      false,
    );
    M(chalk, emis('#eef0f4', '#eef0f4', 0.35, { roughness: 1 }), board);
    // A red ribbon knotted round the newest one: the bet just won.
    const band = M(new THREE.TorusGeometry(R + 0.02, 0.028, 4, 20).rotateY(Math.PI / 2).translate(0.16, 0, 0), cloth('#c23a2a'), at);
    band.matrixAutoUpdate = false;
    band.matrix.copy(newest);
    const knot = M(new THREE.BoxGeometry(0.09, 0.09, 0.12).translate(0.16, R + 0.03, 0), cloth('#c23a2a'), at);
    knot.matrixAutoUpdate = false;
    knot.matrix.copy(newest);
    // A warm glow over the pile so it catches the eye from the high camera, and a red one on the newest.
    glowSprite(at, '#ffb488', 1.9, V(0, topY * 0.5, 0), 0.14);
    const top = new THREE.Vector3().setFromMatrixPosition(newest);
    glowSprite(at, '#ff8a6e', 1.0, V(top.x + 0.16, top.y + R + 0.12, top.z), 0.3);
  }
  return done(c, Math.max(1.3, topY + 0.3));
};

/*
 * The clue board of «Caras prestadas» (docs/SAGAS.md): a board of rough planks on two posts under a little
 * roof, in the keep's frame by its front-right, toward the camera. One paper note pinned on it for each clue
 * the group found (up to a dozen, in three rows), red thread from pin to pin between some, and a lantern
 * hung on the post so it reads at night. Merged by material: a handful of draw calls however many notes.
 */
export interface ClueBoardLook {
  /** Clues found (0: just the board). */
  clues: number;
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the board stands in the keep's frame (for a flare over it). */
export const CLUES_SPOT = V(2.6, 1.0, 1.85);

export const clueBoardTrophy = (o?: BaseOpts & ClueBoardLook): Model => {
  const c = begin(o, 0.9, 63);
  const { g, rng } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, CLUES_SPOT.x, 0, CLUES_SPOT.z);
  // Square to the bastion's camera (it looks from yaw π/4; the keep's frame turns π/6), and big enough to read from there.
  at.rotation.y = o?.centered ? 0 : Math.PI / 4 - Math.PI / 6;
  if (!o?.centered) at.scale.setScalar(1.6);
  // The board leans back on its posts, so its notes face the high camera.
  const face = G(at, 0, 0.55, -0.04);
  face.rotation.x = -0.5;
  const n = Math.max(0, Math.min(12, o?.clues ?? 0));
  const parts: Record<string, THREE.BufferGeometry[]> = { post: [], plank: [], roof: [], paper: [], pin: [], iron: [] };
  /** Pieces on the leaning board (planks, notes, pins, thread) are built relative to its foot. */
  const onFace = new Set(['plank', 'paper', 'pin']);
  const box = (key: string, w: number, h: number, d: number, x: number, y: number, z: number, rz = 0, ry = 0): THREE.BufferGeometry => {
    const gm = new THREE.BoxGeometry(w, h, d).rotateZ(rz).rotateY(ry).translate(x, y, z);
    (parts[key] as THREE.BufferGeometry[]).push(gm);
    return gm;
  };
  // Two posts, the planks between them, a little pitched roof over it.
  const W = 1.6;
  const H = 1.1;
  const base = 0.55;
  for (const x of [-W / 2 - 0.05, W / 2 + 0.05]) box('post', 0.1, base + H + 0.35, 0.1, x, (base + H + 0.35) / 2, 0);
  for (let i = 0; i < 5; i++) box('plank', W, H / 5 - 0.012, 0.05, 0, base + (i + 0.5) * (H / 5), 0, (rng() - 0.5) * 0.012);
  box('plank', W + 0.16, 0.06, 0.07, 0, base - 0.02, 0.01);
  // A narrow cap over the posts, not a roof (a roof would hide the notes from above).
  (parts.roof as THREE.BufferGeometry[]).push(new THREE.BoxGeometry(W + 0.3, 0.06, 0.12).translate(0, base + H + 0.38, 0));
  // The notes: pale paper, a little crooked, pinned with red; thread between some of them.
  const pins: THREE.Vector3[] = [];
  // Rows of four, each row centred, so a few notes fill the middle of the board instead of a corner.
  const rows = Math.ceil(n / 4);
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / 4);
    const inRow = Math.min(4, n - row * 4);
    const col = i % 4;
    const x = (col - (inRow - 1) / 2) * 0.38 + (rng() - 0.5) * 0.06;
    const y = base + H / 2 + ((rows - 1) / 2 - row) * 0.33 + (rng() - 0.5) * 0.04;
    box('paper', 0.3 + rng() * 0.04, 0.26 + rng() * 0.03, 0.014, x, y, 0.035, (rng() - 0.5) * 0.22);
    const pin = new THREE.Vector3(x, y + 0.1, 0.055);
    pins.push(pin);
    (parts.pin as THREE.BufferGeometry[]).push(new THREE.SphereGeometry(0.04, 8, 6).translate(pin.x, pin.y, pin.z));
  }
  // The thread: from each note to the next one or two (the group's guesses joined).
  for (let i = 0; i + 1 < pins.length; i++) {
    const a = pins[i] as THREE.Vector3;
    const b = pins[i + (i % 3 === 2 && i + 2 < pins.length ? 2 : 1)] as THREE.Vector3;
    const d = b.clone().sub(a);
    const len = d.length();
    (parts.pin as THREE.BufferGeometry[]).push(new THREE.BoxGeometry(len, 0.022, 0.012).rotateZ(Math.atan2(d.y, d.x)).translate((a.x + b.x) / 2, (a.y + b.y) / 2, 0.056));
  }
  // The lantern on its hook on the right post.
  const lx = W / 2 + 0.05;
  box('iron', 0.24, 0.025, 0.025, lx + 0.1, base + H + 0.1, 0.06);
  box('iron', 0.12, 0.02, 0.12, lx + 0.2, base + H - 0.02, 0.06);
  box('iron', 0.14, 0.03, 0.14, lx + 0.2, base + H - 0.2, 0.06);
  const mats: Record<string, THREE.Material> = {
    post: DWOOD,
    plank: std('#6b6964', { roughness: 0.95 }),
    roof: std('#3b3f47', { roughness: 0.9 }),
    paper: emis('#eef0f4', '#eef0f4', 0.55, { roughness: 1 }),
    pin: emis('#ff4f3a', '#ff4f3a', 1.4, { roughness: 0.6 }),
    iron: metal('#2a2b30', { roughness: 0.6, metalness: 0.5 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(onFace.has(k) ? merged.translate(0, -base, 0) : merged, mats[k] as THREE.Material, onFace.has(k) ? face : at);
  }
  M(new THREE.BoxGeometry(0.1, 0.15, 0.1), emis('#ffcf8a', '#ffb060', 2.2), at).position.set(lx + 0.2, base + H - 0.11, 0.06);
  glowSprite(at, '#ffd6a0', 0.7, V(lx + 0.2, base + H - 0.11, 0.08), 0.6);
  // Warm light falling on the notes, and a red glint when there are any (it catches the eye at the high camera).
  glowSprite(face, '#ffd6a0', 2.2, V(0, H / 2, 0.18), 0.2);
  glowSprite(at, RED, 2.6, V(0, base + H / 2, 0.1), n ? 0.22 : 0.1);
  return done(c, base + H + 0.45);
};

/*
 * «La cuerda de faroles de esa noche» (Caras prestadas 2, docs/SAGAS.md): the string of paper lanterns the village
 * hung across the plaza on the night of lanterns, hung again in the bastion between two tall posts in front of the
 * keep, between the barrels and the clue board. Seven paper lanterns, white and red, sagging on the rope, each lit
 * from inside (self-lit shades and glow sprites, no lights). Merged by material.
 */
export interface LanternsLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the string hangs in the keep's frame (its middle, for a flare). */
export const LANTERNS_SPOT = V(1.0, 1.9, 3.35);

export const lanternStringTrophy = (o?: BaseOpts & LanternsLook): Model => {
  const c = begin(o, 1.2, 91);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, LANTERNS_SPOT.x, 0, LANTERNS_SPOT.z);
  // Across the camera's view (it looks from yaw π/4 in a frame turned π/6), so the whole string shows.
  at.rotation.y = o?.centered ? 0 : Math.PI / 4 - Math.PI / 6;
  if (!o?.centered) at.scale.setScalar(1.55);
  const W = 1.7;
  const top = 1.75;
  const parts: Record<string, THREE.BufferGeometry[]> = { post: [], rope: [], white: [], red: [], cap: [] };
  for (const x of [-W / 2, W / 2]) {
    parts.post?.push(new THREE.CylinderGeometry(0.04, 0.055, top + 0.1, 6).translate(x, (top + 0.1) / 2, 0));
    parts.post?.push(new THREE.BoxGeometry(0.22, 0.04, 0.04).translate(x, top + 0.06, 0));
    // A foot of stones so the post stands.
    parts.cap?.push(new THREE.CylinderGeometry(0.11, 0.13, 0.08, 7).translate(x, 0.04, 0));
  }
  const n = 7;
  const sag = 0.3;
  const pt = (k: number): THREE.Vector3 => V(-W / 2 + W * k, top - sag * 4 * k * (1 - k), 0);
  let prev = pt(0);
  const steps = 16;
  for (let i = 1; i <= steps; i++) {
    const p = pt(i / steps);
    const d = p.clone().sub(prev);
    const mid = p.clone().add(prev).multiplyScalar(0.5);
    const geo = new THREE.CylinderGeometry(0.009, 0.009, d.length(), 4);
    geo.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), d.clone().normalize()));
    parts.rope?.push(geo.translate(mid.x, mid.y, mid.z));
    prev = p;
  }
  const lights: THREE.Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const k = (i + 1) / (n + 1);
    const p = pt(k);
    const at2 = V(p.x, p.y - 0.2 - (i % 2) * 0.03, p.z);
    const red = i % 3 === 1;
    parts[red ? 'red' : 'white']?.push(new THREE.SphereGeometry(0.13, 10, 8).scale(1, 1.28, 1).translate(at2.x, at2.y, at2.z));
    parts.cap?.push(new THREE.CylinderGeometry(0.045, 0.055, 0.035, 6).translate(at2.x, at2.y + 0.17, at2.z));
    parts.cap?.push(new THREE.CylinderGeometry(0.035, 0.03, 0.03, 6).translate(at2.x, at2.y - 0.175, at2.z));
    parts.rope?.push(new THREE.CylinderGeometry(0.005, 0.005, 0.05, 3).translate(at2.x, at2.y + 0.17, at2.z));
    lights.push(at2);
  }
  const mats: Record<string, THREE.Material> = {
    post: DWOOD,
    rope: std('#2a2420', { roughness: 0.9 }),
    white: emis('#f4f1ec', '#ffe6c8', 1.1, { roughness: 1 }),
    red: emis('#ff6a52', '#ff4f3a', 1.3, { roughness: 1 }),
    cap: std('#2a2b30', { roughness: 0.7 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  for (let i = 0; i < lights.length; i++) {
    const p = lights[i] as THREE.Vector3;
    glowSprite(at, i % 3 === 1 ? '#ff6a52' : '#ffe2c0', 0.9, V(p.x, p.y, p.z + 0.02), 0.55);
  }
  // A soft wash of light under the string, so it reads from the high camera even by day.
  glowSprite(at, '#ffd6a0', 2.4, V(0, top - 0.4, 0.05), 0.14);
  return done(c, top + 0.2);
};

/*
 * «El farol que lo desenmascaró» (Caras prestadas 4, docs/SAGAS.md): the lantern the Prestacaras lost its face under,
 * set up in the bastion on its tall iron post, left of the barrels toward the camera. A caged paper lantern lit from
 * inside, and caught on the cage a long strip of pale paper — what was left of the borrowed face. Merged by
 * material; self-lit shade and glow sprites, no lights.
 */
export interface FacesLanternLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where it stands in the keep's frame (its lantern, for a flare). */
export const FACES_LANTERN_SPOT = V(-2.3, 2.3, 3.4);

export const facesLanternTrophy = (o?: BaseOpts & FacesLanternLook): Model => {
  const c = begin(o, 1.1, 92);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, FACES_LANTERN_SPOT.x, 0, FACES_LANTERN_SPOT.z);
  at.rotation.y = o?.centered ? 0 : Math.PI / 4 - Math.PI / 6;
  if (!o?.centered) at.scale.setScalar(2.2);
  const H = 1.55;
  const parts: Record<string, THREE.BufferGeometry[]> = { iron: [], stone: [], shade: [], paper: [] };
  // A foot of stones, the post, an arm out toward the camera.
  parts.stone?.push(new THREE.CylinderGeometry(0.2, 0.26, 0.12, 7).translate(0, 0.06, 0));
  parts.stone?.push(new THREE.CylinderGeometry(0.14, 0.18, 0.08, 7).translate(0, 0.16, 0));
  parts.iron?.push(new THREE.CylinderGeometry(0.035, 0.05, H, 6).translate(0, H / 2 + 0.1, 0));
  parts.iron?.push(new THREE.BoxGeometry(0.04, 0.04, 0.42).translate(0, H + 0.05, 0.17));
  parts.iron?.push(new THREE.CylinderGeometry(0.008, 0.008, 0.16, 4).translate(0, H - 0.04, 0.34));
  // The cage round the lantern: a cap, a floor, four bars; the paper shade inside.
  const L = V(0, H - 0.34, 0.34);
  parts.iron?.push(new THREE.ConeGeometry(0.17, 0.13, 4).rotateY(Math.PI / 4).translate(L.x, L.y + 0.22, L.z));
  parts.iron?.push(new THREE.CylinderGeometry(0.13, 0.13, 0.03, 4).rotateY(Math.PI / 4).translate(L.x, L.y - 0.19, L.z));
  for (let i = 0; i < 4; i++) {
    const a = Math.PI / 4 + (i * Math.PI) / 2;
    parts.iron?.push(new THREE.BoxGeometry(0.018, 0.4, 0.018).translate(L.x + Math.cos(a) * 0.11, L.y, L.z + Math.sin(a) * 0.11));
  }
  parts.shade?.push(new THREE.CylinderGeometry(0.095, 0.095, 0.32, 10).translate(L.x, L.y, L.z));
  // The strip of the borrowed face, caught on a bar, hanging and curling.
  for (let i = 0; i < 5; i++) {
    const seg = new THREE.PlaneGeometry(0.07 - i * 0.006, 0.09);
    seg.rotateZ(0.12 * i).rotateY(0.5 + i * 0.25);
    parts.paper?.push(seg.translate(L.x + 0.12 + i * 0.012, L.y - 0.05 - i * 0.085, L.z + 0.05 + i * 0.01));
  }
  const mats: Record<string, THREE.Material> = {
    iron: std('#2a2b30', { roughness: 0.55 }),
    stone: stone('#7a7e86'),
    shade: emis('#fff0dc', '#ffd6a0', 1.5, { roughness: 1 }),
    paper: std('#e8ecf4', { roughness: 0.9, side: THREE.DoubleSide }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#ffe2c0', 1.0, V(L.x, L.y, L.z + 0.02), 0.7);
  glowSprite(at, '#ffb070', 2.6, V(L.x, L.y - 0.2, L.z), 0.18);
  // A cold glint off the paper.
  glowSprite(at, '#dfe6f4', 0.5, V(L.x + 0.15, L.y - 0.2, L.z + 0.1), 0.35);
  return done(c, H + 0.35);
};

/*
 * «Un corral con dos jabalíes de piedra» (El Cuerno 1, docs/SAGAS.md): the clan of the Horn's thanks for the drive, a
 * small round corral of weathered rails on the keep's right flank toward the camera, with two of their stone boars in
 * it, one rooting at the hay and one looking out, a water trough and a post at the gate with the clan's red wool and
 * a horn hung from it. Merged by material: rails, stone, hay, the clan's red; no lights.
 */
export interface CorralLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the corral stands in the keep's frame (its middle, for a flare). */
export const CORRAL_SPOT = V(3.9, 0.7, -0.8);

export const corralTrophy = (o?: BaseOpts & CorralLook): Model => {
  const c = begin(o, 1.3, 93);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, CORRAL_SPOT.x, 0, CORRAL_SPOT.z);
  at.rotation.y = o?.centered ? 0 : Math.PI / 4 - Math.PI / 6;
  if (!o?.centered) at.scale.setScalar(2.0);
  const R = 0.62;
  const parts: Record<string, THREE.BufferGeometry[]> = { rail: [], post: [], hay: [], floor: [], trough: [], water: [], red: [], horn: [] };
  // The trodden floor and its hay.
  // Raised a little, so it sits clear of the bastion's tiles under it (they step by a few hundredths).
  parts.floor?.push(new THREE.CylinderGeometry(R + 0.06, R + 0.1, 0.09, 24).translate(0, 0.045, 0));
  for (let i = 0; i < 6; i++) {
    const a = 2.2 + i * 0.35;
    parts.hay?.push(new THREE.IcosahedronGeometry(0.09 + (i % 3) * 0.02, 0).scale(1.3, 0.5, 1).translate(Math.cos(a) * 0.32, 0.11, Math.sin(a) * 0.32));
  }
  // The fence: posts round the ring, two rails between each, a gap for the gate toward the camera.
  const N = 14;
  const gate = (k: number): boolean => k === 3;
  for (let k = 0; k < N; k++) {
    const a = (k / N) * Math.PI * 2;
    const p = V(Math.cos(a) * R, 0, Math.sin(a) * R);
    const tall = gate(k) || gate((k + N - 1) % N) ? 0.36 : 0.26;
    parts.post?.push(new THREE.CylinderGeometry(0.022, 0.028, tall, 5).translate(p.x, tall / 2, p.z));
    if (gate(k)) continue;
    const b = (((k + 1) % N) / N) * Math.PI * 2;
    const q = V(Math.cos(b) * R, 0, Math.sin(b) * R);
    const d = q.clone().sub(p);
    for (const y of [0.11, 0.21]) {
      const rail = new THREE.BoxGeometry(0.018, 0.022, d.length() + 0.03);
      rail.rotateY(Math.atan2(d.x, d.z));
      parts.rail?.push(rail.translate((p.x + q.x) / 2, y + ((k * 7) % 3) * 0.005, (p.z + q.z) / 2));
    }
  }
  // The gate, swung open outward.
  {
    const a = (3 / N) * Math.PI * 2;
    const h = V(Math.cos(a) * R, 0, Math.sin(a) * R);
    for (const y of [0.1, 0.2]) parts.rail?.push(new THREE.BoxGeometry(0.018, 0.02, 0.27).rotateY(a + 0.5).translate(h.x + Math.cos(a + 0.9) * 0.13, y, h.z + Math.sin(a + 0.9) * 0.13));
    // The clan's post: a red wool tuft and the herd horn hung from it.
    parts.red?.push(new THREE.ConeGeometry(0.035, 0.08, 5).rotateX(Math.PI).translate(h.x, 0.33, h.z + 0.03));
    for (let i = 0; i < 5; i++) {
      const t = i / 4;
      parts.horn?.push(new THREE.CylinderGeometry(0.012 + t * 0.012, 0.01 + t * 0.012, 0.04, 6).rotateZ(0.9 - t * 1.1).translate(h.x + 0.05 + Math.sin(t * 1.3) * 0.06, 0.27 - t * 0.04 + t * t * 0.05, h.z + 0.02));
    }
  }
  // A water trough of split log, and its water.
  parts.trough?.push(new THREE.BoxGeometry(0.34, 0.07, 0.11).translate(-0.18, 0.125, -0.32));
  parts.water?.push(new THREE.BoxGeometry(0.3, 0.01, 0.07).translate(-0.18, 0.16, -0.32));
  const mats: Record<string, THREE.Material> = {
    rail: std('#8a7a68', { roughness: 0.95 }),
    post: std('#5e4c3c', { roughness: 0.95 }),
    hay: std('#9aa06a', { roughness: 1, flatShading: true }),
    floor: std('#6a5c4c', { roughness: 1 }),
    trough: std('#4e3e30', { roughness: 0.9 }),
    water: std('#4a7088', { roughness: 0.2 }),
    red: cloth('#b0402e'),
    horn: std('#d6cbb6', { roughness: 0.6 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  // The two boars: one rooting at the hay, one looking out over the rails at the bastion.
  // A little self-light, so the stone reads under the bastion's night sky.
  const bmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true, emissive: '#3a4250', emissiveIntensity: 0.6 });
  const leg = boarLeg();
  const boar = (x: number, z: number, yaw: number, seed: number, nose: number): void => {
    const b = G(at, x, 0.09, z);
    b.rotation.y = yaw;
    b.scale.setScalar(0.46);
    const body = M(boarBody(seed), bmat, b);
    body.rotation.x = nose;
    for (const [lx, lz] of BOAR_LEGS) M(leg, bmat, b).position.set(lx, BOAR_HIP_Y, lz);
  };
  boar(0.17, -0.02, 2.4, 1, 0.22);
  boar(-0.16, 0.16, 0.45, 2, -0.05);
  // A soft pale wash on the floor so the ring reads from the high camera.
  // A herder's lantern on the gate post, and its warm light over the corral: it catches the eye like the others.
  {
    const a = (3 / 14) * Math.PI * 2;
    const L = V(Math.cos(a) * R - 0.05, 0.46, Math.sin(a) * R + 0.02);
    M(new THREE.CylinderGeometry(0.035, 0.04, 0.08, 6), emis('#fff0dc', '#ffd6a0', 1.6, { roughness: 1 }), at).position.copy(L);
    M(new THREE.ConeGeometry(0.05, 0.04, 6), std('#2a2b30', { roughness: 0.6 }), at).position.set(L.x, L.y + 0.06, L.z);
    glowSprite(at, '#ffe2c0', 0.6, L, 0.75);
  }
  glowSprite(at, '#ffd6a0', 2.4, V(0, 0.3, 0), 0.13);
  return done(c, 0.6);
};

/**
 * «Un poste de amarre del clan» (El Cuerno 2, docs/SAGAS.md): the post the clan ties itself to when the titan swings
 * its tail, given to the group after the lash. A thick carved post of dark wood beside the corral, banded with the
 * clan's three red strokes and capped with a horn, a rope coiled at its foot and another tied round it running to a
 * stake, the clan's long red streamer flying from the top. Merged by material; a soft cold glow so it reads at night.
 */
export interface PostLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the post stands in the keep's frame (its top, for a flare). */
export const POST_SPOT = V(4.5, 1.9, 1.7);

export const mooringPostTrophy = (o?: BaseOpts & PostLook): Model => {
  const c = begin(o, 1.9, 94);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, POST_SPOT.x, 0, POST_SPOT.z);
  if (!o?.centered) at.scale.setScalar(1.6);
  const parts: Record<string, THREE.BufferGeometry[]> = { wood: [], dark: [], red: [], rope: [], horn: [], stone: [] };
  // The foot: a few stones packed round it.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.3;
    parts.stone?.push(new THREE.DodecahedronGeometry(0.07 + (i % 3) * 0.015, 0).scale(1.2, 0.6, 1).translate(Math.cos(a) * 0.17, 0.03, Math.sin(a) * 0.17));
  }
  // The post: thick, eight-sided, carved in bands, slightly leaning, a knob on top.
  parts.wood?.push(new THREE.CylinderGeometry(0.1, 0.12, 1.05, 8).translate(0, 0.525, 0));
  for (const y of [0.3, 0.62, 0.9]) parts.dark?.push(new THREE.CylinderGeometry(0.115, 0.115, 0.04, 8).translate(0, y, 0));
  parts.dark?.push(new THREE.CylinderGeometry(0.07, 0.11, 0.1, 8).translate(0, 1.1, 0));
  // The clan's three red strokes, carved and painted on the face toward the bastion.
  for (let k = 0; k < 3; k++) parts.red?.push(new THREE.BoxGeometry(0.025, 0.2, 0.012).rotateZ(0.15).translate(-0.04 + k * 0.04, 0.46, 0.112));
  // The horn on its cap, curling back.
  for (let i = 0; i < 6; i++) {
    const t = i / 5;
    parts.horn?.push(new THREE.CylinderGeometry(0.018 + t * 0.03, 0.016 + t * 0.03, 0.07, 7).rotateZ(1.1 - t * 1.4).translate(0.03 + Math.sin(t * 1.4) * 0.12, 1.17 + t * 0.07 - t * t * 0.06, 0));
  }
  // A rope coiled at its foot, and one tied round it running out to a stake.
  for (let k = 0; k < 3; k++) parts.rope?.push(new THREE.TorusGeometry(0.2 - k * 0.025, 0.02, 5, 18).rotateX(Math.PI / 2).translate(0.32, 0.03 + k * 0.035, 0.12));
  parts.rope?.push(new THREE.TorusGeometry(0.115, 0.022, 5, 16).rotateX(Math.PI / 2).translate(0, 0.78, 0));
  {
    const from = V(0.1, 0.77, 0);
    const to = V(0.62, 0.1, -0.22);
    const d = to.clone().sub(from);
    const rope = new THREE.CylinderGeometry(0.014, 0.014, d.length(), 5).translate(0, d.length() / 2, 0);
    rope.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), d.clone().normalize()));
    parts.rope?.push(rope.translate(from.x, from.y, from.z));
    parts.dark?.push(new THREE.CylinderGeometry(0.02, 0.012, 0.2, 5).rotateZ(-0.3).translate(to.x + 0.02, 0.08, to.z));
  }
  // The streamer: a long strip of red wool from the top, blown out by the wind in a few kinked pieces.
  for (let i = 0; i < 5; i++) parts.red?.push(new THREE.BoxGeometry(0.16, 0.06 - i * 0.006, 0.012).rotateZ(-0.12 - i * 0.05).translate(-0.12 - i * 0.15, 1.02 - i * 0.05 + (i % 2) * 0.02, 0.0));
  const mats: Record<string, THREE.Material> = {
    wood: std('#5e4c3c', { roughness: 0.9, flatShading: true }),
    dark: std('#3e3229', { roughness: 0.9, flatShading: true }),
    red: cloth('#b0402e'),
    rope: std('#a29478', { roughness: 1 }),
    horn: std('#d6cbb6', { roughness: 0.6 }),
    stone: std('#6e7378', { roughness: 1, flatShading: true }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  // A pale wash round its foot and a glint on the horn, so it reads from the high camera at night.
  glowSprite(at, '#dfe9ff', 1.6, V(0, 0.4, 0), 0.12);
  glowSprite(at, '#ffe2c0', 0.5, V(0.1, 1.2, 0), 0.35);
  return done(c, 0.6);
};

/**
 * «La lanza que lo remató» (El Cuerno 3, docs/SAGAS.md): the spear that finished the Old Uro, planted in the ground
 * left of the keep toward the camera, «que la vean desde lejos». A long ash-wood shaft leaning a little, bound in
 * leather at the grip and under the head, a broad leaf blade of dark iron with the edge still glowing where it went
 * into the ember of the bull; the clan's red wool and a tuft of the Uro's black mane tied under the blade, a heap of
 * grey ash and a broken horn tip at its foot. Merged by material; a cold wash at the foot and an ember glint on the
 * blade so it reads from the high camera at night.
 */
export interface SpearLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the spear stands in the keep's frame (its blade, for a flare). */
export const SPEAR_SPOT = V(-3.0, 3.6, 1.5);

export const spearTrophy = (o?: BaseOpts & SpearLook): Model => {
  const c = begin(o, 1.4, 95);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, SPEAR_SPOT.x, 0, SPEAR_SPOT.z);
  if (!o?.centered) at.scale.setScalar(1.7);
  // The spear leans back and to the side, as it was left: everything on it is built upright, then tipped.
  const lean = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(-0.12, 0, 0.1));
  const parts: Record<string, THREE.BufferGeometry[]> = { wood: [], leather: [], iron: [], edge: [], red: [], mane: [], ash: [], horn: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo.applyMatrix4(lean));
  };
  // The shaft, a little thicker at the butt, sunk into the ground.
  on('wood', new THREE.CylinderGeometry(0.03, 0.04, 2.0, 7).translate(0, 0.95, 0));
  // Leather bindings: at the grip and under the head.
  for (const [y, h] of [[0.85, 0.22], [1.88, 0.1]] as const) on('leather', new THREE.CylinderGeometry(0.043, 0.043, h, 7).translate(0, y, 0));
  // The socket and the blade: a broad leaf, its two faces bevelled, a rib down the middle.
  on('iron', new THREE.CylinderGeometry(0.028, 0.042, 0.16, 7).translate(0, 2.02, 0));
  {
    const leaf = new THREE.Shape();
    leaf.moveTo(0, 0);
    leaf.quadraticCurveTo(0.13, 0.12, 0.09, 0.32);
    leaf.quadraticCurveTo(0.05, 0.46, 0, 0.58);
    leaf.quadraticCurveTo(-0.05, 0.46, -0.09, 0.32);
    leaf.quadraticCurveTo(-0.13, 0.12, 0, 0);
    const blade = new THREE.ExtrudeGeometry(leaf, { depth: 0.02, bevelEnabled: true, bevelThickness: 0.012, bevelSize: 0.012, bevelSegments: 1, curveSegments: 6 });
    blade.translate(0, 2.09, -0.01);
    on('iron', blade);
    // The edge that went into it, still glowing: a thin seam round the tip.
    const rim = [V(0.1, 2.36, 0), V(0.07, 2.5, 0), V(0.025, 2.62, 0), V(0, 2.68, 0), V(-0.025, 2.62, 0), V(-0.07, 2.5, 0), V(-0.1, 2.36, 0)];
    on('edge', new THREE.TubeGeometry(new THREE.CatmullRomCurve3(rim), 14, 0.017, 4));
  }
  // The clan's red wool and a hank of the Uro's black mane, knotted under the blade and hanging.
  for (let i = 0; i < 4; i++) on('red', new THREE.BoxGeometry(0.035, 0.26 - i * 0.03, 0.012).rotateZ(0.12 * (i - 1.5)).translate(0.05 + i * 0.02, 1.73 - i * 0.02, 0.03));
  for (let i = 0; i < 4; i++) on('mane', new THREE.ConeGeometry(0.03, 0.3, 4).rotateX(Math.PI).rotateZ(-0.15 * (i - 1.5)).translate(-0.05 - i * 0.02, 1.7, -0.02));
  // At its foot: a heap of the grey ash it crumbled to, and the broken tip of one of its horns.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.4;
    const r = 0.12 + (i % 3) * 0.08;
    parts.ash?.push(new THREE.SphereGeometry(0.12 - (i % 3) * 0.02, 7, 4).scale(1.3, 0.35, 1.1).translate(Math.cos(a) * r, 0.02, Math.sin(a) * r));
  }
  for (let i = 0; i < 6; i++) {
    const t = i / 5;
    parts.horn?.push(new THREE.CylinderGeometry(0.035 - t * 0.026, 0.04 - t * 0.026, 0.09, 7).rotateZ(1.2 - t * 0.6).translate(0.32 + Math.sin(t * 1.1) * 0.22, 0.05 + t * 0.08, 0.16));
  }
  const mats: Record<string, THREE.Material> = {
    wood: std('#6a5848', { roughness: 0.85, flatShading: true }),
    leather: std('#3a2a22', { roughness: 0.95 }),
    iron: metal('#5a606a', { roughness: 0.4 }),
    edge: emis('#ff8a6e', '#ff4f3a', 1.6),
    red: cloth('#b0402e'),
    mane: cloth('#151316', { side: THREE.DoubleSide }),
    ash: std('#5f6168', { roughness: 1, flatShading: true }),
    horn: std('#aeb3ba', { roughness: 0.55, flatShading: true }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#dfe9ff', 1.7, V(0, 0.35, 0), 0.13);
  glowSprite(at, '#ff4f3a', 0.55, V(-0.2, 2.45, 0.25), 0.55);
  return done(c, 0.6);
};

/**
 * Orla's map table (La Quemadura 1, docs/QUEMADURA.md): the wayfarer's trestle table set up by the keep, with the
 * Burn half drawn on it. A long parchment pinned by stones and an inkpot, the titan's shell outlined in ink, the Burn
 * a charcoal blot inked on one half and only pencilled on the other (dotted lines, a red route that stops where the
 * patrol turned back, a cross where they found them); a candle at the corner, Orla's knotted measuring staff leaning
 * on it, a basket of rolled maps at its foot. Merged by material; a warm wash under it and the candle's glint, so it
 * reads from the high camera.
 */
export interface MapTableLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the map table stands in the keep's frame (over its middle, for a flare). */
export const MAP_TABLE_SPOT = V(-3.5, 1.5, -1.3);

export const mapTableTrophy = (o?: BaseOpts & MapTableLook): Model => {
  const c = begin(o, 1.4, 96);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, MAP_TABLE_SPOT.x, 0, MAP_TABLE_SPOT.z);
  if (!o?.centered) {
    at.scale.setScalar(1.75);
    at.rotation.y = 0.5;
  }
  const parts: Record<string, THREE.BufferGeometry[]> = { wood: [], dark: [], paper: [], ink: [], burn: [], pencil: [], red: [], stone: [], wax: [], flame: [], rolls: [], basket: [], knot: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  const TOP = 0.78;
  // The trestle table: a thick top of planks, two A-trestles and a stretcher between them.
  for (let i = 0; i < 4; i++) on(i % 2 ? 'wood' : 'dark', new THREE.BoxGeometry(1.3, 0.06, 0.2).translate(0, TOP - 0.03, -0.3 + i * 0.2));
  for (const sx of [-0.5, 0.5]) {
    for (const sz of [-1, 1]) on('dark', new THREE.BoxGeometry(0.06, 0.82, 0.06).rotateX(sz * 0.32).translate(sx, 0.38, sz * 0.13));
    on('wood', new THREE.BoxGeometry(0.08, 0.06, 0.7).translate(sx, TOP - 0.09, 0));
  }
  on('dark', new THREE.BoxGeometry(1.0, 0.05, 0.05).translate(0, 0.3, 0));
  // The parchment, a little wider than the top on one side, its far corner curling up.
  on('paper', new THREE.BoxGeometry(1.1, 0.012, 0.66).translate(0, TOP + 0.006, 0));
  on('paper', new THREE.CylinderGeometry(0.04, 0.04, 0.3, 8).rotateX(Math.PI / 2).translate(0.56, TOP + 0.04, -0.18));
  const flat = (shape: THREE.Shape, key: string, y: number): void => on(key, new THREE.ShapeGeometry(shape, 6).rotateX(-Math.PI / 2).translate(0, TOP + y, 0));
  // The titan's shell outlined in ink: an oval of short strokes, the head and the tail at its ends.
  const shell = (t: number): [number, number] => [Math.cos(t) * 0.44, Math.sin(t) * 0.25];
  for (let i = 0; i < 28; i++) {
    const t0 = (i / 28) * Math.PI * 2;
    const [x0, z0] = shell(t0);
    const [x1, z1] = shell(t0 + (Math.PI * 2) / 28);
    const len = Math.hypot(x1 - x0, z1 - z0);
    on('ink', new THREE.BoxGeometry(len, 0.004, 0.01).rotateY(-Math.atan2(z1 - z0, x1 - x0)).translate((x0 + x1) / 2, TOP + 0.014, (z0 + z1) / 2));
  }
  on('ink', new THREE.CircleGeometry(0.06, 10).rotateX(-Math.PI / 2).translate(0.5, TOP + 0.014, 0));
  // The Burn: inked solid on the near half, a ragged blot…
  const blot = new THREE.Shape();
  const pts = 14;
  for (let i = 0; i <= pts; i++) {
    const a = Math.PI * 0.5 + (i / pts) * Math.PI;
    const r = 0.17 + Math.sin(i * 2.7) * 0.03;
    const x = -0.05 + Math.cos(a) * r;
    const z = Math.sin(a) * r * 0.75;
    if (i === 0) blot.moveTo(x, z);
    else blot.lineTo(x, z);
  }
  flat(blot, 'burn', 0.016);
  // …and only pencilled on the far half: dotted, unfinished.
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI * 0.5 + (i / 9) * Math.PI;
    const r = 0.17 + Math.sin((i + 14) * 2.7) * 0.03;
    on('pencil', new THREE.BoxGeometry(0.03, 0.003, 0.008).rotateY(-a - Math.PI / 2).translate(-0.05 + Math.cos(a) * r, TOP + 0.015, Math.sin(a) * r * 0.75));
  }
  // The patrol's route in red, from the edge of the shell to where they were found (a cross), stopping there.
  const route: [number, number][] = [[0.36, 0.14], [0.27, 0.1], [0.2, 0.12], [0.13, 0.06], [0.08, 0.04]];
  for (let i = 0; i < route.length - 1; i++) {
    const [x0, z0] = route[i] as [number, number];
    const [x1, z1] = route[i + 1] as [number, number];
    on('red', new THREE.BoxGeometry(Math.hypot(x1 - x0, z1 - z0), 0.004, 0.012).rotateY(-Math.atan2(z1 - z0, x1 - x0)).translate((x0 + x1) / 2, TOP + 0.017, (z0 + z1) / 2));
  }
  for (const r of [0.7, -0.7]) on('red', new THREE.BoxGeometry(0.06, 0.004, 0.012).rotateY(r).translate(0.08, TOP + 0.018, 0.04));
  // Stones and an inkpot on the corners.
  for (const [x, z] of [[-0.5, 0.28], [0.48, 0.28], [-0.5, -0.28]] as const) on('stone', new THREE.DodecahedronGeometry(0.05, 0).scale(1.2, 0.7, 1).translate(x, TOP + 0.03, z));
  on('dark', new THREE.CylinderGeometry(0.035, 0.04, 0.06, 8).translate(0.46, TOP + 0.03, -0.27));
  on('wax', new THREE.CylinderGeometry(0.004, 0.004, 0.12, 4).rotateZ(0.5).translate(0.47, TOP + 0.09, -0.27));
  // The candle on a plate, its flame.
  on('stone', new THREE.CylinderGeometry(0.06, 0.06, 0.012, 10).translate(-0.48, TOP + 0.006, -0.2));
  on('wax', new THREE.CylinderGeometry(0.025, 0.028, 0.14, 8).translate(-0.48, TOP + 0.08, -0.2));
  on('flame', new THREE.ConeGeometry(0.018, 0.06, 6).translate(-0.48, TOP + 0.18, -0.2));
  // Orla's measuring staff leaning on the table's end, knotted every pace.
  const staff = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(0, 0, 0.22)).setPosition(0.86, 0, 0.12);
  on('wood', new THREE.CylinderGeometry(0.02, 0.022, 1.5, 6).translate(0, 0.75, 0).applyMatrix4(staff));
  for (let i = 1; i < 7; i++) on('knot', new THREE.TorusGeometry(0.024, 0.008, 4, 8).rotateX(Math.PI / 2).translate(0, i * 0.2, 0).applyMatrix4(staff));
  // A basket of rolled maps at the foot.
  on('basket', new THREE.CylinderGeometry(0.16, 0.13, 0.26, 10, 1, true).translate(-0.72, 0.13, 0.22));
  on('basket', new THREE.CircleGeometry(0.13, 10).rotateX(-Math.PI / 2).translate(-0.72, 0.02, 0.22));
  for (let i = 0; i < 5; i++) on('rolls', new THREE.CylinderGeometry(0.03, 0.03, 0.42, 8).rotateZ((i - 2) * 0.12).rotateX((i % 2) * 0.1 - 0.05).translate(-0.72 + (i - 2) * 0.05, 0.3, 0.22 + ((i % 3) - 1) * 0.04));
  const mats: Record<string, THREE.Material> = {
    wood: std('#7a6450', { roughness: 0.85, flatShading: true }),
    dark: std('#4a3a2e', { roughness: 0.9, flatShading: true }),
    paper: std('#d8ccb0', { roughness: 0.95, side: THREE.DoubleSide }),
    ink: std('#2a2a30', { roughness: 0.9, side: THREE.DoubleSide }),
    burn: std('#1c1618', { roughness: 0.9, side: THREE.DoubleSide, emissive: '#3a1008', emissiveIntensity: 0.5 }),
    pencil: std('#6e6a66', { roughness: 0.9 }),
    red: emis('#ff8a6e', '#ff4f3a', 0.6),
    stone: std('#8a8680', { roughness: 0.9, flatShading: true }),
    wax: std('#e6e1d8', { roughness: 0.7 }),
    flame: emis('#ffe2b0', '#ffb070', 2.2),
    rolls: std('#cfc3a8', { roughness: 0.95 }),
    basket: std('#6b5238', { roughness: 1, side: THREE.DoubleSide, flatShading: true }),
    knot: std('#c9bba0', { roughness: 0.8 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#ffe2c0', 1.8, V(0, 0.3, 0), 0.13);
  glowSprite(at, '#ffb070', 0.5, V(-0.48, TOP + 0.2, -0.2), 0.6);
  glowSprite(at, '#ff4f3a', 0.7, V(-0.05, TOP + 0.05, 0), 0.25);
  return done(c, 0.6);
};

/**
 * The sunken camp's banner (La Quemadura 2, docs/QUEMADURA.md): the first Vigil's standard, set up by the keep. A tall
 * dark pole on a cairn, a crossbar, and a long cloth of charcoal wool, worn and scorched, its lower edge burnt ragged;
 * on it an open eye stitched in pale thread with a red iris. No gold: pale and red on dark. Merged by material; a faint
 * red wash under it and a pale glint on the eye so it reads from the high camera.
 */
export interface VigilBannerLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the banner stands in the keep's frame (over its eye, for a flare). */
export const VIGIL_BANNER_SPOT = V(2.7, 4.0, -2.6);

export const vigilBannerTrophy = (o?: BaseOpts & VigilBannerLook): Model => {
  const c = begin(o, 1.4, 97);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, VIGIL_BANNER_SPOT.x, 0, VIGIL_BANNER_SPOT.z);
  if (!o?.centered) {
    at.scale.setScalar(2.1);
    at.rotation.y = -0.35;
  }
  const parts: Record<string, THREE.BufferGeometry[]> = { pole: [], cloth: [], scorch: [], thread: [], iris: [], pupil: [], stone: [], cord: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  const TALL = 2.3;
  // The cairn holding the pole.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    on('stone', new THREE.DodecahedronGeometry(0.13, 0).scale(1.1, 0.75, 1).translate(Math.cos(a) * 0.17, 0.08, Math.sin(a) * 0.17));
  }
  on('stone', new THREE.DodecahedronGeometry(0.12, 0).translate(0, 0.2, 0));
  // The pole, a little crooked at the top, and the crossbar with its two finials.
  on('pole', new THREE.CylinderGeometry(0.035, 0.045, TALL, 6).translate(0, TALL / 2, 0));
  on('pole', new THREE.CylinderGeometry(0.025, 0.025, 0.9, 6).rotateZ(Math.PI / 2).translate(0, TALL - 0.12, 0.04));
  for (const x of [-0.46, 0.46]) on('pole', new THREE.SphereGeometry(0.04, 6, 4).translate(x, TALL - 0.12, 0.04));
  on('pole', new THREE.ConeGeometry(0.05, 0.14, 5).translate(0, TALL + 0.07, 0));
  // The cloth: a long panel, waved a little, its lower edge burnt into ragged tongues.
  const W = 0.8;
  const H = 1.35;
  const cloth = new THREE.PlaneGeometry(W, H, 8, 10);
  const pos = cloth.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    let ny = y;
    // Ragged foot: the lowest row eaten back unevenly.
    if (y < -H / 2 + 0.01) ny = y + Math.abs(Math.sin(x * 23.0)) * 0.22 + Math.abs(Math.sin(x * 7.3)) * 0.1;
    const z = Math.sin(x * 4.2 + y * 1.3) * 0.04 + (H / 2 - y) * 0.03;
    pos.setXYZ(i, x, ny, z);
  }
  cloth.computeVertexNormals();
  const top = TALL - 0.16;
  on('cloth', cloth.translate(0, top - H / 2, 0.06));
  // Scorch: dark blotches and a burnt band over the foot, sitting just in front of the cloth.
  for (const [x, y, s] of [[-0.25, -0.45, 0.16], [0.22, -0.52, 0.2], [0.05, -0.35, 0.1], [0.3, 0.42, 0.08], [-0.3, 0.25, 0.07]] as const) on('scorch', new THREE.CircleGeometry(s, 9).scale(1.3, 1, 1).translate(x, top - H / 2 + y, 0.075));
  // The eye: a pale almond of thread, the red iris, a dark pupil; a few lashes of thread over it.
  const eye = new THREE.Shape();
  eye.moveTo(-0.27, 0);
  eye.quadraticCurveTo(0, 0.2, 0.27, 0);
  eye.quadraticCurveTo(0, -0.2, -0.27, 0);
  const ey = top - 0.5;
  on('thread', new THREE.ShapeGeometry(eye, 8).translate(0, ey, 0.085));
  on('iris', new THREE.CircleGeometry(0.085, 14).translate(0, ey, 0.09));
  on('pupil', new THREE.CircleGeometry(0.035, 10).translate(0, ey, 0.095));
  for (let i = 0; i < 5; i++) {
    const a = Math.PI * (0.2 + i * 0.15);
    on('thread', new THREE.BoxGeometry(0.012, 0.09, 0.004).rotateZ(a - Math.PI / 2).translate(Math.cos(a) * 0.26, ey + Math.sin(a) * 0.2, 0.086));
  }
  // A border of stitching round the cloth's top, and the cords it hangs by.
  on('thread', new THREE.BoxGeometry(W * 0.92, 0.018, 0.004).translate(0, top - 0.1, 0.075));
  for (const x of [-0.3, 0, 0.3]) on('cord', new THREE.CylinderGeometry(0.008, 0.008, 0.1, 4).translate(x, top + 0.0, 0.05));
  const mats: Record<string, THREE.Material> = {
    pole: std('#3a2e26', { roughness: 0.9, flatShading: true }),
    cloth: std('#2c2a2e', { roughness: 0.95, side: THREE.DoubleSide, flatShading: true }),
    scorch: std('#140f10', { roughness: 1, side: THREE.DoubleSide, emissive: '#3a1008', emissiveIntensity: 0.35 }),
    thread: std('#d8d2c8', { roughness: 0.8, side: THREE.DoubleSide, emissive: '#d8d2c8', emissiveIntensity: 0.12 }),
    iris: emis('#c43a30', '#ff4f3a', 0.5),
    pupil: std('#141012', { roughness: 0.6, side: THREE.DoubleSide }),
    stone: std('#6a6466', { roughness: 0.95, flatShading: true }),
    cord: std('#6b2a24', { roughness: 0.9 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#ff4f3a', 1.6, V(0, 0.25, 0), 0.12);
  glowSprite(at, '#e8e0d6', 0.6, V(0, ey, 0.15), 0.3);
  return done(c, 0.6);
};

/**
 * The trap's harpoon (La Quemadura 3), once the colossus fell to it: the first Vigil's harpoon, three men long, laid up
 * across two trestles of the trap's own black beams behind the keep, against the wall. Its barbed iron head still dark
 * with old blood; the hide fins at its tail; caught on one barb, a dark scale with a pale crack across it: the dragon's.
 * No gold: black wood, grey iron, red hide. Merged by material; a pale glint on the scale and a faint red wash under it.
 */
export interface HarpoonLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the harpoon lies in the keep's frame (over its head, for a flare). */
export const HARPOON_SPOT = V(1.5, 1.9, -4.4);
/** Where the harpoon's rack stands in the keep's frame. */
const HARPOON_AT = V(-1.15, 0, -3.9);

export const harpoonTrophy = (o?: BaseOpts & HarpoonLook): Model => {
  const c = begin(o, 1.4, 113);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, HARPOON_AT.x, 0, HARPOON_AT.z);
  if (!o?.centered) {
    at.scale.setScalar(1.8);
    at.rotation.y = 0.18;
  }
  const parts: Record<string, THREE.BufferGeometry[]> = { beam: [], shaft: [], iron: [], blood: [], hide: [], scale: [], crack: [], rope: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  const H = 0.95;
  // Two trestles: an A of black beams each side, a cross piece with a notch the shaft lies in.
  for (const x of [-0.75, 0.75]) {
    for (const s of [-1, 1]) on('beam', new THREE.BoxGeometry(0.07, H * 1.12, 0.08).rotateX(s * 0.32).translate(x, H / 2, s * 0.17));
    on('beam', new THREE.BoxGeometry(0.1, 0.07, 0.42).translate(x, H - 0.02, 0));
    on('beam', new THREE.BoxGeometry(0.06, 0.06, 0.36).translate(x, 0.28, 0));
    // A lashing of rope where the shaft rests.
    on('rope', new THREE.TorusGeometry(0.075, 0.016, 4, 10).rotateY(Math.PI / 2).translate(x, H + 0.06, 0));
  }
  // The shaft, along x, a little long past both trestles.
  const SY = H + 0.07;
  on('shaft', new THREE.CylinderGeometry(0.055, 0.06, 2.5, 7).rotateZ(Math.PI / 2).translate(0.05, SY, 0));
  for (const x of [-0.95, -0.2, 0.55]) on('iron', new THREE.CylinderGeometry(0.07, 0.07, 0.05, 7).rotateZ(Math.PI / 2).translate(x, SY, 0));
  // The head: a long barbed point, a socket, two barbs swept back; dark with old blood toward the tip.
  const HX = 1.3;
  on('iron', new THREE.CylinderGeometry(0.075, 0.06, 0.16, 7).rotateZ(Math.PI / 2).translate(HX, SY, 0));
  on('iron', new THREE.ConeGeometry(0.15, 0.42, 4).rotateZ(-Math.PI / 2).translate(HX + 0.28, SY, 0));
  on('blood', new THREE.ConeGeometry(0.075, 0.2, 4).rotateZ(-Math.PI / 2).translate(HX + 0.34, SY, 0));
  for (const s of [-1, 1]) on('iron', new THREE.ConeGeometry(0.045, 0.24, 3).rotateZ(Math.PI / 2 + 0.5 * s * -1).rotateX(s * 0.2).translate(HX + 0.1, SY + s * 0.08, s * 0.02));
  // The fins at the tail: three of red hide.
  for (let i = 0; i < 3; i++) on('hide', new THREE.BoxGeometry(0.26, 0.17, 0.01).translate(0, 0.09, 0).rotateX((i / 3) * Math.PI * 2).translate(-1.05, SY, 0));
  // The scale caught on the upper barb: a dark rounded plate, a pale crack across it.
  const sc = new THREE.CircleGeometry(0.13, 7).scale(1, 1.25, 1).rotateY(Math.PI / 2 - 0.4).rotateZ(-0.3).translate(HX + 0.08, SY + 0.17, 0.06);
  on('scale', sc);
  on('crack', new THREE.BoxGeometry(0.004, 0.2, 0.012).rotateZ(0.5).rotateY(-0.4).translate(HX + 0.09, SY + 0.17, 0.075));
  // A few stones at the trestles' feet against the glass.
  for (const x of [-0.75, 0.75]) for (let i = 0; i < 3; i++) on('beam', new THREE.DodecahedronGeometry(0.06, 0).translate(x + Math.cos(i * 2.2) * 0.2, 0.04, Math.sin(i * 2.2) * 0.22));
  const mats: Record<string, THREE.Material> = {
    beam: std('#221c1c', { roughness: 0.92, flatShading: true }),
    shaft: std('#6a5848', { roughness: 0.8, flatShading: true }),
    iron: std('#a8a4a0', { roughness: 0.5, metalness: 0.45, flatShading: true }),
    blood: std('#3a1412', { roughness: 0.5, metalness: 0.3, flatShading: true }),
    hide: std('#6b2a24', { roughness: 0.95, side: THREE.DoubleSide, flatShading: true }),
    scale: std('#1c1a22', { roughness: 0.25, metalness: 0.6, side: THREE.DoubleSide, emissive: '#2a1018', emissiveIntensity: 0.4 }),
    crack: emis('#e8f0ff', '#dfe9ff', 0.8),
    rope: std('#6b5a4a', { roughness: 0.95 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#ff4f3a', 2.2, V(0.1, 0.25, 0), 0.1);
  glowSprite(at, '#dfe9ff', 0.45, V(HX + 0.09, SY + 0.17, 0.1), 0.35);
  return done(c, 0.6);
};

/**
 * The watchtower's beacon (La Quemadura 4), once its cracks were blown shut: a slim pillar of the first Vigil's black
 * stone in the bastion's far corner, the side the Burn lies on, with an iron cup on top and the fire the wayfarers
 * carried back from the tower burning in it. The Vigil's open eye cut into its foot. No gold: black stone, grey iron,
 * the fire's own orange and a pale core. Merged by material; glows for the fire's halo.
 */
export interface BeaconLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
}

/** Where the beacon's fire burns in the keep's frame (for a flare). */
export const BEACON_SPOT = V(-4.7, 4.4, -2.5);

export const beaconTrophy = (o?: BaseOpts & BeaconLook): Model => {
  const c = begin(o, 1.4, 131);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, BEACON_SPOT.x, 0, BEACON_SPOT.z);
  if (!o?.centered) at.scale.setScalar(1.45);
  const parts: Record<string, THREE.BufferGeometry[]> = { stone: [], dark: [], iron: [], char: [], fire: [], flame: [], core: [], eye: [], iris: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  const TALL = 2.55;
  // A stepped foot of rough blocks, the pillar of stone courses tapering up, a collar under the cup.
  on('stone', new THREE.CylinderGeometry(0.42, 0.5, 0.2, 6).translate(0, 0.1, 0));
  on('stone', new THREE.CylinderGeometry(0.34, 0.4, 0.18, 6).translate(0, 0.29, 0));
  const courses = 6;
  for (let i = 0; i < courses; i++) {
    const h = (TALL - 0.6) / courses;
    const r = 0.26 - i * 0.012;
    on(i % 2 ? 'dark' : 'stone', new THREE.CylinderGeometry(r - 0.01, r, h * 0.94, 6).rotateY(i * 0.2).translate(0, 0.38 + h * (i + 0.5), 0));
  }
  on('stone', new THREE.CylinderGeometry(0.32, 0.24, 0.16, 6).translate(0, TALL - 0.14, 0));
  // The cup: iron, flared, on four short struts; charred logs in it.
  on('iron', new THREE.CylinderGeometry(0.36, 0.2, 0.22, 8, 1, true).translate(0, TALL + 0.1, 0));
  on('iron', new THREE.CylinderGeometry(0.2, 0.2, 0.04, 8).translate(0, TALL, 0));
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    on('iron', new THREE.BoxGeometry(0.03, 0.26, 0.03).rotateX(0.35).rotateY(a).translate(Math.sin(a) * 0.3, TALL + 0.14, Math.cos(a) * 0.3));
  }
  for (let i = 0; i < 4; i++) on('char', new THREE.BoxGeometry(0.06, 0.06, 0.34).rotateX(0.3).rotateY(i * 0.8).translate(0, TALL + 0.12, 0));
  // The fire: an outer orange, red tongues, a pale core (built still; its glow does the rest).
  on('fire', new THREE.ConeGeometry(0.28, 0.85, 7).translate(0, TALL + 0.55, 0));
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    on('flame', new THREE.ConeGeometry(0.12, 0.55, 5).rotateZ(Math.cos(a) * 0.3).rotateX(Math.sin(a) * 0.3).translate(Math.sin(a) * 0.14, TALL + 0.4, Math.cos(a) * 0.14));
  }
  on('core', new THREE.ConeGeometry(0.13, 0.55, 6).translate(0, TALL + 0.4, 0));
  // The eye at its foot, cut into the stone facing the keep.
  const eye = new THREE.Shape();
  eye.moveTo(-0.16, 0);
  eye.quadraticCurveTo(0, 0.11, 0.16, 0);
  eye.quadraticCurveTo(0, -0.11, -0.16, 0);
  on('eye', new THREE.ShapeGeometry(eye, 8).translate(0, 0.62, 0.27));
  on('iris', new THREE.CircleGeometry(0.05, 10).translate(0, 0.62, 0.275));
  // A few stones round its foot.
  for (let i = 0; i < 6; i++) on('dark', new THREE.DodecahedronGeometry(0.09, 0).translate(Math.cos(i * 1.1) * 0.55, 0.05, Math.sin(i * 1.1) * 0.55));
  const mats: Record<string, THREE.Material> = {
    stone: std('#2a2630', { roughness: 0.95, flatShading: true }),
    dark: std('#1c1a20', { roughness: 0.95, flatShading: true }),
    iron: std('#4a4644', { roughness: 0.5, metalness: 0.5, flatShading: true, side: THREE.DoubleSide }),
    char: std('#1a1210', { roughness: 1, flatShading: true, emissive: '#5a1a08', emissiveIntensity: 0.6 }),
    fire: emis('#ff7a2a', '#ff6a2a', 1.4),
    flame: emis('#ff4f2a', '#ff4f2a', 1.2),
    core: emis('#fff0d8', '#ffe2b8', 1.6),
    eye: std('#a8a29a', { roughness: 0.8, side: THREE.DoubleSide }),
    iris: emis('#c43a30', '#ff4f3a', 0.5),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#ffb070', 2.4, V(0, TALL + 0.55, 0), 0.55);
  glowSprite(at, '#fff0d8', 0.9, V(0, TALL + 0.45, 0), 0.7);
  glowSprite(at, '#ff4f3a', 4.2, V(0, TALL + 0.4, 0), 0.14);
  return done(c, 0.6);
};

/**
 * The fast way's cart (Las vías 1), once the train came down: the mouth of Hilde's clan's fast way dug out at the keep's
 * foot, a stone arch in a timber frame over two rails that run down into the dark, a dwarven lamp hung in it, and one
 * of the clan's mine carts standing on a stretch of rail before it, riveted and iron-banded, a load of ore in it and a
 * pick leaning on it. No gold: dark stone, grey iron, old wood, the lamp's warm glass. Merged by material.
 */
export interface MineCartLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
  /** Las vías 2 done: one of Dorn's guard automatons, wound down, keeps watch beside the arch. */
  guard?: boolean;
  /** Las vías 3 done: Dorn's winding key, as long as an arm, hung on a rack on the arch's other side. */
  key?: boolean;
  /** Las vías 4 done: Bruni's forge hammer hung on a stretch of sooty forge wall by the arch. */
  hammer?: boolean;
  /** Las vías 4 done with the rails left open: the cart heaped high with the week's ore, more of it spilled beside. */
  ore?: boolean;
}

/** Where the cart stands in the keep's frame (for a flare). */
export const MINE_CART_SPOT = V(-1.5, 1.0, -3.7);
/** Where Dorn's automaton stands by the cart's arch (its helm), in the trophy's frame. */
export const GUARD_SPOT = ((): THREE.Vector3 => {
  const p = V(-0.98, 0.9, -0.25).multiplyScalar(1.5).applyAxisAngle(V(0, 1, 0), -0.35);
  return p.add(V(MINE_CART_SPOT.x, 0, MINE_CART_SPOT.z));
})();

/** Where Dorn's key hangs (its bow), in the trophy's frame. */
export const KEY_SPOT = ((): THREE.Vector3 => {
  const p = V(1.1, 1.16, -0.2).multiplyScalar(1.5).applyAxisAngle(V(0, 1, 0), -0.35);
  return p.add(V(MINE_CART_SPOT.x, 0, MINE_CART_SPOT.z));
})();

/** Where Bruni's hammer hangs (its head), in the trophy's frame. */
export const HAMMER_SPOT = ((): THREE.Vector3 => {
  const p = V(2.0, 1.15, -0.05).multiplyScalar(1.5).applyAxisAngle(V(0, 1, 0), -0.35);
  return p.add(V(MINE_CART_SPOT.x, 0, MINE_CART_SPOT.z));
})();

/** Where the ore spills beside the cart, in the trophy's frame. */
export const ORE_SPOT = ((): THREE.Vector3 => {
  const p = V(-0.75, 0.5, 1.2).multiplyScalar(1.5).applyAxisAngle(V(0, 1, 0), -0.35);
  return p.add(V(MINE_CART_SPOT.x, 0, MINE_CART_SPOT.z));
})();

export const mineCartTrophy = (o?: BaseOpts & MineCartLook): Model => {
  const c = begin(o, 1.2, 151);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, MINE_CART_SPOT.x, 0, MINE_CART_SPOT.z);
  // Along the keep's side, the arch toward its back.
  at.rotation.y = o?.centered ? 0 : -0.35;
  if (!o?.centered) at.scale.setScalar(1.5);
  const parts: Record<string, THREE.BufferGeometry[]> = { stone: [], dark: [], void: [], wood: [], plank: [], seam: [], iron: [], rivet: [], ore: [], oreRed: [], glass: [], crystal: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  // The rail: sleepers and two rails, from before the cart into the mouth.
  for (let i = 0; i < 9; i++) on(i % 2 ? 'wood' : 'plank', new THREE.BoxGeometry(0.62, 0.05, 0.12).translate(0, 0.025, 1.45 - i * 0.3));
  for (const x of [-0.2, 0.2]) {
    on('iron', new THREE.BoxGeometry(0.045, 0.05, 2.65).translate(x, 0.075, 0.22));
    on('dark', new THREE.BoxGeometry(0.07, 0.02, 2.65).translate(x, 0.04, 0.22));
  }
  // The mouth: a stone arch of rough blocks round a black opening, the rails running in.
  const back = -1.0;
  on('void', new THREE.BoxGeometry(0.86, 0.95, 0.04).translate(0, 0.48, back - 0.02));
  on('void', new THREE.CylinderGeometry(0.43, 0.43, 0.04, 12, 1, false, -Math.PI / 2, Math.PI).rotateX(Math.PI / 2).rotateZ(Math.PI / 2).translate(0, 0.95, back - 0.02));
  for (let i = 0; i < 9; i++) {
    const a = Math.PI - (i / 8) * Math.PI;
    const r = 0.55;
    on(i % 2 ? 'stone' : 'dark', new THREE.BoxGeometry(0.22, 0.24, 0.3).rotateZ(a - Math.PI / 2).translate(Math.cos(a) * r, 0.95 + Math.sin(a) * r, back));
  }
  for (const sx of [-1, 1]) for (let k = 0; k < 4; k++) on(k % 2 ? 'dark' : 'stone', new THREE.BoxGeometry(0.24, 0.24, 0.32).translate(sx * 0.55, 0.12 + k * 0.24, back));
  // The rock it is dug into, heaped round the arch.
  for (let i = 0; i < 9; i++) {
    const a = (i / 8) * Math.PI;
    on('dark', new THREE.DodecahedronGeometry(0.22 + (i % 3) * 0.06, 0).translate(Math.cos(a) * 0.95, 0.15 + Math.sin(a) * 0.9, back - 0.2));
  }
  // The timber frame in front of the arch, braced, a lamp hung from its lintel.
  for (const sx of [-1, 1]) {
    on('wood', new THREE.BoxGeometry(0.1, 1.5, 0.1).translate(sx * 0.78, 0.75, back + 0.25));
    on('plank', new THREE.BoxGeometry(0.06, 0.34, 0.06).rotateZ(sx * 0.8).translate(sx * 0.64, 1.38, back + 0.25));
  }
  on('wood', new THREE.BoxGeometry(1.8, 0.13, 0.14).translate(0, 1.55, back + 0.25));
  on('iron', new THREE.BoxGeometry(0.012, 0.16, 0.012).translate(0, 1.42, back + 0.32));
  on('iron', new THREE.BoxGeometry(0.15, 0.03, 0.15).translate(0, 1.33, back + 0.32));
  on('iron', new THREE.BoxGeometry(0.13, 0.025, 0.13).translate(0, 1.13, back + 0.32));
  for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) on('iron', new THREE.BoxGeometry(0.015, 0.2, 0.015).translate(x * 0.06, 1.23, back + 0.32 + z * 0.06));
  on('glass', new THREE.CylinderGeometry(0.05, 0.055, 0.15, 6).translate(0, 1.23, back + 0.32));
  // The cart on the rail: a riveted tub of planks wider at the top, iron bands and lip, ore heaped in it, low wheels.
  const cz = 0.45;
  // (The cart is built at a hero's scale, then made bigger: it is the trophy, the arch only its setting.)
  const before = Object.fromEntries(Object.entries(parts).map(([k, l]) => [k, l.length]));
  const tub = (): THREE.CylinderGeometry => new THREE.CylinderGeometry(0.34, 0.27, 0.3, 4, 1).rotateY(Math.PI / 4) as THREE.CylinderGeometry;
  on('plank', tub().scale(0.82, 1, 1.12).translate(0, 0.28, cz));
  for (const z of [-0.12, 0.12]) for (const sx of [-1, 1]) on('seam', new THREE.BoxGeometry(0.012, 0.27, 0.02).rotateZ(sx * -0.11).translate(sx * 0.255, 0.28, cz + z));
  for (const y of [0.2, 0.33]) on('iron', new THREE.CylinderGeometry(0.335, 0.31, 0.045, 4, 1, true).rotateY(Math.PI / 4).scale(0.84, 1, 1.14).translate(0, y, cz));
  on('iron', new THREE.CylinderGeometry(0.355, 0.345, 0.035, 4, 1, true).rotateY(Math.PI / 4).scale(0.84, 1, 1.14).translate(0, 0.43, cz));
  for (const y of [0.2, 0.33]) for (const t of [-0.15, 0, 0.15]) for (const sx of [-1, 1]) on('rivet', new THREE.SphereGeometry(0.014, 4, 3).translate(sx * (0.268 + (0.33 - y) * 0.2), y, cz + t));
  on('void', new THREE.BoxGeometry(0.46, 0.02, 0.62).translate(0, 0.4, cz));
  const lumps: [number, number, number, string][] = [
    [-0.1, 0.44, -0.16, 'ore'],
    [0.08, 0.47, -0.1, 'ore'],
    [-0.04, 0.48, 0.04, 'ore'],
    [0.12, 0.44, 0.14, 'oreRed'],
    [-0.12, 0.45, 0.17, 'ore'],
    [0.02, 0.52, -0.02, 'oreRed'],
  ];
  lumps.forEach(([x, y, z, k], i) => on(k, new THREE.DodecahedronGeometry(0.08 * (0.9 + (i % 3) * 0.25), 0).rotateY(i * 1.3).translate(x, y, cz + z)));
  if (o?.ore) {
    // The week's ore: heaped high over the lip, a few crystals catching the light in it.
    const heap: [number, number, number, string][] = [
      [-0.06, 0.56, -0.12, 'ore'],
      [0.1, 0.55, 0.06, 'oreRed'],
      [-0.1, 0.57, 0.1, 'ore'],
      [0.03, 0.63, -0.02, 'ore'],
      [0.12, 0.52, -0.2, 'ore'],
      [-0.14, 0.52, -0.22, 'oreRed'],
      [0.0, 0.69, 0.04, 'oreRed'],
    ];
    heap.forEach(([x, y, z, k], i) => on(k, new THREE.DodecahedronGeometry(0.085 * (0.95 + (i % 3) * 0.2), 0).rotateY(i * 0.9).translate(x, y, cz + z)));
    for (const [x, y, z, a] of [[0.05, 0.7, -0.1, 0.3], [-0.12, 0.62, 0.0, -0.5], [0.14, 0.6, 0.16, 0.8]] as const) on('crystal', new THREE.OctahedronGeometry(0.05, 0).scale(0.7, 1.6, 0.7).rotateZ(a).translate(x, y, cz + z));
  }
  on('dark', new THREE.BoxGeometry(0.36, 0.06, 0.62).translate(0, 0.12, cz));
  for (const z of [-0.2, 0.2])
    for (const sx of [-1, 1]) {
      on('iron', new THREE.CylinderGeometry(0.085, 0.085, 0.045, 10).rotateZ(Math.PI / 2).translate(sx * 0.2, 0.085, cz + z));
      on('rivet', new THREE.CylinderGeometry(0.03, 0.03, 0.06, 6).rotateZ(Math.PI / 2).translate(sx * 0.22, 0.085, cz + z));
    }
  // A pick leaning on its side.
  on('wood', new THREE.CylinderGeometry(0.015, 0.018, 0.6, 5).rotateZ(-0.5).translate(0.42, 0.28, cz + 0.1));
  on('iron', new THREE.BoxGeometry(0.28, 0.035, 0.035).rotateZ(-0.5 + Math.PI / 2 - 0.3).translate(0.55, 0.53, cz + 0.1));
  for (const [k, l] of Object.entries(parts)) for (let i = before[k] ?? 0; i < l.length; i++) (l[i] as THREE.BufferGeometry).translate(0, 0, -cz).scale(1.4, 1.4, 1.4).translate(0, 0, cz + 0.2);
  const mats: Record<string, THREE.Material> = {
    stone: stone('#4a4a52'),
    dark: stone('#2e2e34'),
    void: std('#050405', { roughness: 1 }),
    wood: std('#4a372b', { roughness: 0.9, flatShading: true }),
    plank: std('#5a4130', { roughness: 0.85, flatShading: true }),
    seam: std('#3e2c21', { roughness: 0.9 }),
    iron: metal('#4c4f56', { roughness: 0.55, flatShading: true }),
    rivet: metal('#8a8e96', { roughness: 0.4 }),
    ore: stone('#47464c'),
    oreRed: stone('#6a4434'),
    glass: emis('#ffcfa8', '#ffa66b', 1.4),
    crystal: emis('#d8e8ff', '#7fb4ff', 1.1),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#ffa66b', 1.6, V(0, 1.23, back + 0.32), 0.5);
  glowSprite(at, '#ffe0c4', 0.5, V(0, 1.23, back + 0.32), 0.8);
  if (o?.guard) {
    // Dorn's automaton, its light out, slumped on watch at the arch's left (the pick leans on the cart's right).
    const gm = guard({ seed: 151 });
    gm.pose('off');
    const w = G(at, -0.98, 0, back + 0.75);
    w.rotation.y = 0.45;
    w.scale.setScalar(1.0);
    w.add(gm.root);
    // A ring of rubble at its feet, where it was carried up.
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      M(new THREE.DodecahedronGeometry(0.07 + (i % 2) * 0.03, 0).rotateY(i).translate(Math.cos(a) * 0.38, 0.04, Math.sin(a) * 0.32), stone('#3a3a40'), w);
    }
  }
  if (o?.key) {
    // Dorn's winding key on a rack of dark timber at the arch's right: an iron shaft as long as an arm, its bow a crank
    // with a worn grip, its bit cut in steps, still warm from the Anvil at the tip.
    const w = G(at, 1.1, 0, back + 0.8);
    w.rotation.y = -0.35;
    w.scale.setScalar(1.35);
    const kp: Record<string, THREE.BufferGeometry[]> = { wood: [], dark: [], iron: [], bright: [], grip: [], warm: [] };
    const put = (key: string, geo: THREE.BufferGeometry): void => {
      kp[key]?.push(geo);
    };
    for (const sx of [-1, 1]) {
      put('wood', new THREE.BoxGeometry(0.08, 1.05, 0.08).translate(sx * 0.32, 0.52, 0));
      put('dark', new THREE.BoxGeometry(0.14, 0.06, 0.14).translate(sx * 0.32, 0.03, 0));
    }
    put('wood', new THREE.BoxGeometry(0.82, 0.08, 0.1).translate(0, 1.02, 0));
    put('wood', new THREE.BoxGeometry(0.72, 0.06, 0.06).rotateZ(0.08).translate(0, 0.3, 0));
    for (const x of [-0.2, 0.2]) put('dark', new THREE.CylinderGeometry(0.018, 0.018, 0.12, 5).rotateX(Math.PI / 2).translate(x, 0.92, 0.07));
    // The key across the pegs, a little aslant: its shaft, a collar, the crank bow and grip, the stepped bit.
    const km4 = new THREE.Matrix4().compose(V(0, 0.86, 0.12), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, -0.12)), V(1, 1, 1));
    const keyParts: [string, THREE.BufferGeometry][] = [
      ['iron', new THREE.CylinderGeometry(0.03, 0.03, 0.66, 8).rotateZ(Math.PI / 2)],
      ['bright', new THREE.CylinderGeometry(0.045, 0.045, 0.05, 8).rotateZ(Math.PI / 2).translate(-0.2, 0, 0)],
      ['iron', new THREE.TorusGeometry(0.1, 0.022, 5, 12).translate(-0.43, 0, 0)],
      ['iron', new THREE.BoxGeometry(0.05, 0.2, 0.04).translate(-0.43, -0.1, 0)],
      ['grip', new THREE.CylinderGeometry(0.03, 0.03, 0.13, 6).rotateX(Math.PI / 2).translate(-0.43, -0.2, 0.05)],
      ['iron', new THREE.BoxGeometry(0.07, 0.12, 0.035).translate(0.3, -0.07, 0)],
      ['iron', new THREE.BoxGeometry(0.04, 0.06, 0.035).translate(0.35, -0.1, 0)],
      ['warm', new THREE.BoxGeometry(0.035, 0.035, 0.04).translate(0.34, 0, 0)],
    ];
    for (const [key, geo] of keyParts) put(key, geo.applyMatrix4(km4));
    const km: Record<string, THREE.Material> = {
      wood: std('#3d2e25', { roughness: 0.9, flatShading: true }),
      dark: stone('#2e2e34'),
      iron: metal('#5a5e66', { roughness: 0.45, flatShading: true }),
      bright: metal('#b4bac4', { roughness: 0.3 }),
      grip: std('#4a372b', { roughness: 0.8 }),
      warm: emis('#ff8a4a', '#ff6a2a', 1.2),
    };
    for (const [key, list] of Object.entries(kp)) {
      if (!list.length) continue;
      const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
      for (const x of list) x.dispose();
      if (merged) M(merged, km[key] as THREE.Material, w);
    }
    glowSprite(w, '#ff6a2a', 0.5, V(0.3, 0.86, 0.14), 0.45);
  }
  if (o?.ore) {
    // More of it spilled beside the rail, crystals in it.
    const w = G(at, -0.75, 0, 1.2);
    const op: Record<string, THREE.BufferGeometry[]> = { ore: [], oreRed: [], crystal: [] };
    for (let i = 0; i < 9; i++) {
      const a = i * 2.4;
      const r = 0.06 + (i % 4) * 0.07;
      op[i % 3 === 0 ? 'oreRed' : 'ore']?.push(new THREE.DodecahedronGeometry(0.08 + (i % 3) * 0.03, 0).rotateY(i).translate(Math.cos(a) * r, 0.06 + (i < 3 ? 0.08 : 0), Math.sin(a) * r));
    }
    for (const [x, z, t] of [[0.02, 0.04, 0.2], [-0.12, -0.05, -0.4], [0.14, -0.1, 0.6]] as const) op.crystal?.push(new THREE.OctahedronGeometry(0.06, 0).scale(0.7, 1.8, 0.7).rotateZ(t).translate(x, 0.2, z));
    const om: Record<string, THREE.Material> = { ore: stone('#47464c'), oreRed: stone('#6a4434'), crystal: emis('#d8e8ff', '#7fb4ff', 1.1) };
    for (const [key, list] of Object.entries(op)) {
      if (!list.length) continue;
      const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
      for (const x of list) x.dispose();
      if (merged) M(merged, om[key] as THREE.Material, w);
    }
    glowSprite(w, '#7fb4ff', 0.6, V(0, 0.22, 0), 0.35);
  }
  if (o?.hammer) {
    // Bruni's hammer on a stretch of the forge's sooty brick wall by the arch: two iron hooks, the hammer across them,
    // its haft dark with use, its head heavy and square, his forge-blue still faint in the runes cut in its cheek.
    const w = G(at, 2.0, 0, -0.3);
    w.rotation.y = 0.1;
    w.scale.setScalar(1.45);
    const hp: Record<string, THREE.BufferGeometry[]> = { brick: [], brickDark: [], soot: [], iron: [], haft: [], face: [], rune: [] };
    const put = (key: string, geo: THREE.BufferGeometry): void => {
      hp[key]?.push(geo);
    };
    put('brickDark', new THREE.BoxGeometry(1.0, 1.2, 0.2).translate(0, 0.6, 0));
    for (let row = 0; row < 8; row++)
      for (let k = 0; k < 5; k++) {
        const x = -0.4 + k * 0.2 + (row % 2) * 0.1;
        if (x > 0.45) continue;
        put((row + k) % 3 ? 'brick' : 'brickDark', new THREE.BoxGeometry(0.18, 0.13, 0.03).translate(x, 0.08 + row * 0.15, 0.1));
      }
    put('brickDark', new THREE.BoxGeometry(1.08, 0.08, 0.26).translate(0, 1.22, 0));
    put('soot', new THREE.BoxGeometry(0.5, 0.5, 0.01).translate(0.05, 0.95, 0.12));
    for (const x of [-0.25, 0.25]) put('iron', new THREE.BoxGeometry(0.03, 0.12, 0.1).translate(x, 0.72, 0.15));
    const hm = new THREE.Matrix4().compose(V(0, 0.8, 0.2), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0.06)), V(1, 1, 1));
    const ham: [string, THREE.BufferGeometry][] = [
      ['haft', new THREE.CylinderGeometry(0.03, 0.035, 0.8, 6).rotateZ(Math.PI / 2).translate(-0.08, 0, 0)],
      ['iron', new THREE.CylinderGeometry(0.04, 0.04, 0.06, 6).rotateZ(Math.PI / 2).translate(-0.47, 0, 0)],
      ['iron', new THREE.BoxGeometry(0.2, 0.3, 0.18).translate(0.4, 0, 0)],
      ['face', new THREE.BoxGeometry(0.21, 0.02, 0.19).translate(0.4, 0.16, 0)],
      ['face', new THREE.BoxGeometry(0.21, 0.02, 0.19).translate(0.4, -0.16, 0)],
      ['rune', new THREE.BoxGeometry(0.02, 0.12, 0.006).translate(0.36, 0, 0.093)],
      ['rune', new THREE.BoxGeometry(0.06, 0.02, 0.006).translate(0.4, 0.04, 0.093)],
      ['rune', new THREE.BoxGeometry(0.02, 0.08, 0.006).rotateZ(0.6).translate(0.44, -0.03, 0.093)],
    ];
    for (const [key, geo] of ham) put(key, geo.applyMatrix4(hm));
    const hmats: Record<string, THREE.Material> = {
      brick: std('#6a3e30', { roughness: 0.9, flatShading: true }),
      brickDark: std('#3a2620', { roughness: 0.95, flatShading: true }),
      soot: std('#141214', { roughness: 1, transparent: true, opacity: 0.55 }),
      iron: metal('#3a3d44', { roughness: 0.5, flatShading: true }),
      haft: std('#3a2a20', { roughness: 0.8 }),
      face: metal('#b4bac4', { roughness: 0.3 }),
      rune: emis('#c8f6ff', '#22c4ff', 1.2),
    };
    for (const [key, list] of Object.entries(hp)) {
      if (!list.length) continue;
      const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
      for (const x of list) x.dispose();
      if (merged) M(merged, hmats[key] as THREE.Material, w);
    }
    glowSprite(w, '#22c4ff', 0.45, V(0.4, 0.8, 0.32), 0.35);
  }
  return done(c, 0.6);
};

/**
 * Pell's bucket (El pozo 1), once the well was shut: the well-keeper left it in the bastion until the water runs clear.
 * A wooden bucket with two iron hoops and a rope handle, a coil of its rope beside it, set on a low round curb of the
 * village's grey stone, a wet dark ring on the stone where it stood. No gold: grey stone, old wood, grey iron, hemp; a
 * cold glint of water in it. Merged by material.
 */
export interface BucketLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
  /**
   * El pozo 2 done: beside the curb, the jar of well water Pell sent up once the fountain ran. A big round clay tinaja,
   * its belly ribbed, a wooden lid half off and clear water glinting in its mouth, on a ring of straw.
   */
  jar?: boolean;
  /**
   * El pozo 3 done: the fair's banner on its pole behind the curb, as big as the fair was. `grande`: a tall pole, a long
   * banner with the well and the millstone on it, bunting strung to the curb; `mediana`: a middling banner; `chica`: a
   * small pennant on a short staff.
   */
  banner?: 'grande' | 'mediana' | 'chica';
}

/** Where the banner flies in the keep's frame (for a flare). */
export const BANNER_SPOT = V(-2.9, 2.6, -0.3);
/** Where the bucket stands in the keep's frame (for a flare). */
export const BUCKET_SPOT = V(-2.45, 0.9, 0.15);
/** Where the jar stands in the keep's frame (for a flare). */
export const JAR_SPOT = V(-2.0, 1.1, 0.75);

export const bucketTrophy = (o?: BaseOpts & BucketLook): Model => {
  const c = begin(o, 1.2, 137);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, BUCKET_SPOT.x, 0, BUCKET_SPOT.z);
  if (!o?.centered) {
    at.scale.setScalar(1.7);
    at.rotation.y = 0.5;
  }
  const parts: Record<string, THREE.BufferGeometry[]> = { stone: [], dark: [], wood: [], stave: [], iron: [], hemp: [], water: [], clay: [], clayD: [], cloth: [], trim: [], stitch: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  // The curb: a low drum of fitted stones, a capstone ring on top, two loose stones at its foot.
  on('stone', new THREE.CylinderGeometry(0.34, 0.37, 0.22, 10).translate(0, 0.11, 0));
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    on('dark', new THREE.BoxGeometry(0.012, 0.2, 0.2).rotateY(-a).translate(Math.cos(a) * 0.36, 0.11, Math.sin(a) * 0.36));
  }
  on('stone', new THREE.TorusGeometry(0.31, 0.045, 5, 14).rotateX(Math.PI / 2).translate(0, 0.23, 0));
  on('dark', new THREE.CylinderGeometry(0.3, 0.3, 0.01, 14).translate(0, 0.226, 0));
  for (const [x, z, s] of [[0.42, 0.18, 0.08], [-0.3, 0.35, 0.06]] as const) on('stone', new THREE.DodecahedronGeometry(s, 0).translate(x, s * 0.6, z));
  // The bucket on it: tapered staves, two hoops, the rope handle up.
  const B = 0.24;
  on('wood', new THREE.CylinderGeometry(0.16, 0.13, 0.28, 12).translate(0, B + 0.14, 0));
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    on('stave', new THREE.BoxGeometry(0.006, 0.27, 0.02).rotateY(-a).translate(Math.cos(a) * 0.148, B + 0.14, Math.sin(a) * 0.148));
  }
  for (const [y, r] of [[B + 0.06, 0.137], [B + 0.24, 0.158]] as const) on('iron', new THREE.TorusGeometry(r, 0.012, 4, 16).rotateX(Math.PI / 2).translate(0, y, 0));
  on('water', new THREE.CylinderGeometry(0.145, 0.145, 0.006, 14).translate(0, B + 0.25, 0));
  on('hemp', new THREE.TorusGeometry(0.16, 0.012, 4, 12, Math.PI).translate(0, B + 0.28, 0));
  // A coil of rope beside it on the capstone, its end trailing down the side.
  for (let k = 0; k < 3; k++) on('hemp', new THREE.TorusGeometry(0.08 - k * 0.012, 0.016, 5, 12).rotateX(Math.PI / 2).translate(0.17, 0.27 + k * 0.022, 0.13));
  on('hemp', new THREE.CylinderGeometry(0.014, 0.014, 0.24, 5).rotateZ(0.2).translate(0.29, 0.14, 0.19));
  // The jar beside the curb (its spot in the bucket's own frame), on its ring of straw.
  if (o?.jar) {
    const jx = 0.32;
    const jz = 0.42;
    on('hemp', new THREE.TorusGeometry(0.2, 0.05, 5, 14).rotateX(Math.PI / 2).translate(jx, 0.04, jz));
    const prof: THREE.Vector2[] = [];
    for (let i = 0; i <= 12; i++) {
      const t = i / 12;
      // Narrow foot, round belly, short neck, a rolled lip.
      const r = t < 0.85 ? 0.1 + 0.17 * Math.sin(Math.PI * Math.min(1, t / 0.85) * 0.92 + 0.12) : 0.115 + (t - 0.85) * 0.25;
      prof.push(new THREE.Vector2(r, t * 0.62));
    }
    on('clay', new THREE.LatheGeometry(prof, 14).translate(jx, 0.05, jz));
    for (const y of [0.22, 0.34]) on('clayD', new THREE.TorusGeometry(0.262, 0.009, 4, 18).rotateX(Math.PI / 2).translate(jx, 0.05 + y, jz));
    on('clayD', new THREE.TorusGeometry(0.15, 0.022, 5, 14).rotateX(Math.PI / 2).translate(jx, 0.68, jz));
    on('water', new THREE.CylinderGeometry(0.13, 0.13, 0.006, 14).translate(jx, 0.655, jz));
    // The lid, half off, leant against its side.
    on('wood', new THREE.CylinderGeometry(0.16, 0.16, 0.03, 12).rotateX(1.2).translate(jx + 0.2, 0.2, jz + 0.12));
  }
  // The fair's banner behind the curb, on a pole: its size says how big the fair was.
  if (o?.banner) {
    const big = o.banner === 'grande';
    const mid = o.banner === 'mediana';
    const bx = -0.32;
    const bz = -0.3;
    const H = big ? 1.55 : mid ? 1.2 : 0.85;
    const bw = big ? 0.5 : mid ? 0.38 : 0.26;
    const bh = big ? 0.75 : mid ? 0.52 : 0.22;
    on('wood', new THREE.CylinderGeometry(0.022, 0.028, H, 6).translate(bx, H / 2, bz));
    on('stone', new THREE.CylinderGeometry(0.08, 0.1, 0.08, 8).translate(bx, 0.04, bz));
    on('iron', new THREE.SphereGeometry(0.035, 6, 5).translate(bx, H + 0.02, bz));
    on('wood', new THREE.CylinderGeometry(0.012, 0.012, bw + 0.08, 5).rotateZ(Math.PI / 2).translate(bx + bw / 2, H - 0.06, bz));
    if (big || mid) {
      // A hanging banner, its foot cut in two tails; the well and the millstone stitched on it.
      on('cloth', new THREE.BoxGeometry(bw, bh, 0.012).translate(bx + bw / 2 + 0.02, H - 0.06 - bh / 2, bz));
      for (const sx of [0.25, 0.75]) on('cloth', new THREE.CylinderGeometry(bw * 0.25, 0, bh * 0.22, 3).rotateZ(Math.PI).scale(1, 1, 0.06).translate(bx + 0.02 + bw * sx, H - 0.06 - bh - bh * 0.1, bz));
      on('trim', new THREE.BoxGeometry(bw, 0.03, 0.016).translate(bx + bw / 2 + 0.02, H - 0.08, bz));
      on('trim', new THREE.CylinderGeometry(bw * 0.2, bw * 0.2, 0.016, 12).rotateX(Math.PI / 2).translate(bx + bw / 2 + 0.02, H - 0.06 - bh * 0.42, bz + 0.004));
      on('stitch', new THREE.TorusGeometry(bw * 0.2, 0.012, 4, 14).translate(bx + bw / 2 + 0.02, H - 0.06 - bh * 0.42, bz + 0.012));
      on('stitch', new THREE.BoxGeometry(bw * 0.5, 0.035, 0.016).translate(bx + bw / 2 + 0.02, H - 0.06 - bh * 0.72, bz + 0.006));
    } else {
      // A small pennant off the staff.
      on('cloth', new THREE.CylinderGeometry(bh * 0.5, 0, bw, 3).rotateZ(Math.PI / 2).scale(1, 1, 0.06).translate(bx + bw / 2 + 0.02, H - 0.15, bz));
    }
    if (big) {
      // Bunting from the pole's top down to the curb, its pennants in turn.
      const a = new THREE.Vector3(bx, H - 0.02, bz);
      const b = new THREE.Vector3(0.28, 0.28, 0.12);
      for (let k = 0; k < 7; k++) {
        const t = (k + 0.5) / 7;
        const p = a.clone().lerp(b, t);
        p.y -= Math.sin(Math.PI * t) * 0.15;
        on(k % 2 ? 'trim' : 'cloth', new THREE.CylinderGeometry(0.045, 0, 0.07, 3).rotateZ(Math.PI).scale(1, 1, 0.12).translate(p.x, p.y - 0.035, p.z));
      }
      const mid3 = a.clone().lerp(b, 0.5);
      mid3.y -= 0.15;
      for (const [p0, p1] of [[a, mid3], [mid3, b]] as const) {
        const len = p0.distanceTo(p1);
        const dir = p1.clone().sub(p0).normalize();
        const geo = new THREE.CylinderGeometry(0.004, 0.004, len, 4);
        geo.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir));
        geo.translate((p0.x + p1.x) / 2, (p0.y + p1.y) / 2, (p0.z + p1.z) / 2);
        on('dark', geo);
      }
    }
  }
  const mats: Record<string, THREE.Material> = {
    cloth: cloth('#9a3a30'),
    trim: cloth('#d9d2c6'),
    stitch: std('#5a6a7a', { roughness: 0.9 }),
    stone: stone('#7a7c82'),
    dark: std('#3a3c42', { roughness: 0.95, flatShading: true }),
    wood: std('#6a4e34', { roughness: 0.9, flatShading: true }),
    stave: std('#4a3424', { roughness: 0.95 }),
    iron: metal('#4a4d55', { roughness: 0.5, metalness: 0.55 }),
    hemp: std('#9a8566', { roughness: 1 }),
    water: std('#1c2c3c', { roughness: 0.1, metalness: 0.4, emissive: '#2a4a6a', emissiveIntensity: 0.35 }),
    clay: std('#8a5a42', { roughness: 0.85, flatShading: true }),
    clayD: std('#6a4232', { roughness: 0.9 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  glowSprite(at, '#cfe8ff', 0.55, V(0, B + 0.3, 0), 0.35);
  glowSprite(at, '#7fb8ff', 1.6, V(0, 0.2, 0), 0.08);
  if (o?.jar) glowSprite(at, '#cfe8ff', 0.5, V(0.32, 0.7, 0.42), 0.3);
  return done(c, o?.banner === 'grande' ? 1.0 : 0.6);
};

/** How high a saga's trophies stand on its plinth. */
export const PLINTH_TOP = 0.17;

/** Who has a place in the patio of deeds: the sagas, Brann and Hilde's bets, the fallen Unsleeping, and the table and the shelf at the keep's foot. */
export type PatioGroup = SideId | 'bets' | 'fallen' | 'table' | 'shelf';

/** How big a saga's plinth stands in the world (its own units are about a hex across). */
export const PLINTH_SCALE = 0.9;
/** A plinth pushed in to the keep's yard (its own cell is built on) stands this much smaller. */
export const PLINTH_SMALL = 0.7;


/**
 * The patio of deeds: every saga's trophies stand on the same plinth round the keep, so a yard full of them reads as
 * one set. A low hexagonal dais of pale dressed stone on a darker footing, a dark band round its edge, a small slate
 * at the front with a dragon-red line cut in it, and one iron lamp post at its back left (the corner's only light).
 * `waiting`: a saga not won yet; its plinth stands bare, the lamp out and the line uncut, in darker stone, so it doesn't
 * melt into the pale flags of the keep's yard when it's pushed in there.
 */
export const sagaPlinth = (o?: BaseOpts & { waiting?: boolean }): Model => {
  const c = begin(o, 0.7, 211);
  const { g } = c;
  const parts: Record<string, THREE.BufferGeometry[]> = { foot: [], top: [], band: [], iron: [] };
  const hex = (r0: number, r1: number, h: number, y: number): THREE.BufferGeometry => new THREE.CylinderGeometry(r0, r1, h, 6).rotateY(Math.PI / 6).translate(0, y + h / 2, 0);
  parts.foot!.push(hex(0.66, 0.7, 0.08, 0));
  parts.band!.push(hex(0.63, 0.64, 0.025, 0.08));
  parts.top!.push(hex(0.6, 0.62, 0.065, 0.105));
  // The slate at the front, leaning back a little.
  parts.band!.push(new THREE.BoxGeometry(0.3, 0.12, 0.03).rotateX(-0.35).translate(0, 0.1, 0.66));
  // The lamp: a square iron post, an arm and a caged lantern.
  const lx = -0.42;
  const lz = -0.3;
  parts.iron!.push(new THREE.BoxGeometry(0.035, 0.78, 0.035).translate(lx, PLINTH_TOP + 0.39, lz));
  parts.iron!.push(new THREE.BoxGeometry(0.16, 0.025, 0.025).translate(lx + 0.07, PLINTH_TOP + 0.76, lz));
  parts.iron!.push(new THREE.BoxGeometry(0.1, 0.02, 0.1).translate(lx + 0.13, PLINTH_TOP + 0.72, lz));
  parts.iron!.push(new THREE.BoxGeometry(0.1, 0.02, 0.1).translate(lx + 0.13, PLINTH_TOP + 0.58, lz));
  const mats: Record<string, THREE.Material> = {
    foot: stone(o?.waiting ? '#3a3e45' : '#4b5058'),
    top: stone(o?.waiting ? '#646a73' : '#a3a9b2'),
    band: std('#2c3036', { roughness: 0.9, flatShading: true }),
    iron: metal('#2c3036', { roughness: 0.5, metalness: 0.7, flatShading: true }),
  };
  for (const [k, list] of Object.entries(parts)) {
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, g);
  }
  if (o?.waiting) {
    M(new THREE.BoxGeometry(0.075, 0.12, 0.075).translate(lx + 0.13, PLINTH_TOP + 0.65, lz), std('#3a3f47', { roughness: 0.6 }), g);
    return done(c, 1.0);
  }
  M(new THREE.BoxGeometry(0.18, 0.018, 0.012).rotateX(-0.35).translate(0, 0.11, 0.682), emis(RED_LIGHT, RED, 1.2), g);
  M(new THREE.BoxGeometry(0.075, 0.12, 0.075).translate(lx + 0.13, PLINTH_TOP + 0.65, lz), emis('#eef3fa', '#dfe9ff', 1.6), g);
  glowSprite(g, '#dfe9ff', 1.3, V(lx + 0.13, PLINTH_TOP + 0.65, lz), 0.35);
  return done(c, 1.0);
};

/** Where the great net hangs in the keep's frame (for a flare). */
export const NET_SPOT = V(0.6, 1.3, -2.55);

/**
 * La Orilla 1 done: the stilt-houses' great net, rescued off the reef, hung to dry on a frame of two driftwood poles and
 * a crossbar by the keep: a sagging mesh of grey-green cord with cork floats along its head-rope and stones on its foot,
 * and caught in it, at about a man's height, the black scale the nets brought up, glinting like glass.
 */
/** Where the stilt-houses' oar leans against the net's frame (for a flare). */
export const OAR_SPOT = V(-0.95, 1.1, -2.35);
/** Where the boat saved from the Maw lies by the net's frame (for a flare). */
export const BOAT_SPOT = V(3.4, 0.5, -0.95);

export const netTrophy = (o?: BaseOpts & { centered?: boolean; oar?: boolean; boat?: boolean }): Model => {
  const c = begin(o, 1.2, 151);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, NET_SPOT.x, 0, NET_SPOT.z);
  if (!o?.centered) {
    at.scale.setScalar(1.6);
    at.rotation.y = -0.35;
  }
  const parts: Record<string, THREE.BufferGeometry[]> = { drift: [], cord: [], cork: [], stone: [], scale: [], sand: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  const W = 0.95;
  const H = 1.25;
  // The frame: two crooked driftwood poles and a crossbar lashed on top, a little sand heaped at their feet.
  for (const k of [-1, 1]) {
    on('drift', new THREE.CylinderGeometry(0.035, 0.05, H + 0.15, 6).rotateZ(k * 0.05).translate(k * W, (H + 0.15) / 2, 0));
    on('sand', new THREE.SphereGeometry(0.16, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 0.4, 1).translate(k * W, 0, 0));
  }
  on('drift', new THREE.CylinderGeometry(0.03, 0.03, W * 2 + 0.25, 6).rotateZ(Math.PI / 2).translate(0, H, 0));
  // The net: vertical cords sagging from the crossbar, horizontal ones in a slack catenary across them.
  const COLS = 13;
  const ROWS = 9;
  const pt = (i: number, j: number): THREE.Vector3 => {
    const u = i / (COLS - 1);
    const v = j / (ROWS - 1);
    const x = (u * 2 - 1) * (W - 0.05);
    const sag = Math.sin(Math.PI * u) * 0.18 * v;
    return V(x, H - 0.04 - v * (H - 0.18) - sag, 0.04 * Math.sin(u * 7 + v * 3) + 0.06 * v);
  };
  const cordSeg = (a: THREE.Vector3, b: THREE.Vector3): void => {
    const d = new THREE.Vector3().subVectors(b, a);
    const len = d.length();
    const geo = new THREE.CylinderGeometry(0.006, 0.006, len, 3);
    geo.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), d.normalize()));
    geo.translate((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
    on('cord', geo);
  };
  for (let i = 0; i < COLS; i++) for (let j = 0; j < ROWS - 1; j++) cordSeg(pt(i, j), pt(i, j + 1));
  for (let j = 0; j < ROWS; j++) for (let i = 0; i < COLS - 1; i++) cordSeg(pt(i, j), pt(i + 1, j));
  // The head-rope's corks along the top, the foot-rope's stones along the bottom.
  for (let i = 0; i < COLS; i += 2) on('cork', new THREE.SphereGeometry(0.035, 6, 4).scale(1, 0.8, 1).translate(pt(i, 0).x, pt(i, 0).y - 0.02, 0.03));
  for (let i = 1; i < COLS; i += 3) {
    const p = pt(i, ROWS - 1);
    on('stone', new THREE.DodecahedronGeometry(0.045, 0).translate(p.x, p.y - 0.04, p.z));
  }
  // A tail of net spilled on the ground at one side.
  for (let j = 0; j < 4; j++) cordSeg(V(W - 0.1 + j * 0.04, 0.02, 0.1 + j * 0.06), V(W + 0.3 + j * 0.05, 0.01, 0.25 + j * 0.08));
  // La Orilla 2 done: an oar from the stilt-houses leant against the frame's pole, its blade worn pale by the sea,
  // a band of old red paint and a twist of rope on the loom.
  if (o?.oar) {
    const oar: THREE.BufferGeometry[] = [];
    oar.push(new THREE.CylinderGeometry(0.03, 0.035, 1.55, 6).translate(0, 0.78, 0));
    oar.push(new THREE.BoxGeometry(0.17, 0.5, 0.03).translate(0, 0.22, 0));
    const lean = (g: THREE.BufferGeometry): THREE.BufferGeometry => g.rotateZ(-0.38).rotateY(0.5).translate(-W - 0.28, 0, 0.12);
    M(mergeGeometries(oar.map((x) => lean(x.toNonIndexed())), false) as THREE.BufferGeometry, std('#a89a80', { roughness: 0.9, flatShading: true }), at);
    for (const x of oar) x.dispose();
    M(lean(new THREE.CylinderGeometry(0.04, 0.04, 0.09, 8).translate(0, 0.52, 0)), std('#8a3a30', { roughness: 0.8 }), at);
    M(lean(new THREE.TorusGeometry(0.045, 0.012, 4, 10).rotateX(Math.PI / 2).translate(0, 1.05, 0)), std('#9a8566', { roughness: 0.95 }), at);
  }
  // La Orilla 3 done: the boat the Maw nearly took, hauled up on dry land beside the frame and left there, heeled over
  // on its bilge: a fishers' boat painted sea-green with a red band under the rim, a bite-shaped gash in its strake
  // where the needle teeth went in, its thwarts and a coil of rope inside, a few stones chocking it.
  if (o?.boat) {
    const boat = G(at, W + 1.05, 0, 0.35);
    boat.rotation.set(0, 0.5, 0.14);
    boat.position.y = 0.2;
    const shape = new THREE.Vector3(1.55, 0.44, 0.62);
    const hullG = new THREE.LatheGeometry(
      [[0.0, -0.5], [0.3, -0.45], [0.47, -0.24], [0.5, 0], [0.45, 0], [0.42, -0.22], [0.27, -0.38], [0.0, -0.42]].map(([r, y]) => new THREE.Vector2(r as number, y as number)),
      16,
    );
    M(hullG, std('#3e6a64', { roughness: 0.8, flatShading: true, side: THREE.DoubleSide }), boat).scale.copy(shape);
    const rim = M(new THREE.TorusGeometry(0.5, 0.04, 4, 18).rotateX(Math.PI / 2), std('#6c665a', { roughness: 0.9 }), boat);
    rim.scale.set(shape.x, 1, shape.z);
    const band = M(new THREE.TorusGeometry(0.47, 0.03, 4, 18).rotateX(Math.PI / 2), std('#8a3a30', { roughness: 0.8 }), boat);
    band.scale.set(shape.x, 1, shape.z);
    band.position.y = -0.07;
    // The thwarts, a keel, the bite: dark splintered gaps along one strake.
    for (const k of [-0.25, 0.2]) M(new THREE.BoxGeometry(0.1, 0.03, 0.56), std('#8a7a5a', { roughness: 0.9 }), boat).position.set(k * 1.55, -0.07, 0);
    M(new THREE.BoxGeometry(1.45, 0.05, 0.04), std('#3a3026', { roughness: 0.9 }), boat).position.set(0, -0.21, 0);
    for (let i = 0; i < 5; i++) {
      const gap = M(new THREE.ConeGeometry(0.035, 0.16, 3), std('#0c0a0c', { roughness: 0.6 }), boat);
      gap.position.set(-0.15 + i * 0.09, -0.12, 0.31);
      gap.rotation.set(Math.PI / 2, 0, i % 2 ? 0.3 : -0.3);
    }
    const coil = M(new THREE.TorusGeometry(0.1, 0.03, 4, 10).rotateX(Math.PI / 2), std('#9a8566', { roughness: 0.95 }), boat);
    coil.position.set(0.32, -0.18, 0);
    for (const k of [-1, 1]) M(new THREE.DodecahedronGeometry(0.08, 0), stone('#5a5c62'), at).position.set(W + 1.05 + k * 0.55, 0.05, 0.62);
    hullG.dispose();
  }
  // The black scale caught in the mesh: a broad curved plate, glossy as glass, a crack across it, scorched at the edge.
  const sp = pt(7, 4);
  on('scale', new THREE.SphereGeometry(0.17, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.35).scale(1, 1.2, 0.35).rotateX(Math.PI / 2 + 0.25).translate(sp.x, sp.y, sp.z + 0.05));
  const mats: Record<string, THREE.Material> = {
    drift: std('#8a7a66', { roughness: 0.95, flatShading: true }),
    cord: std('#5a6252', { roughness: 0.95 }),
    cork: std('#b08a5a', { roughness: 0.8 }),
    stone: stone('#5a5c62'),
    scale: std('#08070c', { roughness: 0.08, metalness: 0.7 }),
    sand: std('#9a8a6a', { roughness: 1 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  // The scale's glint, and a faint burnt-red edge where it was scorched.
  glowSprite(at, '#cfe8ff', 0.45, V(sp.x + 0.05, sp.y + 0.08, sp.z + 0.12), 0.55);
  glowSprite(at, '#ff4f3a', 0.4, V(sp.x - 0.08, sp.y - 0.1, sp.z + 0.1), 0.25);
  return done(c, 0.9);
};

/**
 * The dragon's heart (La Orilla 4, taken): by the bastion's forge, a red stone the size of a barrel with an ember inside,
 * cradled in a ring of iron bands on a squat block of dark stone, still warm. The brightest trophy in the bastion: its
 * glow is red and ember-orange, never gold. Built round its own foot (placed by the forge, or on the origin).
 */
export const HEART_SPOT = V(0, 1.15, 0);

export const heartTrophy = (o?: BaseOpts): Model => {
  const c = begin(o, 1.2, 151);
  const { g } = c;
  const parts: Record<string, THREE.BufferGeometry[]> = { stone: [], dark: [], iron: [], shell: [], crack: [], core: [], ash: [] };
  const on = (k: string, geo: THREE.BufferGeometry): void => {
    parts[k]?.push(geo);
  };
  // The block it stands on, stepped, ash round its foot.
  on('dark', new THREE.CylinderGeometry(0.62, 0.7, 0.22, 7).translate(0, 0.11, 0));
  on('stone', new THREE.CylinderGeometry(0.5, 0.56, 0.32, 7).rotateY(0.3).translate(0, 0.38, 0));
  for (let i = 0; i < 9; i++) on('ash', new THREE.DodecahedronGeometry(0.07 + (i % 3) * 0.02, 0).translate(Math.cos(i * 0.7) * 0.72, 0.03, Math.sin(i * 0.7) * 0.72));
  // The cradle: four iron bands rising round it, riveted ring at the top.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    on('iron', new THREE.BoxGeometry(0.06, 0.62, 0.04).rotateZ(Math.cos(a) * 0.22).rotateX(-Math.sin(a) * 0.22).translate(Math.cos(a) * 0.4, 0.82, Math.sin(a) * 0.4));
  }
  on('iron', new THREE.TorusGeometry(0.43, 0.035, 5, 18).rotateX(Math.PI / 2).translate(0, 0.62, 0));
  on('iron', new THREE.TorusGeometry(0.36, 0.03, 5, 18).rotateX(Math.PI / 2).translate(0, 1.08, 0));
  // The heart: a lumpy red stone, cracks glowing, the ember's core shining through.
  const shell = new THREE.DodecahedronGeometry(0.42, 1);
  const p = shell.getAttribute('position');
  for (let i = 0; i < p.count; i++) {
    const k = 1 + 0.08 * Math.sin(p.getX(i) * 9.1 + p.getY(i) * 7.3) + 0.06 * Math.cos(p.getZ(i) * 8.7);
    p.setXYZ(i, p.getX(i) * k, p.getY(i) * k * 0.92, p.getZ(i) * k);
  }
  shell.computeVertexNormals();
  on('shell', shell.translate(HEART_SPOT.x, HEART_SPOT.y, HEART_SPOT.z));
  on('crack', new THREE.TorusGeometry(0.39, 0.02, 4, 18).rotateX(0.6).rotateY(0.3).translate(HEART_SPOT.x, HEART_SPOT.y, HEART_SPOT.z));
  on('crack', new THREE.TorusGeometry(0.36, 0.02, 4, 18).rotateX(-0.4).rotateY(1.2).translate(HEART_SPOT.x, HEART_SPOT.y, HEART_SPOT.z));
  on('core', new THREE.IcosahedronGeometry(0.2, 0).translate(HEART_SPOT.x, HEART_SPOT.y, HEART_SPOT.z));
  const mats: Record<string, THREE.Material> = {
    stone: std('#2a2630', { roughness: 0.95, flatShading: true }),
    dark: std('#1c1a20', { roughness: 0.95, flatShading: true }),
    iron: std('#3e3a38', { roughness: 0.5, metalness: 0.6, flatShading: true }),
    ash: std('#2a2624', { roughness: 1, flatShading: true }),
    shell: std('#6a1812', { roughness: 0.5, flatShading: true, emissive: '#8a1e10', emissiveIntensity: 1.1 }),
    crack: emis('#ffb070', '#ff6a2a', 1.8),
    core: emis('#fff0d8', '#ffb070', 2.2),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, g);
  }
  glowSprite(g, '#ff4f3a', 4.6, HEART_SPOT.clone(), 0.2);
  glowSprite(g, '#ffb070', 2.0, HEART_SPOT.clone(), 0.5);
  glowSprite(g, '#fff0d8', 0.8, HEART_SPOT.clone(), 0.75);
  glowSprite(g, '#ff4f3a', 3.0, V(0, 0.08, 0), 0.18);
  return done(c, 0.6);
};

/**
 * La Orilla 5 (docs/SAGAS.md): a fang of the Drowned Dragon over the bastion's gate. Two tarred piles from the
 * stilt-houses stand either side of the way in, bound with iron, and across their tops lies the fang: as long as the
 * gate is wide, curved like a sickle with its point turned up to the sky, burned black at the root where it came out
 * of the jaw and worn pale along the blade, a crust of red coral at its root and a crack down its length. Chains hang
 * from it, and a rag of kelp. Its glint is cold, sea-pale, never gold. Built round the gate's centre, across +X (the
 * way in runs along Z).
 */
export const FANG_SPOT = V(0, 2.75, 0);

export const fangTrophy = (o?: BaseOpts): Model => {
  const c = begin(o, 1.2, 173);
  const { g } = c;
  const parts: Record<string, THREE.BufferGeometry[]> = { pile: [], iron: [], bone: [], root: [], coral: [], chain: [], kelp: [], crack: [] };
  const on = (k: string, geo: THREE.BufferGeometry): void => {
    parts[k]?.push(geo);
  };
  const W = 1.25;
  // The piles: thick, a little crooked, iron bands, a rope ring under the fang.
  for (const k of [-1, 1]) {
    on('pile', new THREE.CylinderGeometry(0.17, 0.2, 2.7, 7).rotateZ(k * 0.04).translate(k * W, 1.35, 0));
    on('pile', new THREE.CylinderGeometry(0.21, 0.21, 0.08, 7).translate(k * W, 2.7, 0));
    for (const y of [0.5, 1.4, 2.35]) on('iron', new THREE.TorusGeometry(0.19, 0.03, 4, 12).rotateX(Math.PI / 2).translate(k * W, y, 0));
    // A cradle of iron at the top it lies in.
    on('iron', new THREE.BoxGeometry(0.42, 0.06, 0.14).translate(k * W, 2.74, 0));
    for (const s of [-1, 1]) on('iron', new THREE.BoxGeometry(0.05, 0.22, 0.14).translate(k * W + s * 0.2, 2.84, 0));
  }
  // The fang: a tapering curve from its root (left) across and up to its point (right), flattened into a blade.
  const curve = new THREE.CatmullRomCurve3([V(-W - 0.55, 2.62, 0), V(-W * 0.5, 2.92, 0), V(W * 0.3, 3.05, 0), V(W * 0.95, 3.25, 0), V(W + 0.55, 3.75, 0)]);
  const seg = 14;
  const fr = curve.computeFrenetFrames(seg, false);
  const pos: number[] = [];
  const idx: number[] = [];
  const rad = 6;
  for (let i = 0; i <= seg; i++) {
    const k = i / seg;
    const p = curve.getPointAt(k);
    const r = 0.32 * (1 - k) ** 0.8 + 0.015;
    const n = fr.normals[i] as THREE.Vector3;
    const b = fr.binormals[i] as THREE.Vector3;
    for (let j = 0; j < rad; j++) {
      const a = (j / rad) * Math.PI * 2;
      // Flattened across its width (the blade), with a keel along the inner curve.
      const q = p.clone().addScaledVector(n, Math.cos(a) * r * (1 + 0.25 * Math.max(0, Math.cos(a)))).addScaledVector(b, Math.sin(a) * r * 0.55);
      pos.push(q.x, q.y, q.z);
    }
  }
  const tipP = curve.getPointAt(1).addScaledVector(fr.tangents[seg] as THREE.Vector3, 0.12);
  pos.push(tipP.x, tipP.y, tipP.z);
  for (let i = 0; i < seg; i++)
    for (let j = 0; j < rad; j++) {
      const a = i * rad + j;
      const b2 = i * rad + ((j + 1) % rad);
      idx.push(a, a + rad, b2, b2, a + rad, b2 + rad);
    }
  for (let j = 0; j < rad; j++) idx.push(seg * rad + j, (seg + 1) * rad, seg * rad + ((j + 1) % rad));
  const fang = new THREE.BufferGeometry();
  fang.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  fang.setIndex(idx);
  fang.computeVertexNormals();
  on('bone', fang);
  // Its root, burned black where it tore out of the jaw, ragged; the coral crusted on it.
  on('root', new THREE.DodecahedronGeometry(0.36, 0).scale(1.0, 0.9, 0.75).translate(-W - 0.62, 2.6, 0));
  for (let i = 0; i < 7; i++) {
    const a = i * 0.9;
    on('coral', new THREE.ConeGeometry(0.06, 0.28, 4).rotateZ(Math.sin(a) * 0.9).rotateX(Math.cos(a) * 0.6).translate(-W - 0.55 + Math.cos(a) * 0.22, 2.85 + Math.abs(Math.sin(a * 1.3)) * 0.12, Math.sin(a) * 0.18));
  }
  // A crack down the blade.
  on('crack', new THREE.BoxGeometry(0.9, 0.03, 0.05).rotateZ(0.14).translate(-0.3, 3.0, 0.13));
  on('crack', new THREE.BoxGeometry(0.5, 0.03, 0.05).rotateZ(-0.1).translate(0.35, 3.08, 0.12));
  // Chains hanging from it, link by link, and a rag of kelp.
  for (const [x, n] of [[-0.6, 6], [0.5, 5]] as const) {
    for (let i = 0; i < n; i++) {
      const link = new THREE.TorusGeometry(0.055, 0.016, 4, 8);
      if (i % 2) link.rotateY(Math.PI / 2);
      on('chain', link.translate(x, 2.86 - i * 0.1 - (x < 0 ? 0.08 : 0), 0));
    }
  }
  const kp = new THREE.PlaneGeometry(0.2, 0.9, 1, 4).translate(0, -0.45, 0);
  const kpos = kp.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < kpos.count; i++) kpos.setX(i, kpos.getX(i) * (1 + kpos.getY(i) * 0.9) + 0.06 * Math.sin(kpos.getY(i) * 7));
  kp.computeVertexNormals();
  on('kelp', kp.translate(0.05, 3.0, 0.02));
  const mats: Record<string, THREE.Material> = {
    pile: std('#2a2622', { roughness: 0.95, flatShading: true }),
    iron: IRON,
    bone: std('#ada392', { roughness: 0.65, flatShading: true, bumpScale: 0.05 }),
    root: std('#1c1818', { roughness: 0.7, flatShading: true }),
    coral: std('#b4483c', { roughness: 0.8, flatShading: true }),
    chain: IRON,
    kelp: cloth('#283f26', { side: THREE.DoubleSide }),
    crack: std('#2a2420', { roughness: 0.8 }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, g);
  }
  // A cold, sea-pale glint along the blade, so it reads from the table.
  glowSprite(g, '#cfe8ff', 2.6, FANG_SPOT.clone().setY(3.1), 0.08);
  glowSprite(g, '#e6f2ff', 0.9, V(W + 0.5, 3.7, 0), 0.3);
  return done(c, 3.8);
};

/**
 * Bet 9, the open gate (docs/SAGAS.md): Hilde's plaque by the bastion's gate with the names of the heroes who held a
 * whole wave with the gate open. A squat block of the bastion's stone and on it an iron lectern, tilted back so the
 * table's camera reads it: a dark iron plate in a riveted frame, her red enamel mark across its head, and the names cut
 * into it, pale steel. Its front faces +Z; the names are a canvas texture drawn once (again once the fonts load).
 */
export const PLAQUE_SPOT = V(0, 1.15, 0.1);

export interface PlaqueTrophy extends Model {
  /** Draws the face again (the fonts loaded); true if anything changed. */
  redraw: () => void;
}

const plaqueFace = (cv: HTMLCanvasElement, title: string, names: string[]): void => {
  const g = cv.getContext('2d') as CanvasRenderingContext2D;
  const W = cv.width;
  const H = cv.height;
  g.clearRect(0, 0, W, H);
  // The plate: dark iron, a little brushed, a darker bevel round it.
  const grad = g.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#34373e');
  grad.addColorStop(0.5, '#2a2c32');
  grad.addColorStop(1, '#23252a');
  g.fillStyle = grad;
  g.fillRect(0, 0, W, H);
  g.strokeStyle = 'rgba(255,255,255,0.035)';
  g.lineWidth = 1;
  for (let y = 4; y < H; y += 6) {
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(W, y + 3);
    g.stroke();
  }
  g.strokeStyle = '#15161a';
  g.lineWidth = 14;
  g.strokeRect(7, 7, W - 14, H - 14);
  g.strokeStyle = '#5a5e68';
  g.lineWidth = 3;
  g.strokeRect(20, 20, W - 40, H - 40);
  // Her mark across the head: red enamel.
  g.fillStyle = '#b8402c';
  g.fillRect(40, 36, W - 80, 10);
  // The title, small capitals, and the names cut big (pale steel with a dark cut under each letter).
  const cut = (text: string, x: number, y: number, size: number, font: string, fill: string): void => {
    g.font = `${size}px ${font}`;
    g.fillStyle = 'rgba(0,0,0,0.75)';
    g.fillText(text, x + 2, y + 3);
    g.fillStyle = fill;
    g.fillText(text, x, y);
  };
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const display = '"IM Fell English SC", "Iowan Old Style", Georgia, serif';
  let size = 46;
  g.font = `${size}px ${display}`;
  while (size > 26 && g.measureText(title).width > W - 110) {
    size -= 2;
    g.font = `${size}px ${display}`;
  }
  cut(title, W / 2, 92, size, display, '#c9ced8');
  const list = names.length ? names : ['—'];
  const cols = list.length > 4 ? 2 : 1;
  const rows = Math.ceil(list.length / cols);
  const top = 150;
  const room = H - top - 40;
  const line = Math.min(96, room / rows);
  let ns = Math.min(78, Math.floor(line * 0.78));
  const colW = (W - 100) / cols;
  g.font = `${ns}px ${display}`;
  while (ns > 26 && list.some((n) => g.measureText(n).width > colW - 20)) {
    ns -= 2;
    g.font = `${ns}px ${display}`;
  }
  list.forEach((n, i) => {
    const c = cols === 1 ? 0 : Math.floor(i / rows);
    const r = cols === 1 ? i : i % rows;
    cut(n, 50 + colW * c + colW / 2, top + line * r + line / 2, ns, display, '#eef0f4');
  });
};

export const plaqueTrophy = (title: string, names: string[], o?: BaseOpts): PlaqueTrophy => {
  const c = begin(o, 0.9, 191);
  const { g } = c;
  const parts: Record<string, THREE.BufferGeometry[]> = { stone: [], cap: [], iron: [], rivet: [], enamel: [] };
  const on = (k: string, geo: THREE.BufferGeometry): void => {
    parts[k]?.push(geo);
  };
  // The block: the bastion's stone, a cap a little wider.
  on('stone', new THREE.BoxGeometry(1.5, 0.62, 0.62).translate(0, 0.31, 0));
  on('cap', new THREE.BoxGeometry(1.62, 0.1, 0.72).translate(0, 0.67, 0));
  // The lectern: an iron plate tilted back 55° from upright on two struts, framed and riveted.
  const tilt = -0.96;
  const plate = new THREE.Group();
  plate.position.set(0, 1.1, 0.02);
  plate.rotation.x = tilt;
  const PW = 1.7;
  const PH = 1.06;
  const frame = (geo: THREE.BufferGeometry, k: string): void => {
    geo.applyMatrix4(new THREE.Matrix4().makeRotationX(tilt).premultiply(new THREE.Matrix4().makeTranslation(0, 1.1, 0.02)));
    on(k, geo);
  };
  frame(new THREE.BoxGeometry(PW + 0.12, PH + 0.12, 0.06).translate(0, 0, -0.04), 'iron');
  for (const [x, y, w, h] of [[0, PH / 2 + 0.05, PW + 0.16, 0.06], [0, -PH / 2 - 0.05, PW + 0.16, 0.06], [PW / 2 + 0.05, 0, 0.06, PH + 0.16], [-PW / 2 - 0.05, 0, 0.06, PH + 0.16]] as const)
    frame(new THREE.BoxGeometry(w, h, 0.07).translate(x, y, 0.01), 'iron');
  for (const [x, y] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) frame(new THREE.SphereGeometry(0.035, 6, 4).translate(x * (PW / 2 + 0.05), y * (PH / 2 + 0.05), 0.05), 'rivet');
  // Struts down to the cap.
  // Struts from the cap up under the plate's back (kept below it, so nothing shows through the names).
  for (const x of [-0.55, 0.55]) on('iron', new THREE.BoxGeometry(0.07, 0.3, 0.07).translate(x, 0.84, -0.2));
  // A bracket of Hilde's red on the block's face.
  on('enamel', new THREE.BoxGeometry(0.5, 0.06, 0.02).translate(0, 0.42, 0.315));
  on('enamel', new THREE.BoxGeometry(0.08, 0.16, 0.025).translate(0, 0.5, 0.315));
  const mats: Record<string, THREE.Material> = {
    stone: stone('#5f636a'),
    cap: stone('#72767c'),
    iron: IRON,
    rivet: RIVET,
    enamel: std('#b8402c', { roughness: 0.4, flatShading: true }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, g);
  }
  // The face with the names.
  const cv = document.createElement('canvas');
  cv.width = 1024;
  cv.height = 640;
  plaqueFace(cv, title, names);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const faceMat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.55, metalness: 0.35, emissive: '#ffffff', emissiveMap: tex, emissiveIntensity: 0.22 });
  const face = new THREE.Mesh(new THREE.PlaneGeometry(PW, PH), faceMat);
  face.position.z = 0.0;
  plate.add(face);
  g.add(plate);
  // A faint cold glint so it reads from the table.
  glowSprite(g, '#dfe6f0', 2.2, PLAQUE_SPOT.clone(), 0.08);
  const m = done(c, 1.6) as PlaqueTrophy;
  m.redraw = () => {
    plaqueFace(cv, title, names);
    tex.needsUpdate = true;
  };
  return m;
};

/**
 * El Párpado 1 (docs/SAGAS.md): the Order's old sign from its post on the titan's head, brought down and set up by the
 * keep. A broad weathered board on two posts, split along its grain, moss in the cracks; cut into it and painted in
 * faded red, the Order's mark: a closed eye over a bell. Under it a narrower board, an arrow pointing up the neck. Iron
 * straps, a rusted ring. No gold: grey old wood, dull red, iron. Merged by material.
 */
export interface PostSignLook {
  /** Built on the origin instead of its spot by the keep (the gallery). */
  centered?: boolean;
  /**
   * El Párpado 2 done: the bell's old clapper hung beside it, off a gallows post: a long iron-cored bronze shaft gone
   * green, its ball worn bright brown where it struck for forty years, a chain to the arm, a split in the ball.
   */
  clapper?: boolean;
  /**
   * El Párpado 3 done: a shard of the copies' black glass, stood up in a cleft stone at the sign's left, sharp and
   * glossy, a cold glint in its edge: nothing in it reflects.
   */
  shard?: boolean;
}

/** Where the sign's board stands in the keep's frame (for a flare). */
export const POST_SIGN_SPOT = V(-4.4, 1.5, 2.2);

/** Where the old clapper's ball hangs beside the sign, in the keep's frame (for a flare; its post's arm is 0.55 turned, ×1.5). */
export const CLAPPER_SPOT = new THREE.Vector3(1.6 * 1.5, 0.84 * 1.5, 0.05 * 1.5).applyAxisAngle(new THREE.Vector3(0, 1, 0), 0.55).add(new THREE.Vector3(POST_SIGN_SPOT.x, 0, POST_SIGN_SPOT.z));

/** Where the black glass shard stands beside the sign, in the keep's frame (for a flare; like the clapper's). */
export const SHARD_SPOT = new THREE.Vector3(-1.07 * 1.5, 0.82 * 1.5, 0.1 * 1.5).applyAxisAngle(new THREE.Vector3(0, 1, 0), 0.55).add(new THREE.Vector3(POST_SIGN_SPOT.x, 0, POST_SIGN_SPOT.z));

export const postSignTrophy = (o?: BaseOpts & PostSignLook): Model => {
  const c = begin(o, 1.1, 149);
  const { g } = c;
  const at = o?.centered ? G(g, 0, 0, 0) : G(g, POST_SIGN_SPOT.x, 0, POST_SIGN_SPOT.z);
  if (!o?.centered) {
    at.scale.setScalar(1.5);
    // Facing out towards the board's camera, a little turned.
    at.rotation.y = 0.55;
  }
  const parts: Record<string, THREE.BufferGeometry[]> = { wood: [], old: [], crack: [], moss: [], iron: [], paint: [], stone: [], patina: [], worn: [], glass: [], glint: [] };
  const on = (key: string, geo: THREE.BufferGeometry): void => {
    parts[key]?.push(geo);
  };
  const W = 1.3;
  const TOP = 1.55;
  // The posts, leaning a hair, on a heap of stones.
  for (const side of [-1, 1]) {
    on('old', new THREE.BoxGeometry(0.11, TOP + 0.15, 0.11).rotateZ(side * 0.03).translate(side * (W / 2 - 0.05), (TOP + 0.15) / 2, 0));
    on('old', new THREE.ConeGeometry(0.075, 0.12, 4).rotateY(Math.PI / 4).translate(side * (W / 2 - 0.05), TOP + 0.21, 0));
    for (let k = 0; k < 3; k++) on('stone', new THREE.DodecahedronGeometry(0.09, 0).translate(side * (W / 2 - 0.05) + Math.cos(k * 2.1) * 0.12, 0.05, Math.sin(k * 2.1) * 0.12));
  }
  // The board: three planks, the middle one split, their ends ragged.
  for (let k = 0; k < 3; k++) {
    const y = TOP - 0.12 - k * 0.19;
    on(k === 1 ? 'old' : 'wood', new THREE.BoxGeometry(W + (k === 2 ? -0.08 : 0.04), 0.18, 0.05).translate(k === 2 ? -0.03 : 0, y, 0.07));
  }
  on('crack', new THREE.BoxGeometry(0.5, 0.012, 0.055).rotateZ(0.08).translate(0.25, TOP - 0.31, 0.072));
  on('crack', new THREE.BoxGeometry(0.012, 0.16, 0.055).translate(-0.48, TOP - 0.12, 0.072));
  // Iron straps across the planks, a rusted ring hanging off the top.
  for (const side of [-1, 1]) on('iron', new THREE.BoxGeometry(0.05, 0.6, 0.06).translate(side * 0.42, TOP - 0.31, 0.08));
  on('iron', new THREE.TorusGeometry(0.07, 0.014, 4, 10).translate(0, TOP + 0.04, 0.1));
  // The Order's mark, painted: a closed eye (its lashes down) over a bell.
  const lid = new THREE.Shape();
  lid.moveTo(-0.22, 0);
  lid.quadraticCurveTo(0, 0.13, 0.22, 0);
  lid.quadraticCurveTo(0, 0.04, -0.22, 0);
  on('paint', new THREE.ShapeGeometry(lid, 10).translate(0, TOP - 0.13, 0.1));
  for (let k = -2; k <= 2; k++) on('paint', new THREE.BoxGeometry(0.014, 0.05, 0.005).rotateZ(k * 0.25).translate(k * 0.075, TOP - 0.15, 0.101));
  const bell = new THREE.Shape();
  bell.moveTo(-0.12, 0);
  bell.lineTo(0.12, 0);
  bell.quadraticCurveTo(0.09, 0.04, 0.08, 0.14);
  bell.quadraticCurveTo(0.07, 0.22, 0, 0.23);
  bell.quadraticCurveTo(-0.07, 0.22, -0.08, 0.14);
  bell.quadraticCurveTo(-0.09, 0.04, -0.12, 0);
  on('paint', new THREE.ShapeGeometry(bell, 8).translate(0, TOP - 0.52, 0.1));
  on('paint', new THREE.CircleGeometry(0.025, 8).translate(0, TOP - 0.55, 0.101));
  // The arrow board under it, pointing up (the neck, the head).
  on('wood', new THREE.BoxGeometry(0.7, 0.13, 0.04).translate(-0.06, 0.62, 0.07));
  const arrow = new THREE.Shape();
  arrow.moveTo(0.29, 0.69);
  arrow.lineTo(0.42, 0.62);
  arrow.lineTo(0.29, 0.55);
  on('wood', new THREE.ShapeGeometry(arrow).translate(0, 0, 0.07));
  on('paint', new THREE.BoxGeometry(0.4, 0.025, 0.005).translate(-0.04, 0.62, 0.092));
  // Moss in the cracks and along the top, grass at the posts' feet.
  on('moss', new THREE.BoxGeometry(W * 0.9, 0.025, 0.07).translate(0.02, TOP - 0.02, 0.07));
  for (let k = 0; k < 4; k++) on('moss', new THREE.DodecahedronGeometry(0.04, 0).scale(1.4, 0.6, 1).translate(-0.5 + k * 0.33, TOP - 0.21 - (k % 2) * 0.19, 0.1));
  if (o?.clapper) {
    // A gallows post at the sign's right, its arm out over the clapper, braced; stones at its foot.
    const px = W / 2 + 0.5;
    const ph = TOP + 0.55;
    on('old', new THREE.BoxGeometry(0.13, ph, 0.13).translate(px, ph / 2, 0.05));
    on('old', new THREE.BoxGeometry(0.62, 0.1, 0.1).translate(px + 0.25, ph - 0.05, 0.05));
    on('old', new THREE.BoxGeometry(0.07, 0.42, 0.07).rotateZ(-Math.PI / 4).translate(px + 0.13, ph - 0.2, 0.05));
    on('iron', new THREE.BoxGeometry(0.16, 0.12, 0.14).translate(px + 0.45, ph - 0.05, 0.05));
    for (let k = 0; k < 4; k++) on('stone', new THREE.DodecahedronGeometry(0.1, 0).translate(px + Math.cos(k * 1.7) * 0.15, 0.06, 0.05 + Math.sin(k * 1.7) * 0.15));
    // The chain down from the arm.
    const cx = px + 0.45;
    for (let k = 0; k < 4; k++) on('iron', new THREE.TorusGeometry(0.035, 0.011, 4, 8).rotateY(k % 2 ? Math.PI / 2 : 0).translate(cx, ph - 0.15 - k * 0.06, 0.05));
    // The clapper: its eye, the shaft, the swell of the flight, the ball, and a finger of a tail under it.
    const top = ph - 0.4;
    on('patina', new THREE.TorusGeometry(0.05, 0.018, 5, 10).translate(cx, top, 0.05));
    on('patina', new THREE.CylinderGeometry(0.03, 0.04, 0.75, 7).translate(cx, top - 0.42, 0.05));
    on('patina', new THREE.CylinderGeometry(0.06, 0.035, 0.18, 8).translate(cx, top - 0.6, 0.05));
    on('worn', new THREE.SphereGeometry(0.12, 10, 8).scale(1, 1.15, 1).translate(cx, top - 0.86, 0.05));
    on('patina', new THREE.CylinderGeometry(0.035, 0.015, 0.14, 7).translate(cx, top - 1.04, 0.05));
    on('crack', new THREE.BoxGeometry(0.012, 0.12, 0.13).rotateZ(0.2).translate(cx + 0.03, top - 0.84, 0.05));
    // Bands of green where it never struck.
    on('patina', new THREE.TorusGeometry(0.115, 0.015, 4, 12).rotateX(Math.PI / 2).translate(cx, top - 0.76, 0.05));
  }
  if (o?.shard) {
    // A low stone split down the middle, and the shard stood in the split: one long blade, two short ones at its foot.
    const sx = -W / 2 - 0.42;
    on('stone', new THREE.DodecahedronGeometry(0.26, 0).scale(1.3, 0.55, 1).translate(sx - 0.13, 0.12, 0.1));
    on('stone', new THREE.DodecahedronGeometry(0.24, 0).scale(1.2, 0.6, 1).translate(sx + 0.16, 0.12, 0.06));
    on('glass', new THREE.OctahedronGeometry(0.26, 0).scale(0.45, 2.7, 0.22).rotateZ(-0.12).rotateY(0.4).translate(sx, 0.82, 0.1));
    on('glass', new THREE.OctahedronGeometry(0.16, 0).scale(0.4, 1.8, 0.2).rotateZ(0.45).rotateY(-0.3).translate(sx - 0.2, 0.42, 0.12));
    on('glass', new THREE.OctahedronGeometry(0.13, 0).scale(0.4, 1.5, 0.2).rotateZ(-0.6).translate(sx + 0.2, 0.36, 0.14));
    // The glint down the long blade's edge.
    on('glint', new THREE.BoxGeometry(0.018, 0.85, 0.018).rotateZ(-0.12).rotateY(0.4).translate(sx + 0.05, 0.88, 0.16));
  }
  const mats: Record<string, THREE.Material> = {
    glass: std('#0d0f15', { roughness: 0.08, metalness: 0.7, flatShading: true }),
    glint: std('#c7d0ff', { roughness: 0.3, emissive: '#8f9cff', emissiveIntensity: 0.9 }),
    patina: std('#4f7a68', { roughness: 0.6, metalness: 0.35, flatShading: true }),
    worn: std('#6b4f3a', { roughness: 0.4, metalness: 0.55, flatShading: true }),
    wood: std('#5a5048', { roughness: 0.95, flatShading: true }),
    old: std('#4a423c', { roughness: 1, flatShading: true }),
    crack: std('#151210', { roughness: 1 }),
    moss: std('#3f5233', { roughness: 1, flatShading: true }),
    iron: std('#3a3634', { roughness: 0.55, metalness: 0.55, flatShading: true }),
    paint: std('#a8352a', { roughness: 0.9, side: THREE.DoubleSide, emissive: '#4a0e08', emissiveIntensity: 0.5 }),
    stone: std('#5e5c58', { roughness: 0.95, flatShading: true }),
  };
  for (const [k, list] of Object.entries(parts)) {
    if (!list.length) continue;
    const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
    for (const x of list) x.dispose();
    if (merged) M(merged, mats[k] as THREE.Material, at);
  }
  return done(c, TOP + 0.3);
};

/**
 * The night of the bell's prize (El Párpado 4, docs/SAGAS.md): a small bell Hilde cast from the bronze left over from
 * the Order's great one, at the very top of the keep, the game's most important trophy. Four iron legs rise off the
 * keep's crown (`top`, in the keep's own frame) over its fire to a little arched cote with a dark oak yoke; the bell hangs under it in
 * the great bell's green patina, its lip worn to the bronze, a row of the Order's closed eyes round its waist and one red
 * band of runes. A cold light gathers under its mouth (the calm), a long red pennant streams off its spike. It sways in
 * the wind; `ring()` swings it hard (it rings by itself when the plumb moves). No gold.
 */
export const lidBellTrophy = (o?: BaseOpts & { top?: number; beside?: { y: number; x: number } | null }): LidBellTrophy => {
  const top = o?.top ?? 3;
  // Eras IV and V end in a point (the Vigil's lantern, the Dream Beacon's spire): nothing to stand over, so the bell
  // hangs off an iron bracket standing on the crown (`beside`: keepCrownBracket), out to the side (+x) so the camera,
  // in front, keeps seeing the crown.
  const b = o?.beside ?? undefined;
  const arm = b?.y;
  const p = b?.x ?? 0;
  const ox = b ? p + 0.45 : 0;
  const c = begin(o, 0.6, 151);
  const { g } = c;
  const patina = std('#5b8d78', { roughness: 0.5, metalness: 0.35, flatShading: true, emissive: '#16302a', emissiveIntensity: 0.7 });
  const patinaDark = std('#33544a', { roughness: 0.55, metalness: 0.3, flatShading: true, emissive: '#0d1f1a', emissiveIntensity: 0.6 });
  const bronze = std('#7a5c46', { roughness: 0.4, metalness: 0.5, flatShading: true, emissive: '#1e140e', emissiveIntensity: 0.5 });
  const runes = emis(RED_LIGHT, RED, 1.8);
  // The cote: four iron legs straddling the keep's fire from its crown, two arches crossed over the yoke, a spike.
  // Or the bracket: a post up from the belfry roof's corner, an arm out over the edge, a brace under it.
  const base = arm ?? top - 0.32;
  const cote = base + (arm === undefined ? 0.78 : 0.8);
  if (arm === undefined) {
    for (const [x, z] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      limb(g, V(x * 0.3, base, z * 0.3), V(x * 0.2, cote, z * 0.2), 0.03, 0.022, IRON, 6);
      M(new THREE.OctahedronGeometry(0.035, 0), RIVET, g).position.set(x * 0.3, base + 0.02, z * 0.3);
    }
    for (const k of [0, 1]) {
      const arch = M(new THREE.TorusGeometry(0.2, 0.016, 5, 14, Math.PI), IRON, g);
      arch.position.y = cote;
      arch.rotation.y = (k * Math.PI) / 2;
    }
    limb(g, V(0, cote + 0.2, 0), V(0, cote + 0.42, 0), 0.016, 0.002, RIVET, 4);
    for (const k of [-1, 1]) M(new THREE.OctahedronGeometry(0.022, 0), RIVET, g).position.set(k * 0.2, cote, 0);
  } else {
    limb(g, V(p, base, 0), V(p, cote + 0.22, 0), 0.034, 0.026, IRON, 6);
    M(new THREE.OctahedronGeometry(0.04, 0), RIVET, g).position.set(p, base + 0.02, 0);
    limb(g, V(p, cote + 0.2, 0), V(ox + 0.08, cote + 0.2, 0), 0.024, 0.02, IRON, 6);
    limb(g, V(p, cote - 0.2, 0), V(ox - 0.14, cote + 0.19, 0), 0.016, 0.014, IRON, 5);
    limb(g, V(p, cote + 0.22, 0), V(p, cote + 0.44, 0), 0.016, 0.002, RIVET, 4);
    M(new THREE.OctahedronGeometry(0.024, 0), RIVET, g).position.set(ox, cote + 0.2, 0);
  }
  // The yoke and the bell swinging under it.
  const pivot = G(g, ox, cote + 0.17, 0);
  if (arm !== undefined) pivot.rotation.y = Math.PI / 2;
  M(new THREE.BoxGeometry(0.34, 0.06, 0.08), DWOOD, pivot);
  const swing = G(pivot, 0, -0.02, 0);
  const bell = G(swing, 0, -0.56, 0);
  bell.scale.setScalar(1.05);
  M(new THREE.LatheGeometry(PROFILE.map(([x, y]) => new THREE.Vector2(x, y)).reverse(), 20), patina, bell);
  const band = (y: number, r: number, h: number, mat: THREE.Material): void => {
    M(new THREE.CylinderGeometry(r, r, h, 20, 1, true), mat, bell).position.y = y;
  };
  band(0.02, 0.254, 0.035, bronze);
  band(0.4, 0.142, 0.02, patinaDark);
  band(0.06, 0.218, 0.018, patinaDark);
  // One red band of runes, and the Order's closed eyes round the waist.
  M(new THREE.CylinderGeometry(0.168, 0.176, 0.02, 20, 1, true), runes, bell).position.y = 0.13;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const lid = M(new THREE.SphereGeometry(0.022, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), patinaDark, bell);
    lid.position.set(Math.sin(a) * 0.148, 0.24, Math.cos(a) * 0.148);
    lid.rotation.set(Math.PI / 2, a, 0);
    lid.scale.set(1.6, 1, 0.5);
  }
  for (const k of [-1, 1]) {
    const loop = M(new THREE.TorusGeometry(0.04, 0.012, 5, 10, Math.PI), IRON, bell);
    loop.position.set(k * 0.03, 0.5, 0);
    loop.rotation.y = Math.PI / 2;
  }
  limb(bell, V(0, 0.46, 0), V(0, 0.08, 0), 0.007, 0.007, IRON, 4);
  M(new THREE.SphereGeometry(0.03, 8, 6), IRON, bell).position.y = 0.07;
  // The calm under its mouth: a cold light that reads from the bastion's camera, day and night.
  const calm = glowSprite(bell, '#9fc8ff', 1.1, V(0, 0.08, 0), 0.5, true);
  const halo = glowSprite(g, '#cfe3ff', 2.2, V(ox, cote - 0.2, 0), 0.18, true);
  // The pennant: long and red, off the mast's top, in two panels that ripple.
  const flag = G(g, p, cote + 0.36, 0);
  const red = emis(RED, RED, 0.35, { side: THREE.DoubleSide, roughness: 0.9 });
  const panels: THREE.Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const seg = G(i === 0 ? flag : (panels[i - 1] as THREE.Mesh), i === 0 ? 0 : 0.22, 0, 0);
    const p = M(new THREE.PlaneGeometry(0.22, 0.09 - i * 0.02), red, seg);
    p.geometry.translate(0.11, 0, 0);
    panels.push(p);
  }
  let ringing = 0;
  const tick = (n: number): void => {
    const strong = ringing > 0 ? (ringing / 48) * 0.6 : 0;
    swing.rotation.x = Math.sin(n * 0.19) * 0.05 + Math.sin(n * 0.8) * strong;
    if (ringing > 0) ringing--;
    flag.rotation.y = -0.6 + Math.sin(n * 0.12) * 0.25;
    for (let i = 0; i < panels.length; i++) (panels[i] as THREE.Mesh).rotation.y = Math.sin(n * 0.45 + i * 1.3) * 0.35;
    const k = 0.5 + 0.5 * Math.sin(n * 0.22);
    (calm.material as THREE.SpriteMaterial).opacity = 0.35 + k * 0.2 + strong * 0.7;
    (halo.material as THREE.SpriteMaterial).opacity = 0.12 + k * 0.06 + strong * 0.3;
  };
  const m = done(c, cote + 0.45, tick) as LidBellTrophy;
  m.ring = () => {
    ringing = 48;
  };
  m.at = V(ox, cote - 0.25, 0);
  return m;
};

/**
 * Each saga trophy on its saga's plinth. The models are still built in the keep's frame (where they stood before the
 * patio); this moves one onto the plinth: `at` is where the model stands in the keep's frame, `rot` and `scale` how it
 * is turned and sized there, `face` how it faces the gallery's camera, `size` how big it stands on the plinth (in the
 * gallery's units) and `on` where on the plinth's top (its own units; the lamp is at the back left, the slate in front).
 */
export const PATIO_PIECES: Record<string, { saga: PatioGroup; at: THREE.Vector3; rot: number; scale: number; face: number; size: number; on: [number, number] }> = {
  bucket: { saga: 'well', at: BUCKET_SPOT, rot: 0.5, scale: 1.7, face: 0, size: 1.0, on: [0.05, 0.05] },
  corral: { saga: 'horn', at: CORRAL_SPOT, rot: Math.PI / 12, scale: 2.0, face: 0, size: 0.55, on: [0.05, 0.15] },
  post: { saga: 'horn', at: POST_SPOT, rot: 0, scale: 1.6, face: 0, size: 0.5, on: [0.38, -0.2] },
  spear: { saga: 'horn', at: SPEAR_SPOT, rot: 0, scale: 1.7, face: 0, size: 0.5, on: [0.05, -0.38] },
  clues: { saga: 'faces', at: CLUES_SPOT, rot: Math.PI / 12, scale: 1.6, face: 0, size: 0.45, on: [-0.15, 0.22] },
  lanterns: { saga: 'faces', at: LANTERNS_SPOT, rot: Math.PI / 12, scale: 1.55, face: 0, size: 0.5, on: [0.05, -0.35] },
  facesLantern: { saga: 'faces', at: FACES_LANTERN_SPOT, rot: Math.PI / 12, scale: 2.2, face: 0, size: 0.6, on: [0.36, 0.1] },
  mapTable: { saga: 'burn', at: MAP_TABLE_SPOT, rot: 0.5, scale: 1.75, face: 0, size: 0.5, on: [-0.1, 0.22] },
  vigilBanner: { saga: 'burn', at: VIGIL_BANNER_SPOT, rot: -0.35, scale: 2.1, face: 0, size: 0.5, on: [0.38, -0.12] },
  harpoon: { saga: 'burn', at: HARPOON_AT, rot: 0.18, scale: 1.8, face: 0, size: 0.3, on: [0.05, 0.45] },
  beacon: { saga: 'burn', at: BEACON_SPOT, rot: 0, scale: 1.45, face: 0, size: 0.45, on: [0.05, -0.32] },
  mineCart: { saga: 'rails', at: MINE_CART_SPOT, rot: -0.35, scale: 1.5, face: 0, size: 0.3, on: [-0.15, 0] },
  net: { saga: 'shore', at: NET_SPOT, rot: -0.35, scale: 1.6, face: 0, size: 0.3, on: [-0.2, 0] },
  lidSign: { saga: 'lid', at: POST_SIGN_SPOT, rot: 0.55, scale: 1.5, face: 0, size: 0.35, on: [0, 0] },
  barrels: { saga: 'bets', at: BARREL_SPOT, rot: Math.PI / 2 - 0.35, scale: 1, face: Math.PI / 2, size: 0.3, on: [0, 0.05] },
  fallen: { saga: 'fallen', at: V(0, 0, 0), rot: 0, scale: 1, face: 0, size: 0.3, on: [0.05, 0.05] },
  // At the keep's foot in the yard, with no plinth: turned as they stood before.
  table: { saga: 'table', at: TABLE_SPOT, rot: Math.PI / 5, scale: 1, face: Math.PI / 5 + Math.PI / 6, size: 0.6, on: [0, 0] },
  shelf: { saga: 'shelf', at: SHELF_SPOT, rot: -Math.PI / 5, scale: 1, face: -Math.PI / 5 + Math.PI / 6, size: 0.6, on: [-0.15, -0.1] },
  stone: { saga: 'shelf', at: GLYPH_STONE_SPOT, rot: Math.PI + Math.PI / 10, scale: 1, face: Math.PI + Math.PI / 10 + Math.PI / 6, size: 0.6, on: [0.3, 0.3] },
};
